window.Customization3DViewer = (function () {
  'use strict';

  const C3D_VENDOR_ORIGIN_DEFAULT = 'https://spaces.miradoroutdoor.com';
  const FALLBACK_ENV_URL = `${C3D_VENDOR_ORIGIN_DEFAULT}/uploads/environment.env`;
  const CANVAS_ID         = 'preview-3d-viewer';
  const RUNTIME_MODEL_ID  = 'main-model';  

  let kernel              = null;
  let sdkInitialized      = false;
  let initPromise         = null;
  let sdkCapabilityLogged = false;
  let sdkEventsBound      = false;
  let hotspotSubEventsBound = false;
  let boundsWarningEventsBound = false;
  let initCameraPatchLogged = false;
  let initCameraPatchWarned = false;

  let currentModelId  = null;
  let currentModelUrl = null;
  let currentModelAttachKey = null;
  const loadedModelKeys = new Set();
  const modelMeshRegistry = new Map();
  let hasLoadedFirstModel = false;
  let modelLoadInFlight = false;
  let cachedRuntimeScene = null;
  let forceReplaceNextLoad = false;
  const modelRotationAccumY = new Map();
  let loadModelMutex = Promise.resolve();

  const hasFn = (obj, key) => !!obj && typeof obj[key] === 'function';
  const awaitIfPromise = (v) => (v?.then ? v : Promise.resolve(v));
  const withTimeout = (promise, ms = 1200) => {
    let timer = null;
    return Promise.race([
      promise,
      new Promise((resolve) => {
        timer = setTimeout(() => resolve('__timeout__'), ms);
      }),
    ]).finally(() => {
      if (timer) clearTimeout(timer);
    });
  };

  const el = {
    loading:      () => document.querySelector('[data-3d-loading]'),
    empty:        () => document.querySelector('[data-3d-empty]'),
    container:    () => document.querySelector('[data-3d-container]'),
    progressBar:  () => document.querySelector('[data-3d-progress-bar]'),
    progressText: () => document.querySelector('[data-3d-progress-text]'),
  };

  const ensureCanvas = () => {
    const existing = document.getElementById(CANVAS_ID);
    if (!existing) return null;
    if (existing.tagName === 'CANVAS') {
      existing.setAttribute('draggable', 'false');
      return existing;
    }

    const canvas = document.createElement('canvas');
    canvas.id = CANVAS_ID;
    canvas.className = existing.className || 'preview-3d-viewer';
    canvas.setAttribute('draggable', 'false');
    canvas.style.cssText = 'width:100%;height:100%;display:block;';
    existing.innerHTML = '';
    existing.appendChild(canvas);
    return canvas;
  };

  const resizeCanvas = (canvas) => {
    if (!canvas) return;
    const { width, height } = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    if (canvas.width  !== w) canvas.width  = w;
    if (canvas.height !== h) canvas.height = h;
  };

  const setupContainerResizeObserver = () => {
    const container = document.querySelector('[data-3d-container]');
    if (!container || container.__c3dResizeObs) return;
    const ro = new ResizeObserver(() => resizeCanvas(ensureCanvas()));
    ro.observe(container);
    container.__c3dResizeObs = ro;
  };

  const runWithConcurrency = async (items, worker, concurrency = 3) => {
    if (!Array.isArray(items) || !items.length) return;
    const limit = Math.max(1, Math.min(concurrency, items.length));
    let cursor = 0;
    const runners = Array.from({ length: limit }, async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        await worker(items[index], index);
      }
    });
    await Promise.all(runners);
  };

  let autoBaseModelsLoadPromise = null;

  const poll = (check, maxWait = 5000, interval = 100) =>
    new Promise((resolve) => {
      if (check()) { resolve(true); return; }
      const start = Date.now();
      const ticker = setInterval(() => {
        if (check()) {
          clearInterval(ticker);
          resolve(true);
        } else if (Date.now() - start >= maxWait) {
          clearInterval(ticker);
          resolve(false);
        }
      }, interval);
    });

  const isLegacyBusinessMode = () =>
    String(window.CUSTOMIZATION_3D_BUSINESS_MODE || '').trim().toLowerCase() === 'legacy';
  const getBusinessLayer = () => window.kernel_bl || null;

  const waitForSDK = async () => {
    try {
      if (window.Customization3DLoader?.ensureLoaded) {
        await window.Customization3DLoader.ensureLoaded();
      }
    } catch (err) {
      console.error('[3D] SDK/BIZ loader failed:', err);
      return false;
    }
    const sdkReady = await poll(() => !!window.faceSDK?.kernel, 20000);
    if (!sdkReady) return false;
    if (isLegacyBusinessMode()) return true;
    return poll(() => !!(window.kernel_bl && typeof window.kernel_bl.init === 'function'), 8000);
  };
  const waitForContainer = () => {
    const canvas = ensureCanvas();
    if (!canvas) return Promise.resolve(false);
    return poll(() => {
      const { width, height } = canvas.getBoundingClientRect();
      return width > 0 && height > 0 && canvas.offsetParent !== null;
    }).then((ready) => {
      if (ready) resizeCanvas(canvas);
      return ready;
    });
  };

  const setOverlayVisible = (node, visible, displayValue = 'flex') => {
    if (!node) return;
    node.hidden = !visible;
    node.classList.toggle('is-visible', !!visible);
    node.style.display = visible ? displayValue : 'none';
  };

  const showLoading = (progress = 0) => {
    detachModelActionBubbleDom();
    const containerHasModel = el.container()?.classList.contains('has-model');
    if (hasLoadedFirstModel && containerHasModel) return;
    const pct = Math.max(0, Math.round(Number(progress) || 0));
    const loadingEl = el.loading();
    setOverlayVisible(loadingEl, true);
    if (loadingEl) loadingEl.classList.toggle('is-indeterminate', pct <= 0);
    setOverlayVisible(el.empty(), false);
    if (el.container()) el.container().setAttribute('aria-busy', 'true');
    if (el.progressBar())  el.progressBar().style.width = `${pct}%`;
    if (el.progressText()) el.progressText().textContent = `${pct}%`;
  };

  const showEmpty = () => {
    detachModelActionBubbleDom();
    hasLoadedFirstModel = false;
    modelLoadInFlight = false;
    setOverlayVisible(el.loading(), false);
    setOverlayVisible(el.empty(), true);
    if (el.container()) {
      el.container().classList.remove('has-model');
      el.container().removeAttribute('aria-busy');
    }
    const boundsWarning = document.getElementById('bounds-warning')
      || document.querySelector('[data-bounds-warning]');
    if (boundsWarning) boundsWarning.hidden = true;
    currentModelId  = null;
    currentModelUrl = null;
    currentModelAttachKey = null;
    loadedModelKeys.clear();
    modelMeshRegistry.clear();
    modelRoleRegistry.clear();
    modelControlTypeRegistry.clear();
    modelGroupRegistry.clear();
  };

  const showModelReady = (options = {}) => {
    const alreadyReady = hasLoadedFirstModel;
    modelLoadInFlight = false;
    if (!alreadyReady) {
      const loadingEl = el.loading();
      if (loadingEl) loadingEl.classList.remove('is-indeterminate');
      if (el.progressBar())  el.progressBar().style.width   = '100%';
      if (el.progressText()) el.progressText().textContent  = '100%';
    }
    hasLoadedFirstModel = true;
    const finish = () => {
      setOverlayVisible(el.loading(), false);
      setOverlayVisible(el.empty(), false);
      if (el.container()) {
        el.container().classList.add('has-model');
        el.container().removeAttribute('aria-busy');
      }
      resizeCanvas(ensureCanvas());
    };
    if (alreadyReady || options.immediate === true) finish();
    else setTimeout(finish, 280);
  };

  const getEnvUrl = () =>
    window.CUSTOMIZATION_3D_ENV_URL || FALLBACK_ENV_URL;

  const getEnvBlock = () => ({
    envPath: getEnvUrl(),
    intensity:  1.2,
    rotationY:  0.3,
    background: false,
  });

  const getGizmoBlock = () => {
    const o = window.CUSTOMIZATION_3D_GIZMO;
    if (o && typeof o === 'object') {
      return {
        position: o.position === true,
        rotation: o.rotation === true,
        scale: o.scale === true,
      };
    }
    return { position: false, rotation: false, scale: false };
  };

  const getDefaultInitCamera = () => ({
    position: { x: 5, y: 2, z: 7 },
    target: { x: 0, y: 1, z: 0 },
  });

  const normalizeCameraVec3 = (src, fallback) => {
    const out = { ...fallback };
    if (!src || typeof src !== 'object') return out;
    ['x', 'y', 'z'].forEach((k) => {
      const v = Number(src[k]);
      if (Number.isFinite(v)) out[k] = v;
    });
    return out;
  };

  const getInitCamera = () => {
    const base = getDefaultInitCamera();
    const o = window.CUSTOMIZATION_3D_INIT_CAMERA;
    if (!o || typeof o !== 'object') return { ...base };
    const out = {
      position: normalizeCameraVec3(o.position, base.position),
      target: normalizeCameraVec3(o.target, base.target),
    };
    ['alpha', 'beta', 'radius'].forEach((k) => {
      const v = Number(o[k]);
      if (Number.isFinite(v)) out[k] = v;
    });
    return out;
  };

  const buildInitConfig = (canvas, modelUrlList = []) => ({
    container: canvas,
    modelUrlList,
    env: getEnvBlock(),
    camera: getInitCamera(),
    gizmo: getGizmoBlock(),
    debug: false,
    logLevel: 0,
    verbose: false,
  });

  const buildBizInitConfig = (canvas) => {
    const apiBaseUrl = resolveVendorApiBaseUrl();
    const cfg = {
      container: canvas,
      apiBaseUrl,
      env: getEnvBlock(),
      camera: getInitCamera(),
      gizmo: getGizmoBlock(),
    };
    if (window.CUSTOMIZATION_3D_INITIAL_SKU != null) {
      cfg.initialSku = String(window.CUSTOMIZATION_3D_INITIAL_SKU);
    } else {
      cfg.initialSku = '';
    }
    return cfg;
  };

  const trySilenceSdkDebug = () => {
    if (window.CUSTOMIZATION_3D_VERBOSE_CONSOLE === true) return;
    const k = kernel || window.faceSDK?.kernel;
    if (!k) return;
    try {
      if (hasFn(k, 'debug')) {
        const d = k.debug;
        if (hasFn(d, 'setLevel')) d.setLevel(0);
        else if (hasFn(d, 'setEnabled')) d.setEnabled(false);
        else if (hasFn(d, 'disable')) d.disable();
      }
    } catch (_) {
    }
  };

  const pickRuntimeOrbitCamera = () => {
    if (!kernel) return null;
    const candidates = [
      kernel.scene?.activeCamera,
      kernel.scene?.cameras?.[0],
      kernel.viewer?.camera,
      kernel.camera?.instance,
      kernel.camera,
    ].filter(Boolean);
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      if (Number.isFinite(c.alpha) && Number.isFinite(c.beta)) return c;
    }
    return null;
  };

  const tryApplyInitCamera = (reason = '') => {
    if (window.CUSTOMIZATION_3D_SKIP_INIT_CAMERA_PATCH === true) return false;
    const cfg = getInitCamera();
    try {
      if (cfg.position && cfg.target && hasFn(kernel?.camera, 'set')) {
        kernel.camera.set({ position: cfg.position, target: cfg.target });
        if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS === true && !initCameraPatchLogged) {
          initCameraPatchLogged = true;
          console.info('[3D] 已通过 camera.set 应用初始视角（position / target）', {
            reason,
            position: cfg.position,
            target: cfg.target,
          });
        }
        return true;
      }

      if (!Number.isFinite(cfg.alpha) || !Number.isFinite(cfg.beta)) return false;

      const ac = pickRuntimeOrbitCamera();
      if (!ac) {
        if (!initCameraPatchWarned && window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS === true) {
          initCameraPatchWarned = true;
          console.warn(
            '[3D] 未检测到带 alpha/beta 的运行时相机；请使用 init.camera.position/target 或 kernel.camera.set。'
          );
        }
        return false;
      }
      ac.alpha = cfg.alpha;
      ac.beta = cfg.beta;
      if (Number.isFinite(cfg.radius) && Number.isFinite(ac.radius)) {
        ac.radius = cfg.radius;
      }
      try {
        if (typeof ac.rebuildAnglesAndRadius === 'function') ac.rebuildAnglesAndRadius();
      } catch (_) {
        /* optional Babylon helper */
      }
      if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS === true && !initCameraPatchLogged) {
        initCameraPatchLogged = true;
        console.info('[3D] 已通过 alpha/beta/radius 应用初始环绕相机视角（兼容旧配置）', {
          reason,
          alpha: cfg.alpha,
          beta: cfg.beta,
          radius: cfg.radius,
          cameraName: ac.name,
          cameraClass: ac.constructor && ac.constructor.name,
        });
      }
      return true;
    } catch (err) {
      if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS !== false) {
        console.warn('[3D] tryApplyInitCamera failed:', err);
      }
      return false;
    }
  };

  const scheduleApplyInitCamera = (reason) => {
    const run = () => tryApplyInitCamera(reason);
    requestAnimationFrame(run);
    setTimeout(() => run(), 48);
    setTimeout(() => run(), 220);
  };

  const reapplyEnvironmentAfterInit = async () => {
    if (!kernel?.environment) return;
    const envPath = getEnvUrl();
    const env = getEnvBlock();
    const trySetHdri = async (fn) => {
      const r = fn();
      await awaitIfPromise(r);
    };
    try {
      if (hasFn(kernel.environment, 'setHDRI')) {
        const attempts = [
          () => kernel.environment.setHDRI(envPath, env),
          () => kernel.environment.setHDRI({ ...env, path: envPath, url: envPath }),
          () => kernel.environment.setHDRI(envPath),
        ];
        for (const run of attempts) {
          try {
            await trySetHdri(run);
            return;
          } catch (_) {
            /* try next signature */
          }
        }
      }
      if (hasFn(kernel.environment, 'set')) {
        await awaitIfPromise(kernel.environment.set(env));
      }
    } catch (err) {
      console.warn('[3D] reapplyEnvironmentAfterInit failed:', err);
    }
  };

  const getModelAPIHost = () => {
    const k = kernel || window.faceSDK?.kernel;
    const candidates = [
      { path: 'kernel.model', value: k?.model },
      { path: 'kernel.models', value: k?.models },
      { path: 'kernel.modelManager', value: k?.modelManager },
      { path: 'kernel.manager.model', value: k?.manager?.model },
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      if (!c.value) continue;
      if (
        hasFn(c.value, 'add')
        || hasFn(c.value, 'remove')
        || hasFn(c.value, 'load')
        || hasFn(c.value, 'replace')
        || hasFn(c.value, 'destroy')
      ) {
        return c;
      }
    }
    return { path: 'kernel.model', value: k?.model || null };
  };

  const getSDKCapability = () => {
    const k = kernel || window.faceSDK?.kernel;
    const modelHost = getModelAPIHost();
    const m = modelHost.value;
    return {
      'kernel.init':          hasFn(k, 'init'),
      'kernel.on':            hasFn(k, 'on'),
      'kernel.off':           hasFn(k, 'off'),
      'camera.set':           hasFn(k?.camera, 'set'),
      'camera.animateTo':     hasFn(k?.camera, 'animateTo'),
      'lights.update':        hasFn(k?.lights, 'update'),
      'environment.setHDRI':  hasFn(k?.environment, 'setHDRI'),
      'hotspot.render':       hasFn(k?.hotspot, 'render'),
      'hotspot.on':           hasFn(k?.hotspot, 'on'),
      'model.add':            hasFn(m, 'add'),
      'model.remove':         hasFn(m, 'remove'),
      'model.removeByKey':    hasFn(m, 'removeByKey'),
      'model.removeByName':   hasFn(m, 'removeByName'),
      'model.load':           hasFn(m, 'load'),
      'model.replace':        hasFn(m, 'replace'),
      'model.destroy':        hasFn(m, 'destroy'),
      'model.on':             hasFn(m, 'on'),
      'transform.rotation':   hasFn(k?.transform, 'rotation'),
      'material.apply':       hasFn(k?.material, 'apply'),
      'material.color':       hasFn(k?.material, 'color'),
      'material.batch':       hasFn(k?.material, 'batch'),
      'material.reset':       hasFn(k?.material, 'reset'),
      'domTo3D.attach':       hasFn(k?.domTo3D, 'attach'),
      'domTo3D.detach':       hasFn(k?.domTo3D, 'detach'),
      'dropZone.setData':     hasFn(k?.dropZone, 'setData'),
      'dropZone.show':        hasFn(k?.dropZone, 'show'),
      'dropZone.hide':        hasFn(k?.dropZone, 'hide'),
      'dropZone.showWall':    hasFn(k?.dropZone, 'showWall'),
      'dropZone.clearZones':  hasFn(k?.dropZone, 'clearZones'),
      'dropZone.updateDivisions': hasFn(k?.dropZone, 'updateDivisions'),
      'dropZone.recordModelPlacement': hasFn(k?.dropZone, 'recordModelPlacement'),
      'model.removeAll':      hasFn(m, 'removeAll'),
      'debug':                hasFn(k, 'debug'),
    };
  };

  const getModelSlotKey = () =>
    (typeof window.CUSTOMIZATION_3D_MODEL_SLOT_KEY === 'string' && String(window.CUSTOMIZATION_3D_MODEL_SLOT_KEY).trim())
    || RUNTIME_MODEL_ID;

  const normalizeModelControlType = (v) => {
    const raw = String(v || '').trim().toLowerCase();
    if (raw === 'rotation') return 'rotation';
    if (raw === 'color') return 'color';
    return '';
  };

  const makeModelPayload = (modelId, modelUrl, modelControlType = '', modelName = '') => {
    const payload = {
      modelId,
      modelUrl,
    };
    const name = String(modelName || '').trim();
    if (name) payload.modelName = name;
    const ctl = normalizeModelControlType(modelControlType);
    if (ctl) payload.modelControlType = ctl;
    return payload;
  };

  const shouldLogModelAdd = () => window.CUSTOMIZATION_3D_LOG_MODEL_ADD === true;

  const getLiveAppModel = () =>
    kernel?.mainApp?.appModel
    || window.faceSDK?.kernel?.mainApp?.appModel
    || null;

  const getLiveModelDic = () => {
    const am = getLiveAppModel();
    return am?.modelDic || null;
  };

  const modelRuntimeHasKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    if (loadedModelKeys.has(k)) return true;
    const d = getLiveModelDic();
    try {
      return !!(d && typeof d.Has === 'function' && d.Has(k));
    } catch (_) {
      return false;
    }
  };

  const vendorGetRuntimeDebugSnapshot = () => {
    let dicKeys = [];
    try {
      const d = getLiveModelDic();
      dicKeys = d?.Keys ? d.Keys().slice() : [];
    } catch (_) {
      dicKeys = [];
    }
    return {
      viewerTrackedKeys: Array.from(loadedModelKeys),
      kernelModelDicKeys: dicKeys,
      currentModelAttachKey,
    };
  };

  const vendorDebugOperation = (label, detail = {}) => {
    if (window.CUSTOMIZATION_3D_DEBUG_VENDOR_OPS !== true) return;
    try {
      console.info(`[C3D vendor-op] ${label}`, {
        ...detail,
        ...vendorGetRuntimeDebugSnapshot(),
      });
    } catch (_) {
    }
  };

  const useVendorStrictMode = () => window.CUSTOMIZATION_3D_VENDOR_STRICT_MODE === true;

  const sdkRuntimeHasExactKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    const modelHost = getModelAPIHost().value;
    if (hasFn(modelHost, 'exists')) {
      try {
        if (modelHost.exists(k)) return true;
      } catch (_) {
      }
    }
    const d = getLiveModelDic();
    try {
      return !!(d && typeof d.Has === 'function' && d.Has(k));
    } catch (_) {
      return false;
    }
  };

  const waitForModelRuntimeKey = async (key, maxWait = 2500, interval = 80) => {
    const k = String(key || '').trim();
    if (!k) return false;
    if (sdkRuntimeHasExactKey(k)) return true;
    const started = Date.now();
    while (Date.now() - started < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, interval));
      if (sdkRuntimeHasExactKey(k)) return true;
    }
    return false;
  };

  const isExplicitAddFailure = (ret) => {
    if (ret === false) return true;
    if (ret && typeof ret === 'object' && Object.prototype.hasOwnProperty.call(ret, 'success')) {
      return ret.success === false;
    }
    return false;
  };

  const isExplicitRemovalSuccess = (ret) =>
    ret === true
    || Boolean(ret && typeof ret === 'object' && ret.success === true);

  const callModelAddCompat = async (modelHost, modelId, modelUrl, modelControlType = '', modelName = '') => {
    if (!hasFn(modelHost, 'add')) return false;
    const url = String(modelUrl || '').trim();
    if (!url || !/^https?:\/\//i.test(url)) return false;
    const payload = makeModelPayload(modelId, url, modelControlType, modelName);
    const log = (...args) => {
      if (shouldLogModelAdd()) console.info('[3D] model.add attempt', ...args);
    };
    try {
      const ret = await awaitIfPromise(modelHost.add(payload));
      log('kernel.model.add(payload)', 'return value', ret);
      if (isExplicitAddFailure(ret)) return false;
      loadedModelKeys.add(modelId);
      return true;
    } catch (err) {
      log('kernel.model.add(payload)', 'threw', err);
      return vendorModelExists(modelId);
    }
  };

  const resolveVendorApiBaseUrl = () => {
    const raw = window.CUSTOMIZATION_3D_VENDOR_API_BASE;
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim().replace(/\/$/, '');
    }
    return C3D_VENDOR_ORIGIN_DEFAULT;
  };
  const VENDOR_PLACEMENT_ZONE_COLOR = '#3F96B4';
  const VENDOR_PLACEMENT_ZONE_ALPHA = 0.4;
  let vendorActiveSku = '';
  let lastHotspotClickDetail = null;
  let hotspotBizContextApplied = false;
  let vendorHotspotClearInFlight = false;
  const vendorHotspotCatalog = new Map();
  let vendorDefaultHotspotId = '';
  let vendorHotspotCatalogModelKey = '';
  const C3D_DEFAULT_PLACEMENT_SLOT = '__default__';
  const VENDOR_HOTSPOT_CLEAR_PAYLOAD = { highlight: true, hotspot: true, pending: true };
  const normalizeVendorSkuKey = (sku) => String(sku || '').trim().toLowerCase();

  let vendorDropZoneCache = null;
  let vendorKernelReadyResolve = null;
  let vendorColorGeneration = 0;
  let bizSelectGeneration = 0;
  let vendorHotspotRenderSignature = '';
  let vendorHotspotsRendered = false;
  let vendorLastPlacedModelIds = [];
  let vendorLastRemovedModelIds = [];
  let vendorLoadedMainSku = '';
  let vendorMainPlacementZoneCache = null;
  let vendorDropzoneModelReady = true;
  let vendorDropzonePlacementPending = false;
  let vendorDropzoneBusyTipTimer = 0;
  const vendorDropzoneInFlight = new Set();
  const vendorDropzoneRecent = new Map();
  const DROPZONE_DEDUPE_MS = 600;
  const modelPlacementRegistry = new Map();
  const modelSkuRegistry = new Map();
  const modelGroupRegistry = new Map();
  const modelRoleRegistry = new Map();
  const modelControlTypeRegistry = new Map();
  const vendorProductConfigCache = new Map();
  const vendorProductConfigPromiseCache = new Map();
  let vendorStableWallDivisions = null;
  let vendorPreviewWallDivisions = null;
  let vendorPlacementPreviewActive = false;
  let vendorLastCommittedAccessorySku = '';
  let c3dLastOutlineAttachKey = '';
  const vendorKernelReadyPromise = new Promise((resolve) => {
    vendorKernelReadyResolve = resolve;
  });

  const vendorGetApiUrl = (path) => `${resolveVendorApiBaseUrl()}${path}`;

  const DROPZONE_PLACEMENT_BUSY_TIP_ID = 'customization-3d-dropzone-busy-tip';

  const c3dString = (key, fallback = '') => {
    const s = window.CUSTOMIZATION_3D_STRINGS?.[key];
    return typeof s === 'string' && s ? s : fallback;
  };

  const showDropzonePlacementBusyTip = () => {
    const container = document.querySelector('[data-3d-container]');
    if (!container) return;
    let tip = document.getElementById(DROPZONE_PLACEMENT_BUSY_TIP_ID);
    if (!tip) {
      tip = document.createElement('div');
      tip.id = DROPZONE_PLACEMENT_BUSY_TIP_ID;
      tip.setAttribute('role', 'status');
      tip.textContent = c3dString(
        'dropzone_model_loading',
        'Model is loading. Please wait before placing another.'
      );
      Object.assign(tip.style, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: '30',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 18px',
        borderRadius: '8px',
        background: 'rgba(0, 0, 0, 0.72)',
        color: '#fff',
        fontSize: '14px',
        lineHeight: '1.4',
        textAlign: 'center',
        pointerEvents: 'none',
        maxWidth: 'min(90%, 320px)',
      });
      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }
      container.appendChild(tip);
    }
    tip.style.display = 'flex';
    if (vendorDropzoneBusyTipTimer) clearTimeout(vendorDropzoneBusyTipTimer);
    vendorDropzoneBusyTipTimer = setTimeout(() => {
      vendorDropzoneBusyTipTimer = 0;
      tip.style.display = 'none';
    }, 2800);
  };

  const hideDropzonePlacementBusyTip = () => {
    if (vendorDropzoneBusyTipTimer) {
      clearTimeout(vendorDropzoneBusyTipTimer);
      vendorDropzoneBusyTipTimer = 0;
    }
    const tip = document.getElementById(DROPZONE_PLACEMENT_BUSY_TIP_ID);
    if (tip) tip.style.display = 'none';
  };

  const vendorReleaseDropzonePlacementLock = () => {
    vendorDropzoneModelReady = true;
    vendorDropzonePlacementPending = false;
    hideDropzonePlacementBusyTip();
  };

  const vendorAcquireDropzonePlacementLock = () => {
    vendorDropzoneModelReady = false;
    vendorDropzonePlacementPending = true;
  };

  const dropzonePlacementKey = (sku, dropzoneData) => {
    const wall = String(
      dropzoneData?.wallName
      || dropzoneData?.wall
      || dropzoneData?.name
      || ''
    ).trim();
    const index = dropzoneData?.index ?? dropzoneData?.zoneIndex ?? dropzoneData?.slot;
    return `${String(sku || '').trim()}|${wall}|${index == null ? '' : String(index)}`;
  };

  const isDropzonePlacementDuplicate = (key) =>
    vendorDropzoneInFlight.has(key) || vendorDropzoneRecent.has(key);

  const markDropzonePlacementRecent = (key) => {
    const prev = vendorDropzoneRecent.get(key);
    if (prev) clearTimeout(prev);
    vendorDropzoneRecent.set(key, setTimeout(() => {
      vendorDropzoneRecent.delete(key);
    }, DROPZONE_DEDUPE_MS));
  };

  const vendorLooksLikeDropZone = (obj) =>
    !!obj && (hasFn(obj, 'setData') || hasFn(obj, 'show') || hasFn(obj, 'clearZones'));

  const vendorResolveDropZone = () => {
    if (!kernel) return null;
    if (vendorDropZoneCache && vendorLooksLikeDropZone(vendorDropZoneCache)) return vendorDropZoneCache;
    const candidates = [
      kernel.dropZone,
      kernel.mainApp?.dropZone,
      kernel.mainApp?.appDropZone,
      kernel.manager?.dropZone,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      if (vendorLooksLikeDropZone(candidates[i])) {
        vendorDropZoneCache = candidates[i];
        return candidates[i];
      }
    }
    const scan = (host) => {
      if (!host || typeof host !== 'object') return null;
      for (const key of Object.keys(host)) {
        const val = host[key];
        if (vendorLooksLikeDropZone(val)) return val;
      }
      return null;
    };
    const found = scan(kernel) || scan(kernel.mainApp);
    if (found) vendorDropZoneCache = found;
    return found;
  };

  const vendorRecheckDropZone = () => {
    vendorDropZoneCache = null;
    return !!vendorResolveDropZone();
  };

  const vendorWhenKernelReady = async (maxWaitMs = 20000) => {
    if (kernel) return kernel;
    let timer = null;
    try {
      return await Promise.race([
        vendorKernelReadyPromise,
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('[3D] kernel ready timeout')), maxWaitMs);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const vendorFetchProductConfig = async (sku) => {
    const skuKey = String(sku || '').trim();
    if (!skuKey) return null;
    if (vendorProductConfigCache.has(skuKey)) return vendorProductConfigCache.get(skuKey);
    if (vendorProductConfigPromiseCache.has(skuKey)) return vendorProductConfigPromiseCache.get(skuKey);
    const promise = fetch(vendorGetApiUrl(`/api/product-configs/by-sku/${encodeURIComponent(skuKey)}`))
      .then((response) => response.json())
      .then((json) => {
        vendorProductConfigPromiseCache.delete(skuKey);
        vendorProductConfigCache.set(skuKey, json);
        return json;
      })
      .catch((err) => {
        vendorProductConfigPromiseCache.delete(skuKey);
        throw err;
      });
    vendorProductConfigPromiseCache.set(skuKey, promise);
    return promise;
  };

  const vendorApplyDropZoneData = (placementZone) => {
    if (!placementZone) return;
    const dz = vendorResolveDropZone();
    if (!hasFn(dz, 'setData')) return;
    const { alpha, border_color, color, show_border, thickness, walls } = placementZone;
    dz.setData({
      color,
      alpha: +alpha,
      thickness,
      showBorder: !show_border,
      borderColor: border_color,
      walls,
    });
  };

  const vendorApplyPlacementZoneFromConfig = (placementZone) => {
    if (!placementZone || typeof placementZone !== 'object') return false;
    const walls = placementZone.walls;
    if (!Array.isArray(walls) || !walls.length) return false;
    vendorApplyDropZoneData({
      ...placementZone,
      color: placementZone.color || VENDOR_PLACEMENT_ZONE_COLOR,
      alpha: placementZone.alpha != null ? placementZone.alpha : VENDOR_PLACEMENT_ZONE_ALPHA,
      border_color: placementZone.border_color || placementZone.borderColor || VENDOR_PLACEMENT_ZONE_COLOR,
    });
    return true;
  };

  const vendorClearMaterialTextureState = (materialName, clearAlbedo, clearNormal) => {
    const target = String(materialName || '').trim();
    if (!target || (!clearAlbedo && !clearNormal)) return;
    const seen = new Set();
    const targetLower = target.toLowerCase();
    const matchesTarget = (mat) => {
      const names = [
        mat?.name,
        mat?.id,
        mat?._name,
      ].map((v) => String(v || '').trim()).filter(Boolean);
      return names.some((name) => {
        const lower = name.toLowerCase();
        return lower === targetLower
          || lower.startsWith(`${targetLower}_`)
          || lower.startsWith(`${targetLower}.`)
          || lower.startsWith(`${targetLower} `);
      });
    };
    const pushMaterial = (mat, list) => {
      if (!mat || seen.has(mat)) return;
      seen.add(mat);
      list.push(mat);
      const subs = mat.subMaterials;
      if (Array.isArray(subs)) {
        subs.forEach((sub) => pushMaterial(sub, list));
      }
    };
    const clearMaterial = (mat) => {
      if (!mat || !matchesTarget(mat)) return;
      if (clearAlbedo) {
        if ('albedoTexture' in mat) mat.albedoTexture = null;
        if ('diffuseTexture' in mat) mat.diffuseTexture = null;
        if ('baseTexture' in mat) mat.baseTexture = null;
        if ('baseColorTexture' in mat) mat.baseColorTexture = null;
        if ('_albedoTexture' in mat) mat._albedoTexture = null;
      }
      if (clearNormal) {
        if ('bumpTexture' in mat) mat.bumpTexture = null;
        if ('normalTexture' in mat) mat.normalTexture = null;
        if ('_bumpTexture' in mat) mat._bumpTexture = null;
      }
      if (typeof mat.markAsDirty === 'function') mat.markAsDirty(1);
      if (typeof mat.markDirty === 'function') mat.markDirty();
    };
    try {
      const scene = getRuntimeScene();
      const materials = [];
      if (Array.isArray(scene?.materials)) scene.materials.forEach((mat) => pushMaterial(mat, materials));
      if (Array.isArray(scene?.multiMaterials)) scene.multiMaterials.forEach((mat) => pushMaterial(mat, materials));
      getSceneMeshes().forEach((mesh) => pushMaterial(mesh?.material, materials));
      materials.forEach((mat) => {
        clearMaterial(mat);
      });
    } catch (_) {
    }
  };

  const vendorStripScopedMaterialSuffix = (name, modelId) => {
    const raw = String(name || '').trim();
    const key = String(modelId || '').trim();
    if (!raw || !key) return raw;
    const suffix = `_${key}`;
    let out = raw;
    while (out.endsWith(suffix)) {
      out = out.slice(0, -suffix.length);
    }
    return out || raw;
  };

  const vendorMaterialMatchesTarget = (materialName, targetName, modelId) => {
    const mat = String(materialName || '').trim();
    const target = String(targetName || '').trim();
    if (!mat || !target) return false;
    if (mat === target) return true;
    return vendorStripScopedMaterialSuffix(mat, modelId) === target;
  };

  const vendorApplyMaterialEventToMeshes = (event, modelId, meshes = []) => {
    const key = String(modelId || '').trim();
    const td = vendorGetTargetData(event);
    const materialName = String(
      event.material_name || event.materialName || td.material_name || td.materialName || ''
    ).trim();
    const color = td.color ?? td.hex ?? td.hexColor ?? td.albedoColor;
    const targetMeshes = meshes.filter((mesh) => {
      if (!mesh?.material) return false;
      if (!materialName) return true;
      return vendorMaterialMatchesTarget(mesh.material.name, materialName, key);
    });
    if (!targetMeshes.length) return false;

    let didApply = false;
    targetMeshes.forEach((mesh) => {
      const mat = mesh.material;
      if (!mat) return;
      let nextMat = mat;
      const baseName = vendorStripScopedMaterialSuffix(mat.name || materialName || 'material', key);
      const scopedName = key ? `${baseName}_${key}` : baseName;
      if (scopedName && String(mat.name || '') !== scopedName && typeof mat.clone === 'function') {
        try {
          nextMat = mat.clone(scopedName);
          mesh.material = nextMat;
        } catch (_) {
          nextMat = mat;
        }
      }
      const colorValue = vendorHexToColor3Like(color, nextMat.albedoColor || nextMat.diffuseColor);
      if (colorValue && 'albedoColor' in nextMat) {
        nextMat.albedoColor = colorValue;
        didApply = true;
      }
      if (colorValue && 'diffuseColor' in nextMat) {
        nextMat.diffuseColor = colorValue;
        didApply = true;
      }
      try {
        if (typeof nextMat.markAsDirty === 'function') nextMat.markAsDirty(1);
      } catch (_) {
      }
    });

    return didApply;
  };

  const vendorHasNumeric = (value) => value != null && value !== '' && Number.isFinite(Number(value));

  const vendorBuildMaterialApplyPayload = (event, modelId = '') => {
    const td = vendorGetTargetData(event) || {};
    const materialName = String(
      event?.material_name || event?.materialName || td.material_name || td.materialName || ''
    ).trim();
    const color = td.color ?? td.hex ?? td.hexColor ?? td.albedoColor;
    const color_map_url = td.color_map_url || td.colorMapUrl || td.albedoTexture;
    const normal_map_url = td.normal_map_url || td.normalMapUrl || td.normalMap;
    if (!materialName && color == null && !color_map_url && !normal_map_url) return null;
    const payload = { target: materialName };
    const key = String(modelId || '').trim();
    if (key) payload.modelId = key;
    if (color != null && color !== '') payload.albedoColor = color;
    if (color_map_url) payload.albedoTexture = color_map_url;
    if (normal_map_url) payload.normalMap = normal_map_url;
    if (vendorHasNumeric(td.metallic)) payload.metallic = +td.metallic;
    if (vendorHasNumeric(td.roughness)) payload.roughness = +td.roughness;
    return payload;
  };

  const vendorApplyChangeColorEvent = (event) => {
    const payload = vendorBuildMaterialApplyPayload(event);
    if (!payload || !hasFn(kernel?.material, 'apply')) return false;
    vendorDebugOperation('color:global-apply', {
      target: payload.target,
      color: payload.albedoColor,
      reason: 'no modelId scope',
    });
    kernel.material.apply(payload);
    return true;
  };

  const vendorApplyMainColorOnlyEvent = (event, modelKey = '') => {
    const attachKey = String(modelKey || '').trim();
    if (!attachKey) return vendorApplyChangeColorEvent(event);
    const payload = vendorBuildMaterialApplyPayload(event, attachKey);
    if (!payload || !hasFn(kernel?.material, 'apply')) return false;
    try {
      vendorDebugOperation('color:main-scoped-apply', {
        target: payload.target,
        modelId: attachKey,
        color: payload.albedoColor,
        sdkHasRuntimeKey: sdkRuntimeHasExactKey(attachKey),
      });
      kernel.material.apply(payload);
      return true;
    } catch (_) {
      return false;
    }
  };

  const vendorBuildDivisionInclude = (pergolaSkuRaw, accessorySkuRaw) => {
    const pergolaSku = String(pergolaSkuRaw || '').trim();
    const accessorySku = String(accessorySkuRaw || '').trim();
    const divisionInclude = [];
    if (!pergolaSku || !accessorySku) return divisionInclude;

    const only10_13 = /(?=.*10)(?=.*13)/.test(pergolaSku);
    const only10 = /(?=.*10)(?!.*13)(?!.*12)(?!.*20)/.test(pergolaSku);
    const only10_12 = /(?=.*10)(?=.*12)/.test(pergolaSku);
    const only10_20 = /(?=.*10)(?=.*20)/.test(pergolaSku);
    const has10 = /10/.test(accessorySku);
    const has13 = /13/.test(accessorySku);
    const has12 = /12/.test(accessorySku);
    const isSpf111s1020Pillar4pcs = pergolaSku === 'SPF111S1020PILLAR4PCS';

    if (only10 && has10) {
      divisionInclude.push('前', '后', '左', '右', '前1', '后1', '前2', '后2');
    }
    if (only10_13 && has10) divisionInclude.push('左', '右');
    if (only10_13 && has13) divisionInclude.push('前', '后');
    if (only10_12 && has12) divisionInclude.push('前', '后');
    if (only10_12 && has10) divisionInclude.push('左', '右');
    if (only10_20 && has10) {
      if (isSpf111s1020Pillar4pcs) {
        divisionInclude.push('左', '右');
      } else {
        divisionInclude.push('前', '后', '左', '右', '前1', '后1', '前2', '后2');
      }
    }
    if (only10_20 && has13 && isSpf111s1020Pillar4pcs) {
      divisionInclude.push('前', '后');
    }
    if (pergolaSku === 'SPF111SEM13' && has13) {
      divisionInclude.push('前', '后');
    }
    if (pergolaSku === 'SPF111SEM13' && has10) {
      divisionInclude.push('左', '右');
    }
    return divisionInclude;
  };

  const vendorFilterWallDivisionsForAccessory = (pergolaSku, accessorySku, wallDivisions) => {
    if (!Array.isArray(wallDivisions)) return [];
    const divisionInclude = vendorBuildDivisionInclude(pergolaSku, accessorySku);
    if (!divisionInclude.length) return [];
    return wallDivisions.filter((wall) => divisionInclude.includes(String(wall?.name || '').trim()));
  };

  const vendorCollectWallNamesFromZones = (zones, divisions) => {
    const wallNames = new Set();
    if (Array.isArray(zones)) {
      zones.forEach((zone) => {
        const name = String(zone?.wallName || zone?.name || '').trim();
        if (name) wallNames.add(name);
      });
    }
    if (!wallNames.size && Array.isArray(divisions)) {
      divisions.forEach((wall) => {
        const name = String(wall?.name || '').trim();
        if (name) wallNames.add(name);
      });
    }
    return wallNames;
  };

  const vendorCloneWallDivisions = (wallDivisions) => {
    if (!Array.isArray(wallDivisions) || !wallDivisions.length) return null;
    return wallDivisions.map((wall) => ({
      name: wall.name,
      divisions: wall.divisions,
    }));
  };

  const vendorRunPlacementDivisionUpdate = (divisions) => {
    const dz = vendorResolveDropZone();
    if (!dz || !hasFn(dz, 'updateDivisions')) return null;
    if (!Array.isArray(divisions) || !divisions.length) return null;
    if (vendorMainPlacementZoneCache) {
      vendorApplyDropZoneData(vendorMainPlacementZoneCache);
    }
    try {
      const zones = dz.updateDivisions(divisions);
      return { dz, zones, divisions };
    } catch (err) {
      console.warn('[3D] dropZone.updateDivisions failed:', err);
      return null;
    }
  };

  const vendorApplyPlacementZoneDivisions = (divisions, showWalls = true) => {
    const update = vendorRunPlacementDivisionUpdate(divisions);
    if (!update) return false;
    const { dz, zones } = update;

    if (hasFn(dz, 'hide')) {
      dz.hide();
    } else if (hasFn(dz, 'hideAll')) {
      dz.hideAll();
    }

    const wallNamesToShow = new Set();
    if (Array.isArray(zones)) {
      zones.forEach((zone) => {
        if (zone && zone.wallName != null) {
          wallNamesToShow.add(String(zone.wallName));
        }
      });
    }
    vendorDebugOperation('placement:getPlacementZone', {
      divisionCount: divisions.length,
      zonesReturned: Array.isArray(zones) ? zones.length : null,
      wallNames: [...wallNamesToShow],
      showWalls,
    });

    if (!showWalls) return true;

    if (!hasFn(dz, 'showWall') || !wallNamesToShow.size) return false;

    wallNamesToShow.forEach((wallName) => {
      try {
        dz.showWall(wallName);
      } catch (err) {
        console.warn('[3D] dropZone.showWall failed:', wallName, err);
      }
    });
    return true;
  };

  const vendorBuildPlacementDivisionsForSku = (skuKey, result) => {
    const pergolaSku = String(vendorLoadedMainSku || '').trim();
    if (!skuKey) return { ok: false, reason: 'empty_sku' };
    if (!pergolaSku) return { ok: false, reason: 'pergola_required' };
    if (result?.code !== 200 || !result?.data) return { ok: false, reason: 'not_found', result };

    const { enable_placement_zone, wall_divisions } = result.data;
    if (!enable_placement_zone || wall_divisions == null) {
      return { ok: false, reason: 'no_placement_zone', result };
    }

    const filteredWalls = vendorFilterWallDivisionsForAccessory(pergolaSku, skuKey, wall_divisions);
    if (window.CUSTOMIZATION_3D_DEBUG_VENDOR_OPS === true) {
      console.info('[放置区域] 本次配件的方向:', vendorBuildDivisionInclude(pergolaSku, skuKey));
      console.info('[放置区域] 当前配件的墙面配置:', wall_divisions);
      console.info('[放置区域] 当前显示的墙面:', filteredWalls);
    }

    const divisions = filteredWalls.map((wall) => ({
      name: wall.name,
      divisions: wall.divisions,
    }));

    return { ok: true, result, pergolaSku, divisions };
  };

  const vendorApplyPlacementConfigForSku = (skuKey, result, options = {}) => {
    const built = vendorBuildPlacementDivisionsForSku(skuKey, result);
    if (!built.ok) return built;

    vendorPlacementPreviewActive = options.preview === true;
    const showWalls = options.showWalls !== false;
    const shown = vendorApplyPlacementZoneDivisions(built.divisions, showWalls);
    if (!shown) {
      return {
        ok: false,
        reason: 'no_matching_divisions',
        result: built.result,
        pergolaSku: built.pergolaSku,
      };
    }

    return {
      ok: true,
      mode: 'placement_zone',
      result: built.result,
      pergolaSku: built.pergolaSku,
      sku: skuKey,
      divisionCount: built.divisions.length,
      divisions: built.divisions,
    };
  };

  const vendorSyncPlacementDivisionsForSkuFromCache = (sku, options = {}) => {
    const skuKey = String(sku || '').trim();
    if (!skuKey || !vendorProductConfigCache.has(skuKey)) return false;
    const applied = vendorApplyPlacementConfigForSku(skuKey, vendorProductConfigCache.get(skuKey), {
      preview: false,
      showWalls: options.showWalls === true,
    });
    return applied?.ok === true;
  };

  const vendorSyncPlacementDivisionsForModelKey = (modelKey) => {
    const key = String(modelKey || '').trim();
    const sku = key ? String(modelSkuRegistry.get(key) || '').trim() : '';
    if (!sku) return false;
    return vendorSyncPlacementDivisionsForSkuFromCache(sku, { showWalls: false });
  };

  const vendorRunGetPlacementZone = async (accessorySku, options = {}) => {
    const skuKey = String(accessorySku || '').trim();
    if (!skuKey) return { ok: false, reason: 'empty_sku' };

    const result = await vendorFetchProductConfig(skuKey);
    return vendorApplyPlacementConfigForSku(skuKey, result, options);
  };

  const vendorCommitPlacementZoneDivisions = (_wallDivisions, accessorySku = '') => {
    const sku = String(accessorySku || '').trim();
    if (!sku) return false;
    vendorLastCommittedAccessorySku = sku;
    vendorPlacementPreviewActive = false;
    return true;
  };

  const vendorCancelPlacementZonePreview = async () => {
    vendorPlacementPreviewActive = false;
    const restoreSku = String(vendorLastCommittedAccessorySku || '').trim();
    if (!restoreSku) {
      vendorHidePlacementZones();
      return { ok: true, mode: 'cancel_hide_only' };
    }
    return vendorRunGetPlacementZone(restoreSku, { preview: false, showWalls: false });
  };

  const vendorHexToColor3Like = (hex, current) => {
    const raw = String(hex || '').trim();
    const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(raw);
    if (!m) return null;
    const full = m[1].length === 3
      ? m[1].split('').map((c) => c + c).join('')
      : m[1];
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const Ctor = current?.constructor;
    try {
      if (typeof Ctor === 'function') return new Ctor(r, g, b);
    } catch (_) {
    }
    return { r, g, b };
  };

  const getRegisteredMeshesForKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return [];
    const reg = modelMeshRegistry.get(k);
    if (!reg) return [];
    const hasIds = !!reg.ids?.size;
    return getSceneMeshes().filter((mesh) => {
      const id = getMeshUniqueId(mesh);
      const name = String(mesh?.name || '').trim();
      if (hasIds) return id && reg.ids.has(id);
      return name && reg.names?.has(name);
    });
  };

  const vendorApplyScopedChangeColorEvent = (event, modelId) => {
    const key = String(modelId || '').trim();
    if (!key) return false;
    const payload = vendorBuildMaterialApplyPayload(event, key);
    if (!payload || !hasFn(kernel?.material, 'apply')) return false;
    try {
      vendorDebugOperation('color:accessory-scoped-apply', {
        target: payload.target,
        modelId: key,
        color: payload.albedoColor,
        hasAlbedoTexture: !!payload.albedoTexture,
        hasNormalMap: !!payload.normalMap,
        sdkHasRuntimeKey: sdkRuntimeHasExactKey(key),
      });
      kernel.material.apply(payload);
      return true;
    } catch (err) {
      console.warn('[3D] scoped material.apply failed; falling back to mesh material update', err);
    }
    return false;
  };

  const vendorApplySkuColorToModel = async (sku, modelKey) => {
    const skuKey = String(sku || '').trim();
    const key = String(modelKey || '').trim();
    if (!skuKey || !key) return { ok: false, reason: 'missing_sku_or_model' };
    const applyOnce = async () => {
      await vendorWhenKernelReady();
      await waitForModelRuntimeKey(key);
      const result = await vendorFetchProductConfig(skuKey);
      if (result?.code !== 200 || !result?.data) return { ok: false, reason: 'not_found', result };
      const events = Array.isArray(result.data.events) ? result.data.events : [];
      const colorEvents = events.filter((event) => vendorGetEventType(event) === 'change_color');
      if (!colorEvents.length) return { ok: false, reason: 'no_color_events', result };
      let didWork = false;
      colorEvents.forEach((event) => {
        didWork = vendorApplyScopedChangeColorEvent(event, key) || didWork;
      });
      return {
        ok: didWork,
        mode: didWork ? 'scoped_change_color' : 'no_matching_material',
        sku: skuKey,
        result,
      };
    };
    let outcome = await applyOnce();
    if (!outcome.ok) {
      await new Promise((resolve) => setTimeout(resolve, 180));
      outcome = await applyOnce();
    }
    return outcome;
  };

  const vendorModelExists = (modelKey) => {
    const key = String(modelKey || '').trim();
    if (!key) return false;
    if (hasFn(kernel?.model, 'exists')) {
      try {
        return !!kernel.model.exists(key);
      } catch (_) {
      }
    }
    return modelRuntimeHasKey(key);
  };

  const vendorRuntimeModelKey = (modelName, modelId) => {
    const name = String(modelName || '').trim();
    const id = String(modelId || '').trim();
    return name && id ? `${name}_${id}` : (id || name);
  };

  const getModelRole = (key) => String(modelRoleRegistry.get(String(key || '').trim()) || '').trim().toLowerCase();

  const isRotationModelKey = (key) =>
    normalizeModelControlType(modelControlTypeRegistry.get(String(key || '').trim())) === 'rotation';

  const isMainModelRoleKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    if (isRotationModelKey(k)) return false;
    const role = getModelRole(k);
    if (role === 'furniture' || role === 'accessory') return false;
    const mappedSku = String(modelSkuRegistry.get(k) || '').trim().toLowerCase();
    const mainSku = String(vendorLoadedMainSku || '').trim().toLowerCase();
    if (role === 'main' && mappedSku && mainSku) return mappedSku === mainSku;
    if (role === 'main') return true;
    if (k === 'c3d-single-main') return true;
    if (mappedSku && mainSku) return mappedSku === mainSku;
    return false;
  };

  const isFurnitureModelRoleKey = (key) => getModelRole(key) === 'furniture';

  const isAccessoryRuntimeKey = (key) => {
    const k = String(key || '').trim();
    if (!/_\d{10,}$/.test(k)) return false;
    const role = modelRoleRegistry.get(k);
    if (role) return role === 'accessory';
    return true;
  };

  const listTrackedModelKeysByRole = (role) => {
    const want = String(role || '').trim().toLowerCase();
    if (!want) return [];
    const keys = [];
    modelRoleRegistry.forEach((value, key) => {
      if (String(value || '').trim().toLowerCase() === want) keys.push(String(key || '').trim());
    });
    return keys.filter(Boolean);
  };

  const filterNonMainModelKeys = (keys) => (
    (Array.isArray(keys) ? keys : [keys])
      .map((key) => String(key || '').trim())
      .filter((key) => key && !isMainModelRoleKey(key))
  );

  const isLiveTrackedModelKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    return kernelModelExistsByCandidates(k) || modelKeyHasVisibleMeshes(k);
  };

  const isConfigCatalogModelKey = (key) => {
    const k = String(key || '').trim();
    if (!k || /_\d{10,}$/.test(k)) return false;
    return /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(k);
  };

  const isRemovableFurnitureTrackedKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    if (isConfigCatalogModelKey(k) && !modelKeyHasVisibleMeshes(k)) return false;
    return modelKeyHasVisibleMeshes(k) || (
      !isConfigCatalogModelKey(k) && isLiveTrackedModelKey(k)
    );
  };

  const removeTrackedModelsByRole = async (role) => {
    const mainSku = String(vendorLoadedMainSku || '').trim().toLowerCase();
    const keys = filterNonMainModelKeys(listTrackedModelKeysByRole(role)).filter((key) => {
      const mappedSku = String(modelSkuRegistry.get(key) || '').trim().toLowerCase();
      if (!mappedSku || !mainSku) return true;
      return mappedSku !== mainSku;
    });
    let removed = false;
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const canRemoveInSdk = role === 'furniture'
        ? isRemovableFurnitureTrackedKey(key)
        : isLiveTrackedModelKey(key);
      if (!canRemoveInSdk) {
        finalizeModelRemoval(key);
        removed = true;
        continue;
      }
      if (await removeRegisteredModelByKey(key)) removed = true;
    }
    return removed;
  };

  const ensureAccessoryMeshesRegistered = async (key) => {
    const k = String(key || '').trim();
    if (!k || !isAccessoryRuntimeKey(k)) return;
    if (hasRegisteredMeshIds(k)) return;
    await registerModelMeshes(k);
  };

  const ensureTrackedModelMeshesRegistered = async (key, modelControlType = '') => {
    const k = String(key || '').trim();
    if (!k) return;
    if (!loadedModelKeys.has(k)) loadedModelKeys.add(k);
    const controlType = normalizeModelControlType(modelControlType);
    if (controlType) modelControlTypeRegistry.set(k, controlType);
    if (hasRegisteredMeshIds(k)) {
      if (controlType) {
        getRegisteredMeshesForKey(k).forEach((mesh) => {
          try {
            if (!mesh.metadata || typeof mesh.metadata !== 'object') mesh.metadata = {};
            mesh.metadata.modelControlType = controlType;
          } catch (_) {
          }
        });
      }
      return;
    }
    await registerModelMeshes(k, new Set(), modelControlType);
  };

  const vendorGetModelKeysFromTarget = (targetData) => {
    const name = targetData?.name;
    const category = targetData?.category || targetData?.modelId || targetData?.model_id;
    const keys = [];
    if (name && category) keys.push(vendorRuntimeModelKey(name, category));
    if (category) keys.push(String(category).trim());
    return [...new Set(keys.filter(Boolean))];
  };

  const vendorAnyModelExists = (targetData) =>
    vendorGetModelKeysFromTarget(targetData).some((k) => vendorModelExists(k));

  const vendorHasSceneModels = () => {
    const dic = getLiveModelDic();
    if (dic && typeof dic.Keys === 'function') {
      try {
        const keys = dic.Keys();
        if (Array.isArray(keys) && keys.length > 0) return true;
      } catch (_) {
      }
    }
    return loadedModelKeys.size > 0;
  };

  const vendorHidePlacementZones = () => {
    const dz = vendorResolveDropZone();
    if (hasFn(dz, 'hide')) dz.hide();
  };

  const vendorGetEventType = (event) => {
    const raw = String(event?.event_type || event?.eventType || event?.type || event?.name || '').trim();
    const compact = raw.replace(/[\s_-]+/g, '').toLowerCase();
    if (compact === 'changemodel' || compact === 'modelchange' || compact === 'replace model'.replace(/\s+/g, '')) return 'change_model';
    if (compact === 'changecolor' || compact === 'colorchange' || compact === 'replace color'.replace(/\s+/g, '')) return 'change_color';
    return raw;
  };

  const vendorGetTargetData = (event) => {
    const raw = event?.target_data || event?.targetData || event?.data || event?.target || {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (_) {
        return {};
      }
    }
    return raw && typeof raw === 'object' ? raw : {};
  };

  const vendorResolveFileUrl = (targetData) => {
    const candidates = [
      targetData?.file_url,
      targetData?.fileUrl,
      targetData?.model_url,
      targetData?.modelUrl,
      targetData?.url,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      if (typeof c === 'string' && c.trim()) return c.trim();
      if (c && typeof c === 'object') {
        const nested = c.url || c.file_url || c.fileUrl || c.href;
        if (typeof nested === 'string' && nested.trim()) return nested.trim();
      }
    }
    return '';
  };

  const vendorCleanModelIdPart = (...values) => {
    for (let i = 0; i < values.length; i += 1) {
      let v = String(values[i] || '').trim();
      if (!v || v.toLowerCase() === 'undefined' || v.toLowerCase() === 'null') continue;
      v = v.replace(/^undefined[_-]+/i, '').replace(/^null[_-]+/i, '').trim();
      if (v && v.toLowerCase() !== 'undefined' && v.toLowerCase() !== 'null') return v;
    }
    return 'accessory';
  };

  const vendorRemoveMainModels = async (targetData) => {
    const keys = vendorGetModelKeysFromTarget(targetData);
    if (!hasFn(kernel?.model, 'remove')) return;
    for (let i = 0; i < keys.length; i += 1) {
      try {
        await awaitIfPromise(kernel.model.remove(keys[i]));
        loadedModelKeys.delete(keys[i]);
      } catch (_) {
      }
    }
  };

  const buildAccessoryDragOptions = (rotation) => {
    const ry = Number(rotation?.y ?? 0);
    return {
      enable: true,
      axis: ry === 0 || ry === 180 ? 'x' : 'z',
      step: 0.1,
      snapToZone: true,
      returnWhenOutOfBounds: true,
      handleOccupiedZone: true,
      occupiedZoneAction: 'replace',
    };
  };

  const vendorTrackModelPlacement = (wallName, index, runtimeKey) => {
    const key = String(runtimeKey || '').trim();
    if (!key || wallName == null || index == null) return;
    modelPlacementRegistry.set(key, { wallName, index });
  };

  const vendorRecordModelPlacement = (wallName, index, runtimeKey) => {
    const dz = vendorResolveDropZone();
    if (!hasFn(dz, 'recordModelPlacement')) return;
    const key = String(runtimeKey || '').trim();
    if (!key) return;
    dz.recordModelPlacement(wallName, index, key);
    vendorTrackModelPlacement(wallName, index, key);
  };

  const vendorClearModelPlacement = (modelKey) => {
    const key = String(modelKey || '').trim();
    if (!key) return false;
    const dz = vendorResolveDropZone();
    if (dz) {
      ['removeModelPlacement', 'clearModelPlacement', 'releaseModelPlacement', 'unrecordModelPlacement'].forEach((method) => {
        if (!hasFn(dz, method)) return;
        try {
          dz[method](key);
        } catch (_) {
        }
      });
    }
    const hadRecord = modelPlacementRegistry.has(key);
    modelPlacementRegistry.delete(key);
    vendorDebugOperation('placement:clear', { modelKey: key, hadRecord });
    return hadRecord;
  };

  const vendorRemovePriorOccupantAtSlot = async (wallName, index, exceptKey = '') => {
    const wall = wallName;
    const slot = index;
    const keep = String(exceptKey || '').trim();
    if (wall == null || slot == null) return;
    const toRemove = [];
    modelPlacementRegistry.forEach((rec, key) => {
      if (keep && key === keep) return;
      if (rec.wallName === wall && rec.index === slot) toRemove.push(key);
    });
    for (let i = 0; i < toRemove.length; i += 1) {
      await removeRegisteredModelByKey(toRemove[i]);
    }
  };

  const vendorApplyChangeModelEvent = async (event, dropzoneData = null, options = {}) => {
    const targetData = vendorGetTargetData(event);
    const id = targetData.id;
    const name = targetData.name;
    const file_url = vendorResolveFileUrl(targetData);
    const model_control_type = targetData.model_control_type || targetData.modelControlType;
    const category = targetData.category || targetData.modelId || targetData.model_id;
    const placement_zone = targetData.placement_zone || targetData.placementZone;
    if (!file_url || !hasFn(kernel?.model, 'add')) return false;
    const replaceScene = options.replaceScene !== false;
    if (placement_zone && !dropzoneData && replaceScene) {
      vendorMainPlacementZoneCache = placement_zone;
      vendorApplyDropZoneData(placement_zone);
    }
    const modelName = vendorCleanModelIdPart(name, category, id);

    if (dropzoneData) {
      const dz = vendorResolveDropZone();
      const wallName = dropzoneData.wallName ?? dropzoneData.wall_name;
      const index = dropzoneData.index;
      const transform = dropzoneData.transform;
      if (!transform || wallName == null || index == null) return false;
      const { position, rotation } = transform;
      const modelId = Date.now();
      const runtimeKey = vendorRuntimeModelKey(modelName, modelId);
      const meshIdsBeforeAdd = snapshotSceneMeshIds();
      await vendorRemovePriorOccupantAtSlot(wallName, index, runtimeKey);
      vendorRecordModelPlacement(wallName, index, runtimeKey);
      await awaitIfPromise(kernel.model.add({
        modelName,
        modelId,
        modelUrl: file_url,
        modelControlType: normalizeModelControlType(model_control_type),
        drag: buildAccessoryDragOptions(rotation),
        transform: {
          position,
          rotation,
        },
      }));
      vendorDebugOperation('accessory:add', {
        modelName,
        modelId,
        runtimeKey,
        wallName,
        index,
        sdkHasRuntimeKey: sdkRuntimeHasExactKey(runtimeKey),
      });
      loadedModelKeys.add(runtimeKey);
      modelSkuRegistry.set(runtimeKey, String(options.sku || vendorActiveSku || '').trim());
      await registerModelMeshes(runtimeKey, meshIdsBeforeAdd, model_control_type);
      document.dispatchEvent(new CustomEvent('3d:vendor:model-placed', {
        detail: { modelId: runtimeKey, sku: options.sku || vendorActiveSku },
        bubbles: true,
      }));
      return runtimeKey;
    }

    if (!dropzoneData && replaceScene && vendorAnyModelExists(targetData)) {
      const skuKey = String(vendorActiveSku || '').trim();
      if (skuKey && skuKey === vendorLoadedMainSku) {
        const keys = vendorGetModelKeysFromTarget(targetData);
        return keys[0] || category || true;
      }
      await vendorRemoveMainModels(targetData);
    }

    const modelId = !dropzoneData && !replaceScene
      ? String(Date.now())
      : (category || vendorCleanModelIdPart(id, name, Date.now()));
    const runtimeKey = !dropzoneData && !replaceScene
      ? vendorRuntimeModelKey(modelName, modelId)
      : vendorRuntimeModelKey(modelName, modelId);
    const meshIdsBeforeAdd = snapshotSceneMeshIds();
    const added = await callModelAddCompat(kernel.model, modelId, file_url, model_control_type, modelName);
    if (added) {
      loadedModelKeys.delete(modelId);
      loadedModelKeys.add(runtimeKey);
      await registerModelMeshes(runtimeKey, meshIdsBeforeAdd, model_control_type);
    }
    if (added && !dropzoneData && replaceScene && vendorActiveSku) {
      vendorLoadedMainSku = String(vendorActiveSku).trim();
    }
    return added ? runtimeKey : false;
  };

  const extractBizModelIds = (result) => {
    if (!result || typeof result !== 'object') return [];
    const bags = [
      result.modelIds,
      result.model_ids,
      result.data?.modelIds,
      result.data?.model_ids,
    ];
    for (let i = 0; i < bags.length; i += 1) {
      if (Array.isArray(bags[i]) && bags[i].length) {
        return bags[i].map((v) => String(v || '').trim()).filter(Boolean);
      }
    }
    const single = result.modelId || result.model_id || result.modelName || result.model_name
      || result.data?.modelId || result.data?.modelName;
    return single ? [String(single).trim()] : [];
  };

  const snapshotTrackedModelKeys = () => new Set(Array.from(loadedModelKeys));

  const diffNewModelKeys = (beforeSet) => {
    const added = [];
    loadedModelKeys.forEach((key) => {
      if (!beforeSet.has(key)) added.push(key);
    });
    if (added.length) return added;
    try {
      const dic = getLiveModelDic();
      const keys = dic?.Keys?.() || [];
      keys.forEach((key) => {
        const k = String(key || '').trim();
        if (k && !beforeSet.has(k) && !added.includes(k)) added.push(k);
      });
    } catch (_) {
    }
    return added;
  };

  const diffNewSceneModelKeys = (beforeMeshIds) => {
    if (!(beforeMeshIds instanceof Set)) return [];
    const discovered = [];
    getSceneMeshes().forEach((mesh) => {
      const meshId = getMeshUniqueId(mesh);
      if (!meshId || beforeMeshIds.has(meshId)) return;
      const key = String(
        mesh?.metadata?.modelKey
        || mesh?.metadata?.modelId
        || mesh?.metadata?.modelName
        || findSdkModelNameByMesh(mesh)
        || ''
      ).trim();
      if (key && !discovered.includes(key)) discovered.push(key);
    });
    return discovered;
  };

  const waitForBizModelDiscovery = async (beforeSet, beforeMeshIds, maxWait = 1200) => {
    const started = Date.now();
    while (Date.now() - started < maxWait) {
      if (diffNewModelKeys(beforeSet).length || diffNewSceneModelKeys(beforeMeshIds).length) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return false;
  };

  const registerModelGroup = (modelKeys, modelRole = '') => {
    const keys = filterNonMainModelKeys([...new Set(
      (Array.isArray(modelKeys) ? modelKeys : [modelKeys])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    )]);
    if (keys.length <= 1) return keys;
    const role = String(modelRole || '').trim().toLowerCase();
    if (!role || role === 'main') return keys;
    const group = new Set(keys);
    keys.forEach((key) => modelGroupRegistry.set(key, group));
    return keys;
  };

  const getModelGroupKeys = (modelKey, options = {}) => {
    const key = String(modelKey || '').trim();
    if (!key) return [];
    const group = modelGroupRegistry.get(key);
    const members = group ? [...group] : [key];
    if (options.sameRoleOnly === false) {
      return members.filter((member) => !isMainModelRoleKey(member));
    }
    const role = getModelRole(key);
    if (!role) return members.filter((member) => !isMainModelRoleKey(member));
    return members.filter((member) => (
      !isMainModelRoleKey(member) && getModelRole(member) === role
    ));
  };

  const unregisterModelGroupKey = (modelKey) => {
    const key = String(modelKey || '').trim();
    if (!key) return;
    const group = modelGroupRegistry.get(key);
    if (!group) return;
    group.delete(key);
    modelGroupRegistry.delete(key);
    group.forEach((member) => modelGroupRegistry.set(member, group));
  };

  const rememberBizPlacedModels = (
    skuKey,
    result,
    beforeSet,
    beforeMeshIds = null,
    modelRole = ''
  ) => {
    const modelIds = extractBizModelIds(result);
    const discovered = [...new Set(
      modelIds
        .concat(diffNewModelKeys(beforeSet))
        .concat(diffNewSceneModelKeys(beforeMeshIds))
    )];
    const removed = vendorComputeRemovedTrackedKeys(beforeSet);
    modelSkuRegistry.forEach((mappedSku, key) => {
      const k = String(key || '').trim();
      if (!k || isMainModelRoleKey(k) || discovered.includes(k) || removed.includes(k)) return;
      if (!kernelModelExistsByCandidates(k)) removed.push(k);
    });
    vendorLastRemovedModelIds = filterNonMainModelKeys(removed);
    const role = String(modelRole || '').trim().toLowerCase();
    // A furniture selection must be classified before applying the main-model
    // filter. A stale role/SKU entry can otherwise make a newly placed chair
    // look like the Pergola and drop it from all secondary-model registries.
    let effectiveDiscovered = role === 'furniture'
      ? discovered.filter((id) => String(id || '').trim() !== 'c3d-single-main')
      : filterNonMainModelKeys(discovered.slice());
    if (role === 'furniture') {
      effectiveDiscovered = [
        ...new Set(
          modelIds
            .concat(diffNewModelKeys(beforeSet))
            .concat(diffNewSceneModelKeys(beforeMeshIds))
            .map((id) => String(id || '').trim())
            .filter((id) => (
              id
              && id !== 'c3d-single-main'
              && !(
                beforeSet instanceof Set
                && beforeSet.has(id)
                && isMainModelRoleKey(id)
                && !isRotationModelKey(id)
              )
            ))
        ),
      ];
    }
    if (role === 'accessory' || role === 'furniture' || role === 'main') {
      effectiveDiscovered.forEach((id) => {
        modelRoleRegistry.set(id, role);
        modelSkuRegistry.set(id, skuKey);
        if (role === 'furniture') modelControlTypeRegistry.set(id, 'rotation');
      });
    }
    registerModelGroup(effectiveDiscovered, role);
    const registerJobs = effectiveDiscovered.map((id) => {
      loadedModelKeys.add(id);
      return ensureTrackedModelMeshesRegistered(
        id,
        role === 'furniture' ? 'rotation' : ''
      );
    });
    if (registerJobs.length) {
      void Promise.all(registerJobs).catch((err) => {
        console.warn('[3D] mesh registration after biz placement failed:', err);
      });
    }
    vendorLastPlacedModelIds = effectiveDiscovered.slice();
    return effectiveDiscovered;
  };

  const removeStaleSecondaryModelKeys = async (keys) => {
    const targets = [...new Set(
      (Array.isArray(keys) ? keys : [keys])
        .map((key) => String(key || '').trim())
        .filter((key) => key && !isMainModelRoleKey(key))
    )];
    for (let i = 0; i < targets.length; i += 1) {
      await removeRegisteredModelByKey(targets[i], { force: true });
    }
  };

  const collectMaterialNamesFromMat = (mat, into) => {
    if (!mat || !into) return;
    const name = String(mat.name || '').trim();
    if (name) into.add(name);
    const subs = mat.subMaterials;
    if (Array.isArray(subs)) {
      subs.forEach((sub) => collectMaterialNamesFromMat(sub, into));
    }
  };

  const applyHexToMaterialTree = (mat, hex) => {
    if (!mat) return false;
    let didApply = false;
    const colorValue = vendorHexToColor3Like(hex, mat.albedoColor || mat.diffuseColor || mat.baseColor);
    if (colorValue) {
      if ('albedoColor' in mat) {
        mat.albedoColor = colorValue;
        didApply = true;
      }
      if ('diffuseColor' in mat) {
        mat.diffuseColor = colorValue;
        didApply = true;
      }
      if ('baseColor' in mat) {
        mat.baseColor = colorValue;
        didApply = true;
      }
    }
    try {
      if (typeof mat.markAsDirty === 'function') mat.markAsDirty(1);
      if (typeof mat.markDirty === 'function') mat.markDirty();
    } catch (_) {
    }
    const subs = mat.subMaterials;
    if (Array.isArray(subs)) {
      subs.forEach((sub) => {
        if (applyHexToMaterialTree(sub, hex)) didApply = true;
      });
    }
    return didApply;
  };

  const collectTrackedModelKeys = () => {
    const keys = Array.from(loadedModelKeys).filter(Boolean);
    if (keys.length) return keys;
    const preferred = vendorResolveMainModelKey('');
    return preferred ? [preferred] : [];
  };

  const collectTrackedMeshes = (keys = collectTrackedModelKeys()) => {
    const seen = new Set();
    const meshes = [];
    const push = (mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      const id = getMeshUniqueId(mesh);
      const token = id || String(mesh.name || '');
      if (token && seen.has(token)) return;
      if (token) seen.add(token);
      else if (meshes.includes(mesh)) return;
      meshes.push(mesh);
    };
    const keySet = new Set(keys);
    keySet.forEach((k) => {
      getRegisteredMeshesForKey(k).forEach(push);
      collectModelDicMeshesForKey(k).forEach(push);
    });
    if (keySet.size) {
      getSceneMeshes().forEach((mesh) => {
        const meta = mesh?.metadata || {};
        const tagged = String(meta.modelKey || meta.modelId || meta.modelName || '').trim();
        if (tagged && keySet.has(tagged)) push(mesh);
      });
    }
    return meshes;
  };

  const bizApplyKeysColor = (hex, keys = [], options = {}) => {
    const keyList = (Array.isArray(keys) ? keys : [keys])
      .map((k) => String(k || '').trim())
      .filter(Boolean);
    if (!hex || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) || !keyList.length) {
      return { ok: false, reason: 'missing_hex_or_keys' };
    }
    const meshes = collectTrackedMeshes(keyList);
    const names = new Set();
    meshes.forEach((mesh) => collectMaterialNamesFromMat(mesh?.material, names));
    let didApply = false;
    meshes.forEach((mesh) => {
      if (applyHexToMaterialTree(mesh?.material, hex)) didApply = true;
    });
    try {
      names.forEach((target) => {
        if (hasFn(kernel?.material, 'apply')) {
          keyList.forEach((modelId) => {
            kernel.material.apply({ target, modelId, albedoColor: hex });
            didApply = true;
          });
        } else if (hasFn(kernel?.material, 'color')) {
          kernel.material.color(target, hex);
          didApply = true;
        }
      });
    } catch (err) {
      console.warn('[3D] scoped material.apply failed:', err);
    }
    if (!didApply) return { ok: false, reason: 'no_model_materials' };
    return {
      ok: true,
      mode: 'color_scoped',
      sku: options.sku || '',
      modelIds: keyList.slice(),
    };
  };

  const bizApplySceneWideColor = (hex, options = {}) => {
    const keys = collectTrackedModelKeys();
    const result = bizApplyKeysColor(hex, keys, options);
    if (!result.ok) return result;
    return { ...result, mode: 'color_scene_wide' };
  };

  const bizApplyLocalColor = (options = {}) => {
    const hex = String(options.albedoColor || options.hex || options.color || '').trim();
    const allMaterials = options.allMaterials === true || options.replaceScene === true;
    const target = String(
      options.target
      || options.materialName
      || (!allMaterials ? window.CUSTOMIZATION_3D_LAST_MATERIAL_NAME : '')
      || ''
    ).trim();
    const modelId = allMaterials ? '' : String(options.modelId || options.modelKey || '').trim();
    if (!hex || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
      return { ok: false, reason: 'missing_hex' };
    }
    if (allMaterials) {
      return bizApplySceneWideColor(hex, options);
    }
    if (!hasFn(kernel?.material, 'apply') && !hasFn(kernel?.material, 'color')) {
      return { ok: false, reason: 'no_material_api' };
    }
    try {
      if (target && hasFn(kernel?.material, 'apply')) {
        const payload = { target, albedoColor: hex };
        if (modelId) payload.modelId = modelId;
        kernel.material.apply(payload);
      } else if (target && hasFn(kernel?.material, 'color')) {
        kernel.material.color(target, hex);
      } else {
        return { ok: false, reason: 'missing_target' };
      }
      return { ok: true, mode: 'color_only', sku: options.sku || '', modelIds: modelId ? [modelId] : [] };
    } catch (err) {
      console.warn('[3D] local color apply failed:', err);
      return { ok: false, reason: 'error', error: err };
    }
  };

  const bizSelectSku = async (sku, options = {}) => {
    const skuKey = String(sku || '').trim();
    if (!skuKey) return { ok: false, reason: 'empty_sku' };
    const biz = getBusinessLayer();
    if (!biz || typeof biz.select !== 'function') {
      return { ok: false, reason: 'kernel_bl_missing' };
    }
    const myGen = ++bizSelectGeneration;
    vendorActiveSku = skuKey;
    const before = snapshotTrackedModelKeys();
    const previousMainSku = String(vendorLoadedMainSku || '').trim();
    vendorLastPlacedModelIds = [];
    vendorLastRemovedModelIds = [];
    try {
      if (!hasLoadedFirstModel) showLoading(0);
      await vendorWhenKernelReady();
      if (myGen !== bizSelectGeneration) {
        return { ok: false, reason: 'stale_select_superseded', superseded: true, modelIds: [] };
      }
      const beforeMeshIds = snapshotSceneMeshIds();
      const modelRole = String(
        options.modelRole
        || (options.replaceScene === true
          ? 'main'
          : (String(options.modelControlType || '').toLowerCase() === 'rotate' ? 'furniture' : ''))
      ).trim().toLowerCase();
      if (modelRole === 'furniture') {
        [...loadedModelKeys]
          .filter((key) => isMainModelRoleKey(key))
          .forEach((key) => modelRoleRegistry.set(key, 'main'));
        if (!options.hotspotPlacement) {
          await vendorClearHotspotContext({ force: true });
        }
        prunePhantomTrackedKeysByRole('furniture');
      }
      if (options.replaceScene === true && !options.colorOnly) {
        const skuChanged = !previousMainSku
          || previousMainSku.toLowerCase() !== skuKey.toLowerCase();
        if (skuChanged) {
          vendorHotspotCatalog.clear();
          vendorHotspotCatalogModelKey = '';
          await unloadTrackedMainModelsFromScene();
        }
      }
      if (myGen !== bizSelectGeneration) {
        return { ok: false, reason: 'stale_select_superseded', superseded: true, modelIds: [] };
      }
      const result = await awaitIfPromise(biz.select(skuKey));
      if (myGen !== bizSelectGeneration) {
        if (modelRole !== 'main') {
          const staleModelIds = modelRole === 'furniture'
            ? diffNewModelKeys(before).concat(diffNewSceneModelKeys(beforeMeshIds))
            : extractBizModelIds(result)
              .concat(diffNewModelKeys(before))
              .concat(diffNewSceneModelKeys(beforeMeshIds));
          await removeStaleSecondaryModelKeys(staleModelIds);
        }
        return { ok: false, reason: 'stale_select_superseded', superseded: true, modelIds: [] };
      }
      let discovered = rememberBizPlacedModels(
        skuKey,
        result,
        before,
        beforeMeshIds,
        modelRole
      );
      if (!discovered.length && options.expectPlacementZone !== true) {
        const discoveryWaitMs = options.hotspotPlacement
          ? 2400
          : (options.replaceScene === true ? 360 : 1200);
        await waitForBizModelDiscovery(
          before,
          beforeMeshIds,
          discoveryWaitMs
        );
        discovered = rememberBizPlacedModels(
          skuKey,
          result,
          before,
          beforeMeshIds,
          modelRole
        );
      }
      if (options.replaceScene) {
        vendorLoadedMainSku = skuKey;
        discovered.filter((id) => !isAccessoryRuntimeKey(id)).forEach((id) => {
          modelRoleRegistry.set(id, 'main');
        });
        if (!discovered.length && previousMainSku) {
          modelSkuRegistry.forEach((mappedSku, key) => {
            const k = String(key || '').trim();
            if (
              k
              && !isAccessoryRuntimeKey(k)
              && !isMainModelRoleKey(k)
              && String(mappedSku || '').trim().toLowerCase() === previousMainSku.toLowerCase()
              && kernelModelExistsByCandidates(k)
            ) {
              modelSkuRegistry.set(k, skuKey);
            }
          });
        }
        if (!isLegacyBusinessMode()) {
          await vendorClearHotspotContext({ force: true, all: true });
        }
      }
      if (modelRole === 'furniture' || modelRole === 'accessory') {
        ensureMainModelPreservedInScene();
      }
      if (modelRole === 'furniture' && discovered.length) {
        // Temporarily disabled: some furniture models are incorrectly lifted.
        // snapModelKeysToGround(discovered);
      }
      showModelReady({ immediate: hasLoadedFirstModel });
      const expectPlacement = options.expectPlacementZone === true;
      const mode = expectPlacement ? 'placement_zone' : 'events';
      const furniturePlacementSlot = modelRole === 'furniture'
        ? getFurniturePlacementSlotForSelect(options)
        : undefined;
      return {
        ok: true,
        mode,
        sku: skuKey,
        result,
        modelIds: discovered.slice(),
        removedModelIds: vendorLastRemovedModelIds.slice(),
        furniturePlacementSlot,
      };
    } catch (err) {
      console.error('[3D] kernel_bl.select failed:', err);
      return { ok: false, reason: 'error', error: err };
    }
  };

  const bizHandleDropZoneClick = async (dropzoneData, skuOverride = '') => {
    const sku = String(skuOverride || vendorActiveSku || '').trim();
    const biz = getBusinessLayer();
    if (!sku) return { ok: false, reason: 'no_active_sku' };
    const before = snapshotTrackedModelKeys();
    try {
      await vendorWhenKernelReady();
      const beforeMeshIds = snapshotSceneMeshIds();
      let result = null;
      if (typeof biz?.getEvent === 'function') {
        result = await awaitIfPromise(biz.getEvent(dropzoneData, sku));
      }
      let discovered = rememberBizPlacedModels(sku, result, before, beforeMeshIds, 'accessory');
      if (!discovered.length) {
        await waitForBizModelDiscovery(before, beforeMeshIds);
        discovered = rememberBizPlacedModels(sku, result, before, beforeMeshIds, 'accessory');
      }
      showModelReady({ immediate: true });
      if (discovered.length) {
        await Promise.all(discovered.map((id) => vendorApplySkuColorToModel(sku, id)));
      }
      return {
        ok: true,
        reason: discovered.length ? 'placed' : 'placed_no_model_id',
        sku,
        result,
        modelIds: discovered.slice(),
        removedModelIds: vendorLastRemovedModelIds.slice(),
      };
    } catch (err) {
      console.error('[3D] kernel_bl.getEvent failed:', err);
      return { ok: false, reason: 'error', error: err };
    }
  };

  const vendorFetchAndRenderHotspots = async (force = false) => {
    if (!isLegacyBusinessMode()) return true;
    await vendorWhenKernelReady();
    if (!hasFn(kernel?.hotspot, 'render')) return false;
    if (vendorHotspotsRendered && !force) return true;
    try {
      const response = await fetch(vendorGetApiUrl('/api/hotspots?status=active&page=1&pageSize=100'));
      const result = await response.json();
      const list = result?.data?.list;
      if (result?.code !== 200 || !Array.isArray(list) || !list.length) {
        if (!vendorHotspotsRendered) kernel.hotspot.render([]);
        vendorHotspotRenderSignature = '';
        vendorHotspotsRendered = true;
        return false;
      }
      const hotspots = list.map((item) => ({
        id: item.id,
        type: 'hotspot',
        name: item.name,
        meshName: item.name,
        icon: item.image_url,
        position: [item.position_x, item.position_y, item.position_z],
        radius: item.radius,
        color: '#000000',
        payload: {
          skus: item.skus || [],
        },
      }));
      vendorApplyHotspotCatalogFromList(list);
      vendorHotspotCatalogModelKey = '';
      const signature = hotspots.map((h) => String(h.id)).join('|');
      if (!force && signature === vendorHotspotRenderSignature) return true;
      vendorHotspotRenderSignature = signature;
      kernel.hotspot.render(hotspots);
      vendorHotspotsRendered = true;
      return true;
    } catch (err) {
      console.error('[3D] fetchAndRenderHotspots failed:', err);
      return false;
    }
  };

  const vendorExecuteDropzoneEventsStrict = async (dropzoneData, result, options = {}) => {
    const events = result?.data?.events;
    if (!Array.isArray(events) || !events.length) return false;
    const { wallName, index, transform } = dropzoneData || {};
    if (!transform || wallName == null || index == null) return false;
    const { position, rotation } = transform;

    let modelId = null;
    let modelName = null;
    let runtimeKey = '';
    vendorLastPlacedModelIds = [];

    for (const event of events) {
      if (vendorGetEventType(event) !== 'change_model') continue;
      const td = vendorGetTargetData(event);
      const name = String(td.name || '').trim();
      const fileUrl = vendorResolveFileUrl(td);
      const modelControlType = td.model_control_type || td.modelControlType;
      if (!name || !fileUrl) continue;

      modelId = Date.now();
      modelName = name;
      runtimeKey = `${modelName}_${modelId}`;
      const meshIdsBeforeAdd = snapshotSceneMeshIds();

      await vendorRemovePriorOccupantAtSlot(wallName, index, runtimeKey);
      vendorRecordModelPlacement(wallName, index, runtimeKey);

      await awaitIfPromise(kernel.model.add({
        modelName,
        modelId,
        modelUrl: fileUrl,
        modelControlType,
        drag: buildAccessoryDragOptions(rotation),
        transform: {
          position,
          rotation,
        },
      }));

      loadedModelKeys.add(runtimeKey);
      await registerModelMeshes(runtimeKey, meshIdsBeforeAdd, modelControlType);
      vendorLastPlacedModelIds.push(runtimeKey);
      document.dispatchEvent(new CustomEvent('3d:vendor:model-placed', {
        detail: { modelId: runtimeKey, sku: options.sku || vendorActiveSku },
        bubbles: true,
      }));
      vendorDebugOperation('strict:executeEvent:add', {
        modelName,
        modelId,
        runtimeKey,
        wallName,
        index,
      });
    }

    for (const event of events) {
      if (vendorGetEventType(event) !== 'change_color') continue;
      if (!modelName || !modelId) continue;
      const runtimeColorKey = `${modelName}_${modelId}`;
      const payload = vendorBuildMaterialApplyPayload(event, runtimeColorKey);
      if (!payload) continue;
      vendorClearMaterialTextureState(payload.target, !payload.albedoTexture, !payload.normalMap);
      kernel.material.apply(payload);
      vendorDebugOperation('strict:executeEvent:color', {
        target: payload.target,
        modelId: runtimeColorKey,
        color: payload.albedoColor,
        hasAlbedoTexture: !!payload.albedoTexture,
      });
    }

    return !!runtimeKey;
  };

  const vendorExecuteSceneEventsStrict = async (result) => {
    const events = result?.data?.events;
    if (!Array.isArray(events) || !events.length) return false;
    const hasModelChange = events.some((event) => vendorGetEventType(event) === 'change_model');
    let modelAlreadyExists = false;
    const skuKey = String(vendorActiveSku || '').trim();
    const skuChanged = skuKey
      && vendorLoadedMainSku
      && skuKey.toLowerCase() !== vendorLoadedMainSku.toLowerCase();

    if (hasModelChange) {
      const firstModelEvent = events.find((event) => vendorGetEventType(event) === 'change_model');
      const td = vendorGetTargetData(firstModelEvent);
      const name = String(td.name || '').trim();
      const category = td.category || td.modelId || td.model_id;
      const runtimeKey = vendorRuntimeModelKey(name, category);
      if (hasFn(kernel?.model, 'exists')) {
        try {
          modelAlreadyExists = !!kernel.model.exists(runtimeKey);
        } catch (_) {
          modelAlreadyExists = false;
        }
      } else {
        modelAlreadyExists = modelRuntimeHasKey(runtimeKey);
      }
      vendorDebugOperation('strict:executeEvent2:exists', { runtimeKey, modelAlreadyExists, skuChanged });
    }

    if (hasModelChange && (!modelAlreadyExists || skuChanged)) {
      const dz = vendorResolveDropZone();
      if (hasFn(dz, 'clearZones')) dz.clearZones();
      if (hasFn(kernel?.model, 'removeAll')) await awaitIfPromise(kernel.model.removeAll());
      loadedModelKeys.clear();
      modelMeshRegistry.clear();
      modelRoleRegistry.clear();
      modelControlTypeRegistry.clear();
      modelGroupRegistry.clear();
      vendorLoadedMainSku = '';
    } else if (modelAlreadyExists) {
      vendorHidePlacementZones();
    }

    vendorLastPlacedModelIds = [];
    let didWork = false;

    for (const event of events) {
      if (vendorGetEventType(event) !== 'change_model') continue;
      const td = vendorGetTargetData(event);
      const name = String(td.name || '').trim();
      const category = td.category || td.modelId || td.model_id;
      const fileUrl = vendorResolveFileUrl(td);
      const modelControlType = td.model_control_type || td.modelControlType;
      const runtimeKey = vendorRuntimeModelKey(name, category);
      if (modelAlreadyExists && !skuChanged) {
        didWork = true;
        continue;
      }
      if (td.placement_zone || td.placementZone) {
        vendorMainPlacementZoneCache = td.placement_zone || td.placementZone;
        vendorApplyDropZoneData(vendorMainPlacementZoneCache);
      }
      if (!name || !category || !fileUrl) continue;
      await awaitIfPromise(kernel.model.add({
        modelName: name,
        modelId: category,
        modelUrl: fileUrl,
        modelControlType,
      }));
      loadedModelKeys.add(runtimeKey);
      vendorLastPlacedModelIds.push(runtimeKey);
      didWork = true;
      vendorDebugOperation('strict:executeEvent2:add', { modelName: name, modelId: category, runtimeKey });
    }

    for (const event of events) {
      if (vendorGetEventType(event) !== 'change_color') continue;
      const payload = vendorBuildMaterialApplyPayload(event);
      if (!payload) continue;
      vendorClearMaterialTextureState(payload.target, !payload.albedoTexture, !payload.normalMap);
      kernel.material.apply(payload);
      didWork = true;
      vendorDebugOperation('strict:executeEvent2:color', {
        target: payload.target,
        color: payload.albedoColor,
        hasAlbedoTexture: !!payload.albedoTexture,
      });
    }

    if (didWork && hasModelChange) {
      vendorHidePlacementZones();
      const skuKey = String(vendorActiveSku || '').trim();
      if (skuKey) vendorLoadedMainSku = skuKey;
    }

    return didWork;
  };

  const vendorRunConfigEvents = async (result, dropzoneData = null, options = {}) => {
    const events = result?.data?.events;
    if (!Array.isArray(events) || !events.length) return false;
    vendorLastPlacedModelIds = [];

    const modelEvents = events.filter((e) => vendorGetEventType(e) === 'change_model');
    const colorEvents = events.filter((e) => vendorGetEventType(e) === 'change_color');

    let modelAlreadyExists = false;
    const replaceScene = options.replaceScene !== false;
    const skuKey = String(vendorActiveSku || '').trim();
    const skuChanged = skuKey
      && vendorLoadedMainSku
      && skuKey.toLowerCase() !== vendorLoadedMainSku.toLowerCase();
    if (modelEvents.length > 0 && !dropzoneData && replaceScene) {
      const td = vendorGetTargetData(modelEvents[0]);
      modelAlreadyExists =
        vendorAnyModelExists(td) && skuKey && skuKey === vendorLoadedMainSku;
      if (!modelAlreadyExists || skuChanged) {
        const dz = vendorResolveDropZone();
        if (hasFn(dz, 'clearZones')) dz.clearZones();
        if (hasFn(kernel?.model, 'removeAll')) await awaitIfPromise(kernel.model.removeAll());
        collectTrackedMainModelKeys().forEach((key) => finalizeModelRemoval(key));
        vendorLoadedMainSku = '';
      }
    }

    let didWork = false;
    let placedModelCount = 0;
    for (const event of modelEvents) {
      const td = vendorGetTargetData(event);
      if (!vendorResolveFileUrl(td)) continue;
      if (!dropzoneData && replaceScene && modelAlreadyExists && !skuChanged) {
        if (vendorAnyModelExists(td)) {
          didWork = true;
          continue;
        }
      }
      const modelResult = await vendorApplyChangeModelEvent(event, dropzoneData, {
        replaceScene,
        sku: options.sku,
      });
      if (typeof modelResult === 'string' && modelResult) {
        vendorLastPlacedModelIds.push(modelResult);
        placedModelCount += 1;
        if (dropzoneData && colorEvents.length > 0) {
          colorEvents.forEach((colorEvent) => {
            vendorApplyScopedChangeColorEvent(colorEvent, modelResult);
          });
        }
      }
      didWork = !!modelResult || didWork;
    }

    if (colorEvents.length > 0 && !dropzoneData && !replaceScene && vendorLastPlacedModelIds.length) {
      vendorLastPlacedModelIds.forEach((modelId) => {
        colorEvents.forEach((event) => {
          vendorApplyScopedChangeColorEvent(event, modelId);
        });
      });
      didWork = true;
    } else if (dropzoneData && colorEvents.length > 0 && !placedModelCount) {
      didWork = false;
    } else if (colorEvents.length > 0 && !dropzoneData) {
      const applyColors = () => {
        for (const event of colorEvents) {
          vendorApplyChangeColorEvent(event);
        }
      };
      applyColors();
      didWork = true;

      if (modelEvents.length > 0 && !dropzoneData && !modelAlreadyExists) {
        const myGen = ++vendorColorGeneration;
        const onLoaded = () => {
          if (vendorColorGeneration !== myGen) return;
          applyColors();
        };
        try {
          if (hasFn(kernel, 'once')) {
            kernel.once('model:loaded', onLoaded);
          } else if (hasFn(kernel, 'on') && hasFn(kernel, 'off')) {
            const h = () => { kernel.off('model:loaded', h); onLoaded(); };
            kernel.on('model:loaded', h);
          }
        } catch (_) {}
      }
    }

    if (!didWork) {
      console.warn('[3D] vendor events had no successful change_model', events);
    }
    return didWork;
  };

  const extractSkusFromHotspotItem = (item) => {
    const raw = item?.payload?.skus || item?.skus || item?.associated_skus || item?.sku_list || [];
    return Array.isArray(raw) ? raw : [];
  };

  const vendorApplyHotspotCatalogFromList = (list, options = {}) => {
    if (!Array.isArray(list) || !list.length) return false;
    if (!options.merge) vendorHotspotCatalog.clear();
    list.forEach((item, index) => {
      const id = String(item.item_id ?? item.id ?? '').trim();
      const name = String(
        item.item_id
        ?? item.name
        ?? item.hotspot_name
        ?? ''
      ).trim();
      const key = id || name;
      if (!key) return;
      const prev = vendorHotspotCatalog.get(key);
      if (prev?.fromClick && options.merge) return;
      const skus = extractSkusFromHotspotItem(item);
      vendorHotspotCatalog.set(key, {
        id: id || key,
        name,
        order: index,
        fromClick: false,
        skus: new Set(
          skus.map((sku) => normalizeVendorSkuKey(sku)).filter(Boolean)
        ),
        event: buildHotspotEventFromApiItem(item),
      });
    });
    if (list.length) {
      vendorDefaultHotspotId = String(
        list[0].item_id
        ?? list[0].id
        ?? list[0].name
        ?? ''
      ).trim();
    }
    return true;
  };

  const vendorRecordHotspotFromEvent = (event) => {
    if (!event || typeof event !== 'object') return;
    const id = String(event.id ?? '').trim();
    const name = String(event.name || event.meshName || '').trim();
    const key = id || name;
    if (!key) return;
    const skus = event?.payload?.skus || event?.skus || [];
    const prev = vendorHotspotCatalog.get(key);
    vendorHotspotCatalog.set(key, {
      id: id || key,
      name,
      order: prev?.order ?? vendorHotspotCatalog.size,
      fromClick: true,
      skus: new Set(
        (Array.isArray(skus) ? skus : [])
          .map((sku) => normalizeVendorSkuKey(sku))
          .filter(Boolean)
      ),
      event: { ...event },
    });
  };

  const buildHotspotEventFromApiItem = (item) => {
    const id = item?.item_id ?? item?.id;
    const name = String(item?.item_id ?? item?.name ?? item?.hotspot_name ?? '').trim();
    const skus = extractSkusFromHotspotItem(item);
    const numberOrUndefined = (value) => {
      const number = Number(value);
      return Number.isFinite(number) ? number : undefined;
    };
    const position = {
      x: numberOrUndefined(item?.position_x),
      y: numberOrUndefined(item?.position_y),
      z: numberOrUndefined(item?.position_z),
    };
    const modelPosition = {
      x: numberOrUndefined(item?.model_position_x),
      y: numberOrUndefined(item?.model_position_y),
      z: numberOrUndefined(item?.model_position_z),
    };
    return {
      id,
      name,
      meshName: name,
      type: 'hotspot',
      position,
      hotspotId: item?.hotspot_id,
      hotspotName: item?.hotspot_name,
      icon: item?.image_url || item?.image,
      radius: numberOrUndefined(item?.radius),
      modelPosition,
      payload: { skus },
    };
  };

  const vendorFetchHotspotCatalog = async () => {
    try {
      const response = await fetch(vendorGetApiUrl('/api/hotspots?status=active&page=1&pageSize=100'));
      const result = await response.json();
      const list = result?.data?.list;
      if (result?.code !== 200 || !Array.isArray(list) || !list.length) return false;
      vendorApplyHotspotCatalogFromList(list);
      vendorHotspotCatalogModelKey = '';
      return true;
    } catch (err) {
      console.warn('[3D] vendorFetchHotspotCatalog failed:', err);
      return false;
    }
  };

  const getHotspotPlacementSlotId = () => {
    if (!lastHotspotClickDetail) return '';
    const id = String(lastHotspotClickDetail.id ?? '').trim();
    if (id) return id;
    return String(
      lastHotspotClickDetail.name
      || lastHotspotClickDetail.meshName
      || ''
    ).trim();
  };

  const getFurniturePlacementSlotForSelect = (options = {}) => {
    if (options.hotspotPlacement && lastHotspotClickDetail) {
      const slot = getHotspotPlacementSlotId();
      return slot || C3D_DEFAULT_PLACEMENT_SLOT;
    }
    return C3D_DEFAULT_PLACEMENT_SLOT;
  };

  const storeHotspotContext = (event) => {
    if (!event || typeof event !== 'object') {
      lastHotspotClickDetail = null;
      return false;
    }
    lastHotspotClickDetail = event;
    vendorRecordHotspotFromEvent(event);
    const biz = getBusinessLayer();
    if (typeof biz?.hotspot_click === 'function') {
      try {
        biz.hotspot_click(event);
        hotspotBizContextApplied = true;
        return true;
      } catch (err) {
        console.warn('[3D] kernel_bl.hotspot_click on store failed:', err);
        return false;
      }
    }
    return false;
  };

  const prunePhantomTrackedKeysByRole = (role) => {
    filterNonMainModelKeys(listTrackedModelKeysByRole(role)).forEach((key) => {
      const shouldPrune = role === 'furniture'
        ? !isRemovableFurnitureTrackedKey(key)
        : !isLiveTrackedModelKey(key);
      if (shouldPrune) finalizeModelRemoval(key);
    });
  };

  const vendorClearHotspotContext = async (options = {}) => {
    if (vendorHotspotClearInFlight && !options.force) {
      return { ok: true, mode: 'deduped' };
    }
    vendorHotspotClearInFlight = true;
    lastHotspotClickDetail = null;
    hotspotBizContextApplied = false;
    try {
      const biz = getBusinessLayer();
      if (typeof biz?.clearHotspotContext !== 'function') {
        return { ok: false, mode: 'unavailable' };
      }
      await awaitIfPromise(
        options.all === true
          ? biz.clearHotspotContext()
          : biz.clearHotspotContext({ ...VENDOR_HOTSPOT_CLEAR_PAYLOAD })
      );
      return { ok: true, mode: 'clearHotspotContext' };
    } catch (err) {
      console.warn('[3D] kernel_bl.clearHotspotContext failed:', err);
      return { ok: false, mode: 'error', error: err };
    } finally {
      vendorHotspotClearInFlight = false;
    }
  };

  const resetHotspotBizMode = async (options = {}) => {
    if (!hotspotBizContextApplied && !lastHotspotClickDetail && !options.force) {
      return { ok: true, mode: 'noop' };
    }
    return vendorClearHotspotContext(options);
  };

  const cancelPendingOperations = () => {
    bizSelectGeneration += 1;
    vendorColorGeneration += 1;
    vendorActiveSku = '';
    return true;
  };

  const releaseHotspotBizContext = async () => {
    await vendorClearHotspotContext({ force: Boolean(lastHotspotClickDetail) });
    prunePhantomTrackedKeysByRole('furniture');
    return { ok: true };
  };

  const vendorApi = {
    initKernel: (nextKernel) => {
      kernel = nextKernel || kernel;
      vendorRecheckDropZone();
      if (typeof vendorKernelReadyResolve === 'function') {
        vendorKernelReadyResolve(kernel);
        vendorKernelReadyResolve = null;
      }
      return kernel;
    },
    hasKernel: () => !!kernel,
    hasDropZoneApi: () => !!vendorResolveDropZone(),
    recheckDropZone: vendorRecheckDropZone,
    onSceneReady: async () => {
      vendorRecheckDropZone();
      if (isLegacyBusinessMode() && window.CUSTOMIZATION_3D_DEFER_HOTSPOTS === false) {
        await vendorFetchAndRenderHotspots();
      }
      if (!isLegacyBusinessMode()) {
        await vendorClearHotspotContext({ force: true, all: true });
      }
    },
    whenKernelReady: vendorWhenKernelReady,
    requireKernel: vendorWhenKernelReady,
    getApiUrl: vendorGetApiUrl,
    setActiveSku: (sku) => {
      vendorActiveSku = String(sku || '').trim();
    },
    getActiveSku: () => vendorActiveSku,
    setPergolaSku: (sku) => {
      const next = String(sku || '').trim();
      if (next !== vendorLoadedMainSku) {
        vendorHotspotCatalog.clear();
        vendorHotspotCatalogModelKey = '';
      }
      vendorLoadedMainSku = next;
      if (next && isLegacyBusinessMode()) void vendorFetchHotspotCatalog();
    },
    getPergolaSku: () => vendorLoadedMainSku,
    setModelSku: (modelId, sku) => {
      const id = String(modelId || '').trim();
      const skuKey = String(sku || '').trim();
      if (!id || !skuKey) return;
      modelSkuRegistry.set(id, skuKey);
    },
    ensureTrackedModelMeshesRegistered: (modelId, modelControlType = '') =>
      ensureTrackedModelMeshesRegistered(modelId, modelControlType),
    fetchProductConfig: vendorFetchProductConfig,
    selectSku: bizSelectSku,
    showPlacementZonesBySku: async (sku, options = {}) => {
      if (!isLegacyBusinessMode()) return bizSelectSku(sku, options);
      const skuKey = String(sku || '').trim();
      if (!skuKey) return { ok: false, reason: 'empty_sku' };
      vendorActiveSku = skuKey;
      try {
        await vendorWhenKernelReady();
        const result = await vendorFetchProductConfig(skuKey);
        if (result?.code !== 200 || !result?.data) return { ok: false, reason: 'not_found', result };
        const { enable_placement_zone, wall_divisions } = result.data;
        if (enable_placement_zone && wall_divisions != null) {
          return vendorRunGetPlacementZone(skuKey, { preview: true });
        }
        const loaded = await vendorRunConfigEvents(result, null, {
          replaceScene: options.replaceScene !== false,
        });
        if (loaded) showModelReady();
        if (!loaded && vendorHasSceneModels()) {
          showModelReady();
          return { ok: true, mode: 'events', result, kept_scene: true };
        }
        return {
          ok: loaded,
          mode: loaded ? 'events' : 'no_events',
          result,
          modelIds: vendorLastPlacedModelIds.slice(),
        };
      } catch (err) {
        console.error('[3D] vendor SKU config failed:', err);
        return { ok: false, reason: 'error', error: err };
      }
    },
    applySkuToModel: async (sku, modelKey, options = {}) => {
      if (!isLegacyBusinessMode()) {
        if (options.colorOnly) {
          const key = String(modelKey || options.modelKey || '').trim();
          if (!key) return { ok: false, reason: 'empty_model_key' };
          return vendorApplySkuColorToModel(sku, key);
        }
        if (options.replaceScene === true) {
          return bizSelectSku(sku, {
            expectPlacementZone: false,
            replaceScene: true,
            modelId: modelKey,
            modelKey,
          });
        }
        const key = String(modelKey || options.modelKey || '').trim();
        if (!key) return { ok: false, reason: 'empty_model_key' };
        return vendorApplySkuColorToModel(sku, key);
      }
      const skuKey = String(sku || '').trim();
      const key = String(modelKey || '').trim();
      if (!skuKey) return { ok: false, reason: 'empty_sku' };
      try {
        await vendorWhenKernelReady();
        const result = await vendorFetchProductConfig(skuKey);
        if (result?.code !== 200 || !result?.data) return { ok: false, reason: 'not_found', result };
        const events = Array.isArray(result.data.events) ? result.data.events : [];
        const colorEvents = events.filter((event) => vendorGetEventType(event) === 'change_color');
        if (options.colorOnly) {
          if (!colorEvents.length) return { ok: false, reason: 'no_color_events', result };
          let didWork = false;
          if (options.replaceScene) {
            vendorColorGeneration += 1;
            colorEvents.forEach((event) => {
              didWork = vendorApplyChangeColorEvent(event) || didWork;
            });
          } else if (key) {
            await waitForModelRuntimeKey(key);
            colorEvents.forEach((event) => {
              didWork = vendorApplyScopedChangeColorEvent(event, key) || didWork;
            });
          }
          return { ok: didWork, mode: didWork ? 'color_only' : 'no_matching_material', result };
        }
        if (options.replaceScene) {
          vendorActiveSku = skuKey;
          const loaded = await vendorRunConfigEvents(result, null, { replaceScene: true });
          if (loaded) showModelReady();
          return { ok: !!loaded, mode: loaded ? 'events' : 'no_events', result };
        }
        if (!key) return { ok: false, reason: 'empty_model_key', result };
        let didWork = false;
        colorEvents.forEach((event) => {
          didWork = vendorApplyScopedChangeColorEvent(event, key) || didWork;
        });
        return { ok: didWork, mode: didWork ? 'scoped_color' : 'no_color_events', result };
      } catch (err) {
        console.error('[3D] vendor applySkuToModel failed:', err);
        return { ok: false, reason: 'error', error: err };
      }
    },
    applyMaterialColor: (options = {}) => {
      const allMaterials = options.allMaterials === true
        || options.sceneWide === true
        || options.replaceScene === true;
      const albedoColor = options.albedoColor ?? options.color ?? options.hex;
      const scopedKeys = Array.isArray(options.modelKeys)
        ? options.modelKeys
        : (options.modelId || options.modelKey ? [options.modelId || options.modelKey] : []);
      if (!allMaterials && scopedKeys.some(Boolean) && albedoColor && !options.target && !options.materialName) {
        return bizApplyKeysColor(albedoColor, scopedKeys, { sku: options.sku });
      }
      if (allMaterials) {
        return bizApplySceneWideColor(albedoColor, { sku: options.sku });
      }
      const target = String(options.target || options.materialName || '').trim();
      const modelId = String(options.modelId || '').trim();
      const albedoTexture = options.albedoTexture || options.color_map_url || null;
      const normalMap = options.normalMap || options.normal_map_url || null;
      if (!target || (!albedoColor && !albedoTexture && !normalMap)) {
        return { ok: false, reason: 'missing_target_or_color' };
      }
      if (!hasFn(kernel?.material, 'apply')) return { ok: false, reason: 'no_material_api' };
      try {
        const payload = { target, albedoColor };
        if (modelId) payload.modelId = modelId;
        if (albedoTexture) payload.albedoTexture = albedoTexture;
        if (normalMap) payload.normalMap = normalMap;
        if (options.metallic != null) payload.metallic = +options.metallic;
        if (options.roughness != null) payload.roughness = +options.roughness;
        vendorDebugOperation(modelId ? 'color:scoped-direct' : 'color:global-direct', payload);
        kernel.material.apply(payload);
        return { ok: true };
      } catch (err) {
        console.warn('[3D] applyMaterialColor failed:', err);
        return { ok: false, reason: 'error', error: err };
      }
    },
    handleDropZoneClick: async (dropzoneData, skuOverride = '') => {
      if (!isLegacyBusinessMode()) return bizHandleDropZoneClick(dropzoneData, skuOverride);
      const sku = String(skuOverride || vendorActiveSku || '').trim();
      if (!sku) return { ok: false, reason: 'no_active_sku' };
      try {
        const trackedBeforePlacement = Array.from(loadedModelKeys);
        vendorActiveSku = sku;
        vendorColorGeneration += 1;
        await vendorWhenKernelReady();
        const result = await vendorFetchProductConfig(sku);
        if (result?.code !== 200 || !result?.data) return { ok: false, reason: 'not_found' };
        const placed = await vendorRunConfigEvents(result, dropzoneData, {
          sku,
          replaceScene: false,
        });
        if (placed) showModelReady();
        const modelIds = vendorLastPlacedModelIds.slice();
        const removedModelIds = vendorComputeRemovedTrackedKeys(trackedBeforePlacement)
          .filter((k) => !modelIds.includes(k));
        return {
          ok: !!placed,
          reason: placed ? (modelIds.length > 0 ? 'placed' : 'placed_no_model_id') : 'no_events',
          sku,
          modelIds,
          removedModelIds,
        };
      } catch (err) {
        console.error('[3D] vendor dropzone click failed:', err);
        return { ok: false, reason: 'error', error: err };
      }
    },
    autoLoadBaseModels: async () => {
      if (!isLegacyBusinessMode()) return true;
      await vendorWhenKernelReady();
      const response = await fetch(vendorGetApiUrl('/api/models/auto-load/list'));
      const data = await response.json();
      const models = data?.data;
      if (!Array.isArray(models) || !hasFn(kernel?.model, 'add')) return;
      await runWithConcurrency(models, async (model) => {
        if (model.placement_zone) {
          vendorMainPlacementZoneCache = model.placement_zone;
          vendorApplyDropZoneData(model.placement_zone);
        }
        const modelId = model.category || String(model.name || Date.now());
        const modelName = String(model.name || '').trim();
        const runtimeKey = vendorRuntimeModelKey(modelName, modelId);
        const added = await callModelAddCompat(
          kernel.model,
          modelId,
          model.file_url,
          model.model_control_type,
          modelName
        );
        if (added) {
          loadedModelKeys.delete(modelId);
          loadedModelKeys.add(runtimeKey);
        }
      }, 3);
    },
    ensureAutoLoadBaseModels: async () => {
      if (autoBaseModelsLoadPromise) return autoBaseModelsLoadPromise;
      autoBaseModelsLoadPromise = vendorApi.autoLoadBaseModels().catch((err) => {
        autoBaseModelsLoadPromise = null;
        throw err;
      });
      return autoBaseModelsLoadPromise;
    },
    hidePlacementZones: vendorHidePlacementZones,
    getPlacementZone: vendorRunGetPlacementZone,
    runGetPlacementZone: vendorRunGetPlacementZone,
    syncPlacementDivisionsForSku: (sku) => {
      if (!isLegacyBusinessMode()) return true;
      return vendorRunGetPlacementZone(sku, { preview: false, showWalls: false });
    },
    getLastCommittedAccessorySku: () => vendorLastCommittedAccessorySku,
    commitPlacementZoneDivisions: vendorCommitPlacementZoneDivisions,
    cancelPlacementZonePreview: vendorCancelPlacementZonePreview,
    isDropzoneModelReady: () => vendorDropzoneModelReady,
    fetchAndRenderHotspots: vendorFetchAndRenderHotspots,
    hasSceneModels: vendorHasSceneModels,
    getModelRole: (modelId) => getModelRole(modelId),
    listTrackedModelKeysByRole: (role) => listTrackedModelKeysByRole(role),
    removeTrackedModelsByRole: (role) => removeTrackedModelsByRole(role),
    removeNonPrimaryModels: (options) => removeNonPrimaryModels(options),
    cancelPendingOperations,
    getSkuByModelId: (modelId) => {
      const id = String(modelId || '').trim();
      const registrySku = String(modelSkuRegistry.get(id) || '').trim();
      if (registrySku) return registrySku;
      const biz = getBusinessLayer();
      if (id && typeof biz?.getSkuByModelId === 'function') {
        try {
          const sku = biz.getSkuByModelId(id);
          if (sku) return String(sku).trim();
        } catch (_) {
        }
      }
      return '';
    },
    storeHotspotContext,
    resetHotspotBizMode,
    releaseHotspotBizContext,
    hotspotClick: (event) => storeHotspotContext(event),
    clearHotspotContext: () => vendorClearHotspotContext(),
    hasHotspotPlacementContext: () => Boolean(lastHotspotClickDetail),
    getHotspotPlacementSlotId,
    hasHotspotCatalog: () => vendorHotspotCatalog.size > 0,
    hasPergolaHotspotAnchors: () => vendorHotspotCatalog.size > 0,
    ensureHotspotCatalogReady: async () => {
      if (!isLegacyBusinessMode()) return true;
      if (vendorHotspotCatalog.size) return true;
      return vendorFetchHotspotCatalog();
    },
    refreshHotspotCatalogForCurrentModel: async () => {
      if (!isLegacyBusinessMode()) return true;
      return vendorFetchHotspotCatalog();
    },
    hasHotspotBizContextApplied: () => hotspotBizContextApplied,
    clearSkuMapping: (modelId) => {
      const biz = getBusinessLayer();
      const id = String(modelId || '').trim();
      if (!id || typeof biz?.clearSkuMapping !== 'function') return;
      try { biz.clearSkuMapping(id); } catch (err) {
        console.warn('[3D] kernel_bl.clearSkuMapping failed:', id, err);
      }
    },
    clearAllSkuMappings: () => {
      const biz = getBusinessLayer();
      if (typeof biz?.clearAllSkuMappings !== 'function') return;
      try { biz.clearAllSkuMappings(); } catch (err) {
        console.warn('[3D] kernel_bl.clearAllSkuMappings failed:', err);
      }
    },
  };
  window.Customization3DVendor = vendorApi;

  const callModelReplaceCompat = async (modelHost, modelId, modelUrl, modelControlType = '', modelName = '') => {
    if (!hasFn(modelHost, 'replace')) return false;
    const payload = makeModelPayload(modelId, modelUrl, modelControlType, modelName);
    const arity = Number(modelHost.replace.length || 0);
    const tryObjectFirst = arity <= 1;
    const calls = tryObjectFirst
      ? [
          () => modelHost.replace(payload),
          () => modelHost.replace(modelId, modelUrl),
        ]
      : [
          () => modelHost.replace(modelId, modelUrl),
          () => modelHost.replace(payload),
        ];
    for (let i = 0; i < calls.length; i += 1) {
      try {
        const ret = await awaitIfPromise(calls[i]());
        if (ret === false) continue;
        return true;
      } catch (_) {
      }
    }
    return false;
  };

  const useAddRemoveModelAPI = () => {
    const cap = getSDKCapability();
    return cap['model.add'] && (
      cap['model.removeByKey']
      || cap['model.removeByName']
      || cap['model.remove']
    );
  };

  const useReplaceModelAPI = () => {
    const cap = getSDKCapability();
    return cap['model.replace'];
  };

  const shouldUseModelAddRemoveForSwap = () => {
    if (window.CUSTOMIZATION_3D_FORCE_MODEL_REPLACE === true) return false;
    if (useReplaceModelAPI()) return false;
    return useAddRemoveModelAPI();
  };

  const useRuntimeModelAPI = () => {
    const cap = getSDKCapability();
    return cap['model.load'] && cap['model.replace'];
  };

  const useEmptySceneFirstInit = () => useAddRemoveModelAPI() || useReplaceModelAPI() || useRuntimeModelAPI();

  const resetHotspotSubBindings = () => {
    hotspotSubEventsBound = false;
  };

  const tryBindHotspotSubEvents = () => {
    if (!hasFn(kernel?.hotspot, 'on') || hotspotSubEventsBound) return;
    hotspotSubEventsBound = true;
    try {
      kernel.hotspot.on('hover', (data) => {
        if (window.CUSTOMIZATION_3D_LOG_HOTSPOT_HOVER === true) {
          document.dispatchEvent(new CustomEvent('3d:hotspot:hover', { detail: data, bubbles: true }));
        }
      });
    } catch (err) {
      hotspotSubEventsBound = false;
      console.warn('[3D] kernel.hotspot.on bind failed:', err);
    }
  };

  const logFaceSDKReportAfterInit = () => {
    if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS !== true) return;
    if (sdkCapabilityLogged) return;
    sdkCapabilityLogged = true;
    const cap = getSDKCapability();
    console.groupCollapsed('[3D] faceSDK — vendor doc vs runtime (measured after kernel.init)');
    console.info(
      '[3D] Vendor documented event APIs — table below is the contract (payload shapes from vendor doc), not live event samples.'
    );
    console.table([
      {
        event: 'model:load',
        register_with: "kernel.on('model:load', fn)",
        payload: '(only fires when a model file starts loading)',
      },
      {
        event: 'model:load:progress',
        register_with: "kernel.on('model:load:progress', fn)",
        payload: '{ progress: 0-1, success: boolean, total: number }',
      },
      {
        event: 'model:loaded',
        register_with: "kernel.on('model:loaded', fn)",
        payload: '(e.g. hide progress bar)',
      },
      {
        event: 'model:click',
        register_with: "kernel.on('model:click', fn)",
        payload: '{ meshName: string }',
      },
      {
        event: 'hotspot:rendered',
        register_with: "kernel.on('hotspot:rendered', fn) and/or kernel.hotspot.on('rendered', fn)",
        payload: '(after kernel.hotspot.render(items) in all:ready or theme equivalent)',
      },
      {
        event: 'hotspot:click',
        register_with: "kernel.on('hotspot:click', fn)",
        payload: '{ id, name }',
      },
    ]);
    console.info(
      '[3D] Imperative methods on kernel (same names as in code; many attach only after init — earlier logs were misleading):'
    );
    console.table(cap);
    const modelHost = getModelAPIHost();
    const swapStrategy = shouldUseModelAddRemoveForSwap()
      ? 'kernel.model.remove + kernel.model.add({ modelId, modelUrl, modelControlType })'
      : useReplaceModelAPI()
        ? 'kernel.model.replace({ modelId, modelUrl, modelControlType })'
        : useAddRemoveModelAPI()
          ? 'kernel.model.remove + kernel.model.add（仅 add/remove 无 replace）'
          : useRuntimeModelAPI()
            ? 'kernel.model.load / kernel.model.replace'
            : 'kernel.init(modelUrlList) 兜底';
    console.log('[3D] active model swap strategy:', swapStrategy);
    console.log('[3D] detected model API host:', modelHost.path);
    console.groupEnd();
  };

  const patchDomTo3DLayoutToCssPixels = () => {};

  const isModelBubbleInteractionGuarded = () => {
    try {
      if (window.__C3D_PRESERVE_MODEL_BUBBLE_ON_HIDE_ALL === true) return true;
      return Date.now() < Number(window.__C3D_MODEL_BUBBLE_GRACE_UNTIL || 0);
    } catch (_) {
      return false;
    }
  };

  const patchDomTo3DHideAllBridge = () => {
    const dom3d = kernel?.mainApp?.appDomTo3D;
    if (!dom3d || dom3d.__c3dHideAllBridge) return;
    const original = dom3d.hideAll.bind(dom3d);
    dom3d.__c3dHideAllBridge = true;
    dom3d.hideAll = function patchedHideAll() {
      detachModelActionBubbleDom();
      const ret = original();
      try {
        document.dispatchEvent(new CustomEvent('3d:dom-to-3d-hide-all', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('3d:canvas:blank-click', { bubbles: true }));
      } catch (_) {
      }
      return ret;
    };
  };

  const detachModelActionBubbleDom = () => {
    try {
      if (hasFn(kernel?.domTo3D, 'detach')) {
        kernel.domTo3D.detach('customization-3d-model-bubble');
      }
    } catch (_) {
    }
    const bubble = document.getElementById('customization-3d-model-bubble');
    if (bubble) {
      bubble.style.display = 'none';
      bubble.style.visibility = 'hidden';
      bubble.style.left = '';
      bubble.style.top = '';
    }
  };

  const patchAppRayPickCoercion = () => {
    if (window.CUSTOMIZATION_3D_SCENE_PICK_ON_RAY_MISS === false) return;
    try {
      const ray = kernel?.mainApp?.appRay;
      if (!ray || typeof ray.handleSingleClick !== 'function' || ray.__c3dRayPickCoercion) return;
      ray.__c3dRayPickCoercion = true;
      const orig = ray.handleSingleClick.bind(ray);
      ray.handleSingleClick = function c3dHandleSingleClick(evt, pickInfo) {
        let pi = pickInfo;
        try {
          const hasHit = !!(pi?.hit && pi?.pickedMesh && pi?.pickedPoint);
          if (!hasHit) {
            invokeSdkCanvasDeselect({ force: true });
            return orig(evt, { hit: false });
          }
        } catch (_) {
        }
        if (pi?.pickedMesh && isSceneEnvironmentMesh(pi.pickedMesh)) {
          invokeSdkCanvasDeselect({ force: true });
          return orig(evt, { hit: false });
        }
        return orig(evt, pi);
      };
    } catch (_) {
    }
  };

  const invokeSdkCanvasDeselect = () => {
    detachModelActionBubbleDom();
    void vendorClearHotspotContext();
    try {
      document.dispatchEvent(new CustomEvent('3d:dom-to-3d-hide-all', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('3d:canvas:blank-click', { bubbles: true }));
    } catch (_) {
    }
    return true;
  };

  const wireVendorModule = () => {
    if (!kernel) return;
    try {
      vendorApi.initKernel(kernel);
      window.Customization3DVendor = vendorApi;
      if (isLegacyBusinessMode() && window.CUSTOMIZATION_3D_DEFER_AUTO_BASE_MODELS === false) {
        vendorApi.ensureAutoLoadBaseModels().catch((err) => {
          console.warn('[3D] vendor autoLoadBaseModels failed:', err);
        });
      }
    } catch (err) {
      console.warn('[3D] vendor initKernel failed:', err);
    }
  };

  const setupModelPointerDownPlacementSync = () => {
    if (!isLegacyBusinessMode()) return;
    if (document.__c3dModelPointerDownPlacementSync) return;
    document.__c3dModelPointerDownPlacementSync = true;
    document.addEventListener(
      'pointerdown',
      (event) => {
        const canvas = ensureCanvas();
        if (!canvas || !canvas.contains(event.target)) return;
        const mesh = pickMeshAtClientPoint(event.clientX, event.clientY);
        if (!mesh || isSceneEnvironmentMesh(mesh)) return;
        const attachKey =
          resolveAttachKeyFromPick({ pickedMesh: mesh })
          || resolveAttachKeyFromMesh(mesh)
          || '';
        if (!isAccessoryRuntimeKey(attachKey)) return;
        vendorSyncPlacementDivisionsForModelKey(attachKey);
      },
      { capture: true, passive: true }
    );
  };

  const afterKernelInitHooks = () => {
    trySilenceSdkDebug();
    tryBindHotspotSubEvents();
    bindBoundsWarningEvents();
    patchDomTo3DLayoutToCssPixels();
    patchDomTo3DHideAllBridge();
    patchAppRayPickCoercion();
    wireVendorModule();
    setupModelPointerDownPlacementSync();
    logFaceSDKReportAfterInit();
    scheduleApplyInitCamera('kernel:init');
  };

  const setBoundsWarningVisible = (visible) => {
    const el = document.getElementById('bounds-warning')
      || document.querySelector('[data-bounds-warning]');
    if (!el) return;
    el.hidden = !visible;
    if (!visible) el.classList.remove('is-tooltip-open');
  };

  const bindBoundsWarningEvents = () => {
    if (boundsWarningEventsBound) return;
    const biz = getBusinessLayer();
    const eventHost = (biz && typeof biz.on === 'function') ? biz : kernel;
    if (!eventHost || typeof eventHost.on !== 'function') return;
    boundsWarningEventsBound = true;
    try {
      eventHost.on('model:out-bounds', () => setBoundsWarningVisible(true));
      eventHost.on('model:in-bounds', () => setBoundsWarningVisible(false));
    } catch (err) {
      boundsWarningEventsBound = false;
      console.warn('[3D] bounds warning event bind failed:', err);
    }
  };

  const bindSDKEvents = () => {
    if (!hasFn(kernel, 'on') || sdkEventsBound) return;
    sdkEventsBound = true;

    kernel.on('model:load', (data = {}) => {
      const raw = Number(data.percentage ?? data.progress ?? data.percent);
      const pct = Number.isFinite(raw)
        ? (raw <= 1 && raw >= 0 ? Math.round(raw * 100) : Math.round(raw))
        : 0;
      modelLoadInFlight = true;
      showLoading(pct);
    });
    kernel.on('model:load:progress', (payload = {}) => {
      if (!modelLoadInFlight) return;
      const raw = Number(payload.progress ?? payload.percentage ?? payload.percent);
      const pct = Number.isFinite(raw)
        ? (raw <= 1 && raw >= 0 ? Math.round(raw * 100) : Math.round(raw))
        : 0;
      showLoading(pct);
      if (payload.success === false) {
        console.warn('[3D] model:load:progress reported failure', payload);
        modelLoadInFlight = false;
        if (vendorDropzonePlacementPending) {
          vendorReleaseDropzonePlacementLock();
        }
      }
    });
    kernel.on('model:loaded',        ()                       => {
      if (vendorDropzonePlacementPending) {
        vendorReleaseDropzonePlacementLock();
      }
      detachModelActionBubbleDom();
      scheduleApplyInitCamera('model:loaded');
      showModelReady();
    });
    kernel.on('model:replaced',      ()                       => {
      detachModelActionBubbleDom();
      scheduleApplyInitCamera('model:replaced');
      showModelReady();
    });
    kernel.on('all:ready',           async (data)             => {
      scheduleApplyInitCamera('all:ready');
      try {
        await vendorApi.onSceneReady();
      } catch (err) {
        console.warn('[3D] vendor onSceneReady failed:', err);
      }
      document.dispatchEvent(new CustomEvent('3d:scene:ready', { detail: data, bubbles: true }));
    });

    kernel.on('model:click', (data) => {
      if (data == null) {
        invokeSdkCanvasDeselect();
        document.dispatchEvent(new CustomEvent('3d:model:click', { detail: data, bubbles: true }));
        return;
      }
      const pickedMesh = data?.pickedMesh || null;
      const meshName = String(data?.meshName || pickedMesh?.name || '').trim();
      if (isSceneEnvironmentMesh(pickedMesh) || isSceneEnvironmentMeshName(meshName)) {
        invokeSdkCanvasDeselect();
        document.dispatchEvent(new CustomEvent('3d:model:click', { detail: data, bubbles: true }));
        return;
      }
      tryCacheSceneFromMesh(pickedMesh);
      c3dLastOutlineAttachKey =
        resolveAttachKeyFromPick({ pickedMesh, ...data })
        || resolveAttachKeyFromMesh(pickedMesh)
        || '';
      if (window.CUSTOMIZATION_3D_LOG_VENDOR_EVENTS === true) {
        console.info('模型点击', {
          meshName: data && data.meshName,
          raw: data,
        });
      }
      document.dispatchEvent(new CustomEvent('3d:model:click', { detail: data, bubbles: true }));
    });
    kernel.on('hotspot:click', (data) => {
      document.dispatchEvent(new CustomEvent('3d:hotspot:click', { detail: data, bubbles: true }));
    });
    if (hasFn(kernel, 'on')) {
      kernel.on('camera:changed', (state) =>
        document.dispatchEvent(new CustomEvent('3d:camera:changed', { detail: state, bubbles: true }))
      );
    }

    kernel.on('env:error', (err) => console.warn('[3D] Environment map error:', err));

    kernel.on('dropzone:click', async (dropzoneData) => {
      const skuAtClick = String(vendorActiveSku || '').trim();
      if (!skuAtClick) {
        vendorHidePlacementZones();
        return;
      }
      const placementGeneration = bizSelectGeneration;
      if (!vendorDropzoneModelReady) {
        showDropzonePlacementBusyTip();
        return;
      }
      const placementKey = dropzonePlacementKey(skuAtClick, dropzoneData);
      if (isDropzonePlacementDuplicate(placementKey)) return;

      vendorDropzoneInFlight.add(placementKey);
      vendorAcquireDropzonePlacementLock();
      let placementResult = null;
      document.dispatchEvent(new CustomEvent('3d:dropzone:placing', {
        detail: { dropzoneData, sku: skuAtClick },
        bubbles: true,
      }));
      try {
        if (!isLegacyBusinessMode() && window.CUSTOMIZATION_3D_BIZ_HANDLES_DROPZONE === true) {
          const discovered = diffNewModelKeys(snapshotTrackedModelKeys());
          placementResult = {
            ok: true,
            sku: skuAtClick,
            modelIds: discovered,
            removedModelIds: [],
          };
        } else {
          placementResult = await vendorApi.handleDropZoneClick(dropzoneData, skuAtClick);
        }
      } catch (err) {
        console.warn('[3D] dropzone:click handler failed:', err);
      } finally {
        vendorDropzoneInFlight.delete(placementKey);
        vendorReleaseDropzonePlacementLock();
      }
      if (placementGeneration !== bizSelectGeneration) {
        await removeStaleSecondaryModelKeys(placementResult?.modelIds || []);
        placementResult = {
          ...(placementResult || {}),
          ok: false,
          cancelled: true,
          modelIds: [],
        };
      }
      if (placementResult?.ok) {
        markDropzonePlacementRecent(placementKey);
        vendorHidePlacementZones();
        showModelReady();
      }
      document.dispatchEvent(new CustomEvent('3d:dropzone:placed', {
        detail: { dropzoneData, placementResult },
        bubbles: true,
      }));
    });

    document.addEventListener('3d:vendor:model-placed', async (e) => {
      const modelId = String(e?.detail?.modelId || '').trim();
      if (!modelId) return;
      try {
        loadedModelKeys.add(modelId);
      } catch (err) {
        console.warn('[3D] register placed accessory failed:', err);
      }
    });
  };

  const initSDK = async () => {
    setupContainerResizeObserver();
    if (sdkInitialized) return true;
    if (initPromise)    return initPromise;

    initPromise = (async () => {
      try {
        const [sdkReady, containerReady] = await Promise.all([
          waitForSDK(),
          waitForContainer(),
        ]);
        if (!sdkReady || !containerReady) {
          throw new Error('[3D] SDK or container not ready');
        }

        const canvas = ensureCanvas();
        if (!canvas) throw new Error('[3D] Canvas element not found');

        if (canvas.getBoundingClientRect().width === 0) {
          canvas.style.minWidth  = '100px';
          canvas.style.minHeight = '100px';
        }
        resizeCanvas(canvas);

        await new Promise(r => requestAnimationFrame(r));
        await new Promise(r => requestAnimationFrame(r));

        kernel = window.faceSDK.kernel;
        bindSDKEvents();

        resetHotspotSubBindings();
        if (!isLegacyBusinessMode()) {
          const biz = getBusinessLayer();
          if (!biz || typeof biz.init !== 'function') {
            throw new Error('[3D] kernel_bl.init is not available');
          }
          await awaitIfPromise(biz.init(buildBizInitConfig(canvas)));
        } else {
          await awaitIfPromise(kernel.init(buildInitConfig(canvas, [])));
        }
        afterKernelInitHooks();
        sdkInitialized = true;

        return true;
      } catch (err) {
        console.error('[3D] Initialization failed:', err);
        return false;
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  };

  const countTrackedModelsForNumericProductPrefix = (firstSegDigits) => {
    const pid = String(firstSegDigits || '').trim();
    if (!pid || !/^\d+$/.test(pid)) return 999;
    let n = 0;
    loadedModelKeys.forEach((sk) => {
      const s = String(sk || '');
      if (s === `product-${pid}` || s.startsWith(`${pid}::`)) n += 1;
    });
    return n;
  };

  const collectSdkModelUnloadCandidates = (trackedKey) => {
    const raw = String(trackedKey ?? '').trim();
    const out = [];
    const push = (s) => {
      const v = String(s || '').trim();
      if (!v || out.includes(v)) return;
      out.push(v);
    };
    push(raw);
    push(raw.replace(/^undefined[_-]+/i, ''));
    push(raw.replace(/^null[_-]+/i, ''));
    if (/_\d{10,}$/.test(raw)) return out;
    const firstSeg = String(raw.split('::')[0] ?? '').trim();
    if (/^\d+$/.test(firstSeg) && countTrackedModelsForNumericProductPrefix(firstSeg) <= 1) {
      push(`product-${firstSeg}`);
    }
    if (/^\d+$/.test(raw)) push(`product-${raw}`);
    const tsTail = raw.match(/_(\d{10,})$/)?.[1];
    if (tsTail) push(tsTail);
    return out;
  };

  const kernelModelExistsByCandidates = (trackedKey) => {
    const modelHost = getModelAPIHost().value;
    const candidates = collectSdkModelUnloadCandidates(trackedKey);
    if (hasFn(modelHost, 'exists')) {
      for (let i = 0; i < candidates.length; i += 1) {
        try {
          if (modelHost.exists(candidates[i])) return true;
        } catch (_) {
        }
      }
    }
    const dic = getLiveModelDic();
    if (dic && typeof dic.Has === 'function') {
      for (let i = 0; i < candidates.length; i += 1) {
        try {
          if (dic.Has(candidates[i])) return true;
        } catch (_) {
        }
      }
    }
    return false;
  };

  const modelKeyHasVisibleMeshes = (trackedKey) => {
    const k = String(trackedKey || '').trim();
    if (!k) return false;
    const registration = modelMeshRegistry.get(k);
    if (registration?.ids?.size) {
      const meshes = getRegisteredMeshesForKey(k);
      return meshes.some((mesh) => {
        if (!mesh || mesh.isDisposed?.()) return false;
        if (mesh.isVisible === false) return false;
        if (typeof mesh.visibility === 'number' && mesh.visibility <= 0) return false;
        return true;
      });
    }

    if (registration?.names?.size) {
      const sceneMeshes = getSceneMeshes();
      const exactSceneMeshes = sceneMeshes.filter((mesh) => {
        const metadata = mesh?.metadata;
        return metadata?.modelKey === k
          || metadata?.modelId === k
          || metadata?.modelName === k;
      });
      if (exactSceneMeshes.length) {
        return exactSceneMeshes.some((mesh) => {
          if (!mesh || mesh.isDisposed?.()) return false;
          if (mesh.isVisible === false) return false;
          if (typeof mesh.visibility === 'number' && mesh.visibility <= 0) return false;
          return true;
        });
      }

      const sceneIds = new Set(sceneMeshes.map(getMeshUniqueId).filter(Boolean));
      const modelDicMeshes = collectModelDicMeshesForKey(k);
      const inSceneModelDicMeshes = modelDicMeshes.filter((mesh) => {
        const id = getMeshUniqueId(mesh);
        return id ? sceneIds.has(id) : sceneMeshes.includes(mesh);
      });
      if (inSceneModelDicMeshes.length) {
        return inSceneModelDicMeshes.some((mesh) => {
          if (!mesh || mesh.isDisposed?.()) return false;
          if (mesh.isVisible === false) return false;
          if (typeof mesh.visibility === 'number' && mesh.visibility <= 0) return false;
          return true;
        });
      }

      return false;
    }

    return kernelModelExistsByCandidates(k);
  };

  const vendorResolveMainModelKey = (preferredKey = '') => {
    const preferred = String(preferredKey || '').trim();
    if (preferred && loadedModelKeys.has(preferred) && !/_\d{10,}$/.test(preferred)) return preferred;
    const nonAccessory = Array.from(loadedModelKeys).filter((k) => k && !/_\d{10,}$/.test(k));
    if (nonAccessory.length === 1) return nonAccessory[0];
    const current = String(currentModelAttachKey || '').trim();
    if (current && loadedModelKeys.has(current) && !/_\d{10,}$/.test(current)) return current;
    if (nonAccessory.length > 1) return nonAccessory[0];
    if (loadedModelKeys.size === 1) return [...loadedModelKeys][0];
    return preferred;
  };

  const vendorComputeRemovedTrackedKeys = (beforeKeys = []) => {
    const removed = [];
    beforeKeys.forEach((key) => {
      const k = String(key || '').trim();
      if (!k || isMainModelRoleKey(k)) return;
      if (!loadedModelKeys.has(k)) {
        removed.push(k);
        return;
      }
      if (!kernelModelExistsByCandidates(k)) {
        removed.push(k);
      }
    });
    if (removed.length) {
      removed.forEach((k) => {
        if (isMainModelRoleKey(k)) return;
        loadedModelKeys.delete(k);
        unregisterModelMeshes(k);
        modelRotationAccumY.delete(k);
        modelSkuRegistry.delete(k);
        modelRoleRegistry.delete(k);
        modelControlTypeRegistry.delete(k);
        unregisterModelGroupKey(k);
        vendorClearModelPlacement(k);
        clearBizSkuMapping(k);
      });
    }
    return removed;
  };

  const ensureMainModelPreservedInScene = () => {
    const mainSku = String(vendorLoadedMainSku || '').trim();
    if (!mainSku) return true;
    const mainKeys = [...loadedModelKeys].filter((key) => isMainModelRoleKey(key));
    if (currentModelAttachKey && isMainModelRoleKey(currentModelAttachKey) && !mainKeys.includes(currentModelAttachKey)) {
      mainKeys.push(currentModelAttachKey);
    }
    let hasLiveMain = mainKeys.some((key) => (
      modelKeyHasVisibleMeshes(key) || kernelModelExistsByCandidates(key)
    ));
    mainKeys.forEach((key) => {
      if (restoreModelVisibilityByKey(key)) hasLiveMain = true;
    });
    if (!hasLiveMain) {
      vendorDebugOperation('main:missing-after-secondary-select', { mainSku, mainKeys });
    }
    return hasLiveMain;
  };

  const getRuntimeScene = () =>
    cachedRuntimeScene
    || kernel?.scene
    || kernel?.mainApp?.appScene?.object
    || kernel?.mainApp?.scene
    || window.faceSDK?.kernel?.scene
    || window.faceSDK?.kernel?.mainApp?.appScene?.object
    || null;

  const tryCacheSceneFromMesh = (mesh) => {
    if (cachedRuntimeScene) return;
    try {
      const s = typeof mesh?.getScene === 'function' ? mesh.getScene() : null;
      if (s && Array.isArray(s.meshes)) cachedRuntimeScene = s;
    } catch (_) {}
  };

  const getSceneMeshes = () => {
    const scene = getRuntimeScene();
    const meshes = scene?.meshes;
    return Array.isArray(meshes) ? meshes.filter(Boolean) : [];
  };

  const getMeshUniqueId = (mesh) => {
    const id = Number(mesh?.uniqueId);
    return Number.isFinite(id) ? String(id) : '';
  };

  const snapshotSceneMeshIds = () => new Set(
    getSceneMeshes()
      .map(getMeshUniqueId)
      .filter(Boolean)
  );

  const collectNewSceneMeshes = (beforeIds) =>
    getSceneMeshes().filter((mesh) => {
      const id = getMeshUniqueId(mesh);
      return id && !beforeIds.has(id);
    });

  const collectModelDicMeshesForKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return [];
    try {
      const dic = getLiveModelDic();
      const roots = (dic && typeof dic.Get === 'function') ? dic.Get(k) : null;
      const meshes = [];
      if (Array.isArray(roots)) {
        roots.forEach((root) => {
          collectMeshesUnderImportRoot(root).forEach((mesh) => meshes.push(mesh));
        });
      }
      return meshes;
    } catch (_) {
      return [];
    }
  };

  const isolateModelMaterialTextures = (meshes, modelKey) => {
    const key = String(modelKey || '').trim();
    if (!key || !Array.isArray(meshes) || !meshes.length) return;
    const seenMaterials = new Set();
    const textureProps = [
      'albedoTexture',
      'diffuseTexture',
      'baseTexture',
      'baseColorTexture',
      'bumpTexture',
      'normalTexture',
      'metallicTexture',
      'roughnessTexture',
      'ambientTexture',
      'opacityTexture',
    ];
    const visitMaterial = (mat) => {
      if (!mat || seenMaterials.has(mat)) return;
      seenMaterials.add(mat);
      if (!mat.metadata || typeof mat.metadata !== 'object') mat.metadata = {};
      if (mat.metadata.c3dTextureIsolatedForModelKey === key) return;

      textureProps.forEach((prop) => {
        const tex = mat[prop];
        if (!tex || typeof tex.clone !== 'function') return;
        try {
          const cloned = tex.clone();
          if (cloned) mat[prop] = cloned;
        } catch (_) {
        }
      });

      mat.metadata.c3dTextureIsolatedForModelKey = key;
      if (typeof mat.markAsDirty === 'function') mat.markAsDirty(1);
      if (typeof mat.markDirty === 'function') mat.markDirty();

      const subs = mat.subMaterials;
      if (Array.isArray(subs)) subs.forEach((sub) => visitMaterial(sub));
    };

    meshes.forEach((mesh) => {
      visitMaterial(mesh?.material);
    });
  };

  const registerModelMeshes = async (key, beforeIds = new Set(), modelControlType = '') => {
    const k = String(key || '').trim();
    if (!k) return;
    const controlType = normalizeModelControlType(modelControlType);
    if (controlType) modelControlTypeRegistry.set(k, controlType);
    let meshes = collectModelDicMeshesForKey(k);
    for (let i = 0; i < 12 && !meshes.length; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 120));
      meshes = collectModelDicMeshesForKey(k);
    }
    if (!meshes.length && !/_\d{10,}$/.test(k)) {
      meshes = collectNewSceneMeshes(beforeIds);
      for (let i = 0; i < 12 && !meshes.length; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 120));
        meshes = collectNewSceneMeshes(beforeIds);
      }
    }
    const ids = new Set();
    const names = new Set();
    meshes.forEach((mesh) => {
      const id = getMeshUniqueId(mesh);
      if (id) ids.add(id);
      const name = String(mesh?.name || '').trim();
      if (name) names.add(name);
      try {
        if (!mesh.metadata || typeof mesh.metadata !== 'object') mesh.metadata = {};
        mesh.metadata.modelId = k;
        mesh.metadata.modelName = k;
        mesh.metadata.modelKey = k;
        if (controlType) mesh.metadata.modelControlType = controlType;
      } catch (_) {
      }
    });
    if (isAccessoryRuntimeKey(k)) isolateModelMaterialTextures(meshes, k);
    modelMeshRegistry.set(k, { ids, names });
  };

  const unregisterModelMeshes = (key) => {
    const k = String(key || '').trim();
    if (k) modelMeshRegistry.delete(k);
  };

  const hasRegisteredMeshIds = (key) => {
    const reg = modelMeshRegistry.get(String(key || '').trim());
    return !!reg?.ids?.size;
  };

  const disposeMesh = (mesh) => {
    if (!mesh || mesh.isDisposed?.()) return false;
    try {
      if (typeof mesh.dispose === 'function') {
        mesh.dispose(false, true);
        return true;
      }
    } catch (_) {
    }
    return false;
  };

  const disposeRegisteredMeshes = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    if (!/_\d{10,}$/.test(k)) return false;
    const meshes = getRegisteredMeshesForKey(k);
    let didDispose = false;
    meshes.forEach((mesh) => {
      didDispose = disposeMesh(mesh) || didDispose;
    });
    if (didDispose) {
      loadedModelKeys.delete(k);
      unregisterModelMeshes(k);
      modelRotationAccumY.delete(k);
      try {
        const dic = getLiveModelDic();
        if (dic && typeof dic.Remove === 'function') dic.Remove(k);
      } catch (_) {
      }
      vendorClearModelPlacement(k);
    }
    return didDispose;
  };

  const hasRegisteredMeshesInScene = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    const reg = modelMeshRegistry.get(k);
    if (!reg) return false;
    return getSceneMeshes().some((mesh) => {
      const id = getMeshUniqueId(mesh);
      const name = String(mesh?.name || '').trim();
      return (id && reg.ids?.has(id)) || (name && reg.names?.has(name));
    });
  };

  const findTrackedModelKeyForMesh = (mesh) => {
    if (!mesh) return '';
    const ids = [];
    const names = [];
    let node = mesh;
    for (let i = 0; i < 256 && node; i += 1) {
      const id = getMeshUniqueId(node);
      if (id) ids.push(id);
      const name = String(node?.name || '').trim();
      if (name) names.push(name);
      node = getTransformParentNode(node);
    }
    for (const [key, item] of modelMeshRegistry.entries()) {
      if (ids.some((id) => item.ids?.has(id))) return key;
    }
    const keysByName = [];
    for (const [key, item] of modelMeshRegistry.entries()) {
      if (names.some((name) => item.names?.has(name))) keysByName.push(key);
    }
    if (keysByName.length === 1) {
      return keysByName[0];
    }
    if (keysByName.length > 1) {
      return '';
    }
    for (const name of names) {
      if (loadedModelKeys.has(name)) return name;
    }
    const untrackedKeys = Array.from(loadedModelKeys).filter((key) => {
      const item = modelMeshRegistry.get(key);
      return !item || (!item.ids?.size && !item.names?.size);
    });
    if (untrackedKeys.length === 1) return untrackedKeys[0];
    return '';
  };

  const sdkUnloadByRegisteredName = async (name) => {
    if (!name || !sdkInitialized) return false;
    const modelHost = getModelAPIHost().value;
    const trackedKey = String(name || '').trim();
    if (!trackedKey) return false;
    const dic = getLiveModelDic();
    const rawCandidates = collectSdkModelUnloadCandidates(trackedKey);
    let candidates = rawCandidates;
    if (dic && typeof dic.Has === 'function') {
      const inDic = rawCandidates.filter((cand) => {
        try {
          return !!dic.Has(cand);
        } catch (_) {
          return false;
        }
      });
      if (inDic.length) {
        candidates = inDic;
      }
    }
    vendorDebugOperation('delete:start', {
      trackedKey,
      candidates,
    });

    const tryMethod = async (method) => {
      if (!hasFn(modelHost, method)) return false;
      for (let i = 0; i < candidates.length; i += 1) {
        const cand = candidates[i];
        try {
          vendorDebugOperation('delete:attempt', {
            trackedKey,
            method,
            candidate: cand,
          });
          const wasPresent = kernelModelExistsByCandidates(cand)
            || modelKeyHasVisibleMeshes(cand);
          if (!wasPresent) continue;
          const ret = await withTimeout(awaitIfPromise(modelHost[method](cand)));
          if (ret === '__timeout__') throw new Error(`timeout for ${method}`);
          const stillExists = kernelModelExistsByCandidates(cand)
            || modelKeyHasVisibleMeshes(cand);
          if (ret === false || (!isExplicitRemovalSuccess(ret) && stillExists)) {
            throw new Error(`model.${method} did not confirm removal`);
          }
          if (stillExists) {
            throw new Error(`model still exists after ${method}`);
          }
          finalizeModelRemoval(trackedKey);
          vendorDebugOperation('delete:success', {
            trackedKey,
            method,
            candidate: cand,
            returnValue: ret,
          });
          return true;
        } catch (err) {
          console.warn(`[3D] model.${method} failed:`, cand, err);
        }
      }
      return false;
    };

    const unloadOrder = isAccessoryRuntimeKey(trackedKey)
      ? ['removeByKey', 'remove', 'destroy', 'removeByName']
      : ['removeByKey', 'removeByName', 'remove', 'destroy'];
    for (let i = 0; i < unloadOrder.length; i += 1) {
      if (await tryMethod(unloadOrder[i])) return true;
    }
    return false;
  };

  const clearBizSkuMapping = (modelId) => {
    const biz = getBusinessLayer();
    const id = String(modelId || '').trim();
    if (!id || typeof biz?.clearSkuMapping !== 'function') return;
    try {
      biz.clearSkuMapping(id);
    } catch (err) {
      console.warn('[3D] kernel_bl.clearSkuMapping failed:', id, err);
    }
  };

  const clearAllBizSkuMappings = () => {
    const biz = getBusinessLayer();
    if (typeof biz?.clearAllSkuMappings !== 'function') return;
    try {
      biz.clearAllSkuMappings();
    } catch (err) {
      console.warn('[3D] kernel_bl.clearAllSkuMappings failed:', err);
    }
  };

  const unloadAllRegisteredModels = async () => {
    const keys = Array.from(loadedModelKeys);
    for (let i = 0; i < keys.length; i += 1) {
      await sdkUnloadByRegisteredName(keys[i]);
    }
    loadedModelKeys.clear();
    modelMeshRegistry.clear();
    modelPlacementRegistry.clear();
    modelSkuRegistry.clear();
    modelRoleRegistry.clear();
    modelControlTypeRegistry.clear();
    modelGroupRegistry.clear();
    vendorLoadedMainSku = '';
    vendorActiveSku = '';
    vendorStableWallDivisions = null;
    vendorPreviewWallDivisions = null;
    vendorLastCommittedAccessorySku = '';
    modelRotationAccumY.clear();
    currentModelId = null;
    currentModelUrl = null;
    currentModelAttachKey = null;
    clearAllBizSkuMappings();
    showEmpty();
  };

  const collectTrackedMainModelKeys = () => {
    const keys = new Set();
    [...loadedModelKeys].forEach((key) => {
      if (isMainModelRoleKey(key)) keys.add(key);
    });
    modelRoleRegistry.forEach((role, key) => {
      if (role === 'main') keys.add(String(key || '').trim());
    });
    if (currentModelAttachKey && isMainModelRoleKey(currentModelAttachKey)) {
      keys.add(currentModelAttachKey);
    }
    return [...keys].filter(Boolean);
  };

  const unloadTrackedMainModelsFromScene = async () => {
    const keys = collectTrackedMainModelKeys();
    if (!keys.length) return false;
    vendorDebugOperation('main:unload-before-replace', { keys });
    let removed = false;
    for (let i = 0; i < keys.length; i += 1) {
      if (await sdkUnloadByRegisteredName(keys[i])) {
        removed = true;
      } else {
        finalizeModelRemoval(keys[i]);
        removed = true;
      }
    }
    vendorLoadedMainSku = '';
    if (!collectTrackedMainModelKeys().length) {
      currentModelAttachKey = null;
      currentModelId = null;
      currentModelUrl = null;
    }
    return removed;
  };

  const finalizeModelRemoval = (modelName) => {
    const k = String(modelName || '').trim();
    if (!k) return;
    loadedModelKeys.delete(k);
    unregisterModelMeshes(k);
    modelRotationAccumY.delete(k);
    modelSkuRegistry.delete(k);
    modelRoleRegistry.delete(k);
    modelControlTypeRegistry.delete(k);
    unregisterModelGroupKey(k);
    vendorClearModelPlacement(k);
    collectSdkModelUnloadCandidates(k).forEach((candidate) => {
      clearBizSkuMapping(candidate);
    });
  };

  const removeAccessoryModelWithoutDisposingMaterials = (modelName) => {
    const k = String(modelName || '').trim();
    if (!isAccessoryRuntimeKey(k)) return false;
    const dic = getLiveModelDic();
    let meshes = [];
    try {
      const roots = dic && typeof dic.Get === 'function' ? dic.Get(k) : null;
      if (Array.isArray(roots)) meshes = roots.filter(Boolean);
    } catch (_) {
      meshes = [];
    }
    if (!meshes.length) meshes = getRegisteredMeshesForKey(k);
    if (!meshes.length) meshes = collectModelDicMeshesForKey(k);
    if (!meshes.length) return false;

    let removed = false;
    meshes.forEach((mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      try {
        if (typeof mesh.dispose === 'function') {
          mesh.dispose(false, false);
          removed = true;
        }
      } catch (_) {
      }
    });

    if (!removed) return false;
    try {
      if (dic && typeof dic.Remove === 'function') dic.Remove(k);
    } catch (_) {
    }
    try {
      const appModel = getLiveAppModel();
      if (appModel?.modelMetadataDic && typeof appModel.modelMetadataDic.Remove === 'function') {
        appModel.modelMetadataDic.Remove(k);
      }
    } catch (_) {
    }
    try {
      const app = getLiveAppModel()?.mainApp || kernel?.mainApp;
      app?.gameManager?.updateDictionaries?.();
      app?.appDropZone?.notifyModelRemoved?.(k);
    } catch (_) {
    }
    finalizeModelRemoval(k);
    vendorDebugOperation('delete:accessory-safe-dispose', { modelName: k, meshCount: meshes.length });
    return true;
  };

  const removeModelByVendorMesh = async (mesh, fallbackKey = '') => {
    if (!sdkInitialized) return { ok: false, modelName: '' };
    const modelName = mesh
      ? String(
        findRegisteredModelKeyForMesh(mesh)
        || findTrackedModelKeyForMesh(mesh)
        || findSdkModelNameByMesh(mesh)
        || ''
      ).trim()
      : String(fallbackKey || '').trim();
    if (!modelName || modelName === 'null' || modelName === 'undefined') {
      return { ok: false, modelName: '' };
    }

    const modelHost = getModelAPIHost().value;
    const isPickedMeshLive = () => {
      if (!mesh || mesh.isDisposed?.()) return false;
      if (mesh.isVisible === false || (typeof mesh.visibility === 'number' && mesh.visibility <= 0)) {
        return false;
      }
      const sceneMeshes = getSceneMeshes();
      if (!sceneMeshes.length) return false;
      const meshId = getMeshUniqueId(mesh);
      return sceneMeshes.some((sceneMesh) => (
        sceneMesh === mesh
        || (meshId && getMeshUniqueId(sceneMesh) === meshId)
      ));
    };
    const acceptsRemoval = (ret) => {
      if (ret === false) return false;
      if (isExplicitRemovalSuccess(ret)) return true;
      return mesh ? !isPickedMeshLive() : !kernelModelExistsByCandidates(modelName);
    };
    let removed = false;
    let removedByPickedMesh = false;

    if (mesh && hasFn(modelHost, 'remove')) {
      try {
        const ret = await awaitIfPromise(modelHost.remove(mesh));
        removed = acceptsRemoval(ret);
        removedByPickedMesh = removed;
      } catch (err) {
        console.warn('[3D] model.remove(mesh) failed:', modelName, err);
      }
    }

    if (!removed && hasFn(modelHost, 'removeByKey')) {
      try {
        const ret = await awaitIfPromise(modelHost.removeByKey(modelName));
        removed = acceptsRemoval(ret);
      } catch (err) {
        console.warn('[3D] model.removeByKey failed:', modelName, err);
      }
    }

    if (!removed && hasFn(modelHost, 'removeByName')) {
      try {
        const ret = await awaitIfPromise(modelHost.removeByName(modelName));
        removed = acceptsRemoval(ret);
      } catch (err) {
        console.warn('[3D] model.removeByName failed:', modelName, err);
      }
    }

    if (removed) {
      const remainingMeshes = removedByPickedMesh
        ? getRegisteredMeshesForKey(modelName)
        : [];
      const modelStillHasVisibleMeshes = removedByPickedMesh
        ? modelKeyHasVisibleMeshes(modelName)
        : false;
      if (!remainingMeshes.length && !modelStillHasVisibleMeshes) {
        finalizeModelRemoval(modelName);
      }
      vendorDebugOperation(
        removedByPickedMesh ? 'delete:vendor-remove-picked-mesh' : 'delete:vendor-removeByKey',
        {
          modelName,
          remainingMeshCount: remainingMeshes.length,
          modelStillHasVisibleMeshes,
        }
      );
    }

    return { ok: removed, modelName };
  };

  const removeRegisteredModelByKey = async (key, options = {}) => {
    const k = key != null && String(key) !== '' ? key : currentModelAttachKey;
    if (!k) return false;
    if (isMainModelRoleKey(k) && options.force !== true) {
      vendorDebugOperation('delete:blocked-main', { modelKey: k });
      return false;
    }
    modelRotationAccumY.delete(k);

    if (isAccessoryRuntimeKey(k)) {
      if (removeAccessoryModelWithoutDisposingMaterials(k)) {
        if (loadedModelKeys.size === 0) {
          currentModelId = null;
          currentModelUrl = null;
          currentModelAttachKey = null;
          showEmpty();
        } else if (currentModelAttachKey === k) {
          currentModelAttachKey = null;
          currentModelId = null;
          currentModelUrl = null;
        }
        return true;
      }
      const modelHost = getModelAPIHost().value;
      if (hasFn(modelHost, 'removeByName')) {
        try {
          const ret = await awaitIfPromise(modelHost.removeByName(k));
          if (
            ret !== false
            && (isExplicitRemovalSuccess(ret) || !kernelModelExistsByCandidates(k))
          ) {
            finalizeModelRemoval(k);
            if (loadedModelKeys.size === 0) {
              currentModelId = null;
              currentModelUrl = null;
              currentModelAttachKey = null;
              showEmpty();
            } else if (currentModelAttachKey === k) {
              currentModelAttachKey = null;
              currentModelId = null;
              currentModelUrl = null;
            }
            return true;
          }
        } catch (err) {
          console.warn('[3D] accessory removeByName failed:', k, err);
        }
      }
      return false;
    }

    const ok = await sdkUnloadByRegisteredName(k);
    if (ok) {
      if (loadedModelKeys.size === 0) {
        currentModelId = null;
        currentModelUrl = null;
        currentModelAttachKey = null;
        showEmpty();
      } else if (currentModelAttachKey === k) {
        currentModelAttachKey = null;
        currentModelId = null;
        currentModelUrl = null;
      }
      return true;
    }
    if (currentModelAttachKey === k) {
      currentModelAttachKey = null;
      currentModelId = null;
      currentModelUrl = null;
    }
    return false;
  };

  const isProtectedPrimaryModelKey = (key, keepKeys, primarySkus, extraKeys) => {
    const k = String(key || '').trim();
    if (!k) return true;
    if (keepKeys.has(k) || k === 'c3d-single-main') return true;
    if (extraKeys.has(k)) return false;
    if (isRotationModelKey(k)) return false;
    const sku = String(modelSkuRegistry.get(k) || '').trim().toLowerCase();
    const role = getModelRole(k);
    if (role === 'furniture' || role === 'accessory') return false;
    if (sku && primarySkus.has(sku)) return true;
    if (role === 'main') {
      if (sku && primarySkus.size && !primarySkus.has(sku)) return false;
      return true;
    }
    if (sku && primarySkus.size && !primarySkus.has(sku)) return false;
    return false;
  };

  const collectNonPrimaryModelKeys = (keepKeys, primarySkus, extraKeys) => {
    const extraKeySet = new Set(
      (Array.isArray(extraKeys) ? extraKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    );
    const keys = new Set();
    const add = (key) => {
      const k = String(key || '').trim();
      if (!k || isProtectedPrimaryModelKey(k, keepKeys, primarySkus, extraKeySet)) return;
      keys.add(k);
    };
    extraKeySet.forEach(add);
    loadedModelKeys.forEach(add);
    modelRoleRegistry.forEach((_, key) => add(key));
    modelSkuRegistry.forEach((_, key) => add(key));
    listTrackedModelKeysByRole('furniture').forEach(add);
    listTrackedModelKeysByRole('accessory').forEach(add);
    try {
      const runtimeKeys = getLiveModelDic()?.Keys?.();
      if (Array.isArray(runtimeKeys)) runtimeKeys.forEach(add);
    } catch (_) {
    }
    if (currentModelAttachKey) add(currentModelAttachKey);
    return [...keys];
  };

  const forceRemoveNonPrimaryModelByKey = async (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    modelRotationAccumY.delete(k);
    const role = getModelRole(k);
    if (role === 'accessory' || isAccessoryRuntimeKey(k)) {
      if (removeAccessoryModelWithoutDisposingMaterials(k)) {
        if (currentModelAttachKey === k) {
          currentModelAttachKey = null;
          currentModelId = null;
          currentModelUrl = null;
        }
        return true;
      }
    }
    if (await sdkUnloadByRegisteredName(k)) {
      if (currentModelAttachKey === k) {
        currentModelAttachKey = null;
        currentModelId = null;
        currentModelUrl = null;
      }
      return true;
    }
    if (await removeRegisteredModelByKey(k, { force: true })) return true;
    finalizeModelRemoval(k);
    if (currentModelAttachKey === k) {
      currentModelAttachKey = null;
      currentModelId = null;
      currentModelUrl = null;
    }
    return !modelKeyHasVisibleMeshes(k);
  };

  const removeNonPrimaryModels = async (options = {}) => {
    const keepKeys = new Set(
      (Array.isArray(options.keepKeys) ? options.keepKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    );
    const primarySkus = new Set(
      (Array.isArray(options.primarySkus) ? options.primarySkus : [])
        .map((sku) => String(sku || '').trim().toLowerCase())
        .filter(Boolean)
    );
    let keys = collectNonPrimaryModelKeys(keepKeys, primarySkus, options.extraKeys);
    let removed = false;
    for (let attempt = 0; attempt < 3 && keys.length; attempt += 1) {
      for (let i = 0; i < keys.length; i += 1) {
        if (await forceRemoveNonPrimaryModelByKey(keys[i])) removed = true;
      }
      const remaining = collectNonPrimaryModelKeys(keepKeys, primarySkus, options.extraKeys);
      if (!remaining.length) break;
      keys = remaining;
      if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 80));
    }
    const remainingKeys = collectNonPrimaryModelKeys(keepKeys, primarySkus, options.extraKeys);
    return {
      ok: remainingKeys.length === 0,
      modelNames: [...new Set(keys.concat(remainingKeys))],
    };
  };

  const getCurrentModelAttachKey = () => currentModelAttachKey;

  const getTransformParentNode = (node) => {
    if (!node || typeof node !== 'object') return null;
    try {
      const pub = node.parent;
      if (pub != null) return pub;
    } catch (_) {
    }
    try {
      if (node._parentNode != null) return node._parentNode;
    } catch (_) {
    }
    return null;
  };

  const meshesReferToSamePick = (a, b) => {
    if (!a || !b) return false;
    try {
      if (a === b) return true;
    } catch (_) {
      return false;
    }
    const ua = Number(a.uniqueId);
    const ub = Number(b.uniqueId);
    return Number.isFinite(ua) && Number.isFinite(ub) && ua === ub;
  };

  const collectMeshesUnderImportRoot = (root) => {
    const out = [];
    try {
      if (!root || root.isDisposed?.()) return out;
      out.push(root);
      if (typeof root.getChildMeshes === 'function') {
        const list = root.getChildMeshes(false);
        if (Array.isArray(list)) {
          for (let i = 0; i < list.length; i += 1) {
            if (list[i] && !list[i].isDisposed?.()) out.push(list[i]);
          }
        }
      }
    } catch (_) {
    }
    return out;
  };

  const meshBelongsUnderAnyRootMesh = (mesh, roots = []) => {
    if (!mesh || !roots?.length) return false;
    for (let i = 0; i < roots.length; i += 1) {
      const root = roots[i];
      if (!root || root.isDisposed?.()) continue;
      if (meshesReferToSamePick(mesh, root)) return true;
      try {
        if (typeof mesh.isDescendantOf === 'function' && mesh.isDescendantOf(root)) return true;
      } catch (_) {
      }
      let p = mesh;
      const guard = 256;
      for (let depth = 0; depth < guard; depth += 1) {
        if (meshesReferToSamePick(p, root)) return true;
        const next = getTransformParentNode(p);
        if (!next) break;
        p = next;
      }
    }
    return false;
  };

  const meshContainedInImportedEntryDeep = (mesh, roots = []) => {
    if (!mesh || !roots?.length) return false;
    for (let r = 0; r < roots.length; r += 1) {
      const rootsRow = roots[r];
      if (!rootsRow || rootsRow.isDisposed?.()) continue;
      try {
        if (typeof mesh.isDescendantOf === 'function' && mesh.isDescendantOf(rootsRow)) return true;
      } catch (_) {
      }
      const candidates = collectMeshesUnderImportRoot(rootsRow);
      for (let j = 0; j < candidates.length; j += 1) {
        if (meshesReferToSamePick(mesh, candidates[j])) return true;
      }
    }
    return false;
  };

  const findRegisteredModelKeyForMesh = (mesh) => {
    const dic = getLiveModelDic();
    if (!mesh || !dic) return '';
    try {
      const keys = typeof dic.Keys === 'function' ? dic.Keys() : [];
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const roots = dic.Get(key);
        if (meshBelongsUnderAnyRootMesh(mesh, roots)) return String(key || '').trim();
        if (meshContainedInImportedEntryDeep(mesh, roots)) return String(key || '').trim();
      }
    } catch (_) {
    }
    return '';
  };

  const restoreModelVisibilityByKey = (key) => {
    const k = String(key || '').trim();
    if (!k || !loadedModelKeys.has(k)) return false;
    let meshes = getRegisteredMeshesForKey(k);
    if (!meshes.length) meshes = collectModelDicMeshesForKey(k);
    if (!meshes.length) return false;
    let restored = false;
    meshes.forEach((mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      try {
        if (typeof mesh.setEnabled === 'function') mesh.setEnabled(true);
      } catch (_) {
      }
      try {
        if ('isVisible' in mesh) mesh.isVisible = true;
      } catch (_) {
      }
      try {
        if ('visibility' in mesh) {
          const v = Number(mesh.visibility);
          if (!Number.isFinite(v) || v <= 0) mesh.visibility = 1;
        }
      } catch (_) {
      }
      restored = true;
    });
    if (restored) {
      vendorDebugOperation('model:restore-visibility', {
        modelKey: k,
        meshCount: meshes.length,
      });
    }
    return restored;
  };

  const softDeleteModelByKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return false;
    let meshes = getRegisteredMeshesForKey(k);
    if (!meshes.length) meshes = collectModelDicMeshesForKey(k);
    if (!meshes.length) return false;
    meshes.forEach((mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      try {
        if (typeof mesh.setEnabled === 'function') mesh.setEnabled(false);
      } catch (_) {
      }
      try {
        if ('isPickable' in mesh) mesh.isPickable = false;
      } catch (_) {
      }
      try {
        if ('isVisible' in mesh) mesh.isVisible = false;
      } catch (_) {
      }
      try {
        if ('visibility' in mesh) mesh.visibility = 0;
      } catch (_) {
      }
    });
    try {
      const dic = getLiveModelDic();
      if (dic && typeof dic.Remove === 'function') dic.Remove(k);
    } catch (_) {
    }
    loadedModelKeys.delete(k);
    unregisterModelMeshes(k);
    modelRotationAccumY.delete(k);
    vendorClearModelPlacement(k);
    vendorDebugOperation('delete:soft-hide', {
      modelKey: k,
      meshCount: meshes.length,
    });
    return true;
  };

  const findSdkModelNameByMesh = (mesh) => {
    if (!mesh) return '';
    try {
      const finder = kernel?.model?.findModelNameByMesh;
      if (typeof finder === 'function') {
        const name = String(finder.call(kernel.model, mesh) || '').trim();
        if (name) return name;
      }
    } catch (_) {
    }
    try {
      const finder = kernel?.mainApp?.appModel?.findModelNameByMesh;
      if (typeof finder === 'function') {
        const name = String(finder.call(kernel.mainApp.appModel, mesh) || '').trim();
        if (name) return name;
      }
    } catch (_) {
    }
    return '';
  };

  const isSceneEnvironmentMeshName = (name) => {
    const n = String(name || '').trim();
    if (!n) return false;
    if (/^(gridground|grid_ground|ground|floor|groundplane|gridplane|skybox|hdrsky)$/i.test(n)) {
      return true;
    }
    return /grid.*ground/i.test(n) || /ground.*grid/i.test(n);
  };

  const isSceneEnvironmentMesh = (mesh) => {
    if (!mesh) return false;
    const metaType = String(mesh?.metadata?.type || '').trim().toLowerCase();
    if (metaType === 'ground' || metaType === 'grid' || metaType === 'environment') return true;
    if (mesh?.metadata?.selectable === false) return true;
    if (isSceneEnvironmentMeshName(mesh.name)) return true;
    let node = mesh;
    for (let i = 0; i < 8 && node; i += 1) {
      if (isSceneEnvironmentMeshName(node.name)) return true;
      node = getTransformParentNode(node);
    }
    return false;
  };

  const isRaycastPickableMesh = (mesh) => {
    if (!mesh) return false;
    if (!mesh.isPickable) return false;
    if (mesh?.metadata?.type === 'hotspot') return false;
    if (isSceneEnvironmentMesh(mesh)) return false;
    return true;
  };

  const isPickableProductMesh = (mesh) => {
    if (!mesh) return false;
    if (isSceneEnvironmentMesh(mesh)) return false;
    if (mesh?.metadata?.type === 'hotspot') return false;
    if (
      findTrackedModelKeyForMesh(mesh)
      || findRegisteredModelKeyForMesh(mesh)
      || findSdkModelNameByMesh(mesh)
    ) {
      return true;
    }
    let node = mesh;
    for (let i = 0; i < 32 && node; i += 1) {
      const metaKey = String(
        node?.metadata?.modelKey
        || node?.metadata?.modelId
        || node?.metadata?.modelName
        || ''
      ).trim();
      if (metaKey) return true;
      node = getTransformParentNode(node);
    }
    return false;
  };

  const resolveAttachKeyFromMesh = (mesh) => {
    if (!mesh) return '';
    return (
      findTrackedModelKeyForMesh(mesh)
      || findRegisteredModelKeyForMesh(mesh)
      || findSdkModelNameByMesh(mesh)
      || ''
    );
  };

  const meshMatchesAttachKey = (mesh, attachKey) => {
    const target = String(attachKey || '').trim();
    if (!mesh || !target) return false;
    const hitKey = resolveAttachKeyFromMesh(mesh);
    if (hitKey && hitKey === target) return true;
    const pick = resolveAttachKeyFromPick({ pickedMesh: mesh });
    if (pick && pick === target) return true;
    if (target === getModelSlotKey() || target === 'c3d-single-main') {
      return isPickableProductMesh(mesh) && !/_\d{10,}$/.test(hitKey || pick || '');
    }
    return false;
  };

  const clearMeshOutlineOnly = (mesh, visited = new Set()) => {
    if (!mesh || visited.has(mesh)) return;
    visited.add(mesh);
    try {
      if (mesh.renderOutline) mesh.renderOutline = false;
    } catch (_) {
    }
    try {
      if ('outlineWidth' in mesh) mesh.outlineWidth = 0;
    } catch (_) {
    }
    try {
      if (typeof mesh.getChildMeshes === 'function') {
        mesh.getChildMeshes(false).forEach((child) => clearMeshOutlineOnly(child, visited));
      }
    } catch (_) {
    }
  };

  const clearModelOutlineForModelKey = (modelKey) => {
    const k = String(modelKey || '').trim();
    if (!k) return false;
    let meshes = getRegisteredMeshesForKey(k);
    if (!meshes.length) meshes = collectModelDicMeshesForKey(k);
    if (!meshes.length) return false;
    meshes.forEach((mesh) => clearMeshOutlineOnly(mesh));
    if (c3dLastOutlineAttachKey === k) c3dLastOutlineAttachKey = '';
    return true;
  };

  const clearModelOutlineSelection = () => {
    const k = kernel || window.faceSDK?.kernel;
    const outlineHosts = [
      k?.outline,
      k?.mainApp?.appOutline,
      k?.mainApp?.outline,
    ].filter(Boolean);
    for (let i = 0; i < outlineHosts.length; i += 1) {
      const api = outlineHosts[i];
      for (const method of ['clear', 'deselect', 'clearSelection', 'unselect', 'reset']) {
        if (!hasFn(api, method)) continue;
        try {
          api[method]();
        } catch (_) {
        }
      }
    }

    const app = k?.mainApp;
    const ray = app?.appRay;
    if (ray) {
      for (const method of ['clearOutline', 'clearSelection', 'deselect', 'unselect', 'reset']) {
        if (!hasFn(ray, method)) continue;
        try {
          ray[method]();
        } catch (_) {
        }
      }
      for (const prop of [
        'selectedMesh',
        '_selectedMesh',
        'outlineMesh',
        '_outlineMesh',
        'currentMesh',
        '_currentMesh',
      ]) {
        try {
          if (prop in ray) ray[prop] = null;
        } catch (_) {
        }
      }
    }

    const scene = getRuntimeScene();
    if (!scene) return false;

    const visited = new Set();
    getSceneMeshes().forEach((mesh) => clearMeshOutlineOnly(mesh, visited));
    try {
      if (Array.isArray(scene.meshes)) scene.meshes.forEach((mesh) => clearMeshOutlineOnly(mesh, visited));
    } catch (_) {
    }
    try {
      const outlineRenderer = scene.getOutlineRenderer?.();
      if (outlineRenderer) {
        if (hasFn(outlineRenderer, 'clear')) outlineRenderer.clear();
        if (hasFn(outlineRenderer, 'reset')) outlineRenderer.reset();
        if (Array.isArray(outlineRenderer._meshes)) {
          outlineRenderer._meshes.forEach((mesh) => clearMeshOutlineOnly(mesh, visited));
        }
      }
    } catch (_) {
    }
    c3dLastOutlineAttachKey = '';
    return true;
  };

  const dispatchBlankCanvasClick = () => {
    invokeSdkCanvasDeselect();
  };

  const pickMeshAtClientPoint = (clientX, clientY) => {
    const scene = getRuntimeScene();
    const canvasEl = ensureCanvas();
    if (!scene?.pick || !canvasEl || typeof clientX !== 'number' || typeof clientY !== 'number') {
      return null;
    }
    const rect = canvasEl.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    try {
      const result = scene.pick(x, y, (m) => isRaycastPickableMesh(m));
      if (result?.hit && result.pickedMesh && isRaycastPickableMesh(result.pickedMesh)) {
        return result.pickedMesh;
      }
    } catch (_) {
    }
    return null;
  };

  const resolveAttachKeyFromPick = (pick = null) => {
    if (!pick || typeof pick !== 'object') return '';
    const mesh =
      pick?.pickedMesh
      || pick?.detail?.pickedMesh
      || null;
    if (mesh) {
      const fromTrackedMeshes = findTrackedModelKeyForMesh(mesh);
      if (fromTrackedMeshes) return fromTrackedMeshes;
      const fromDicWalk = findRegisteredModelKeyForMesh(mesh);
      if (fromDicWalk) return fromDicWalk;
      try {
        const finder = kernel?.mainApp?.appModel?.findModelNameByMesh;
        if (typeof finder === 'function') {
          const byMesh = String(finder.call(kernel.mainApp.appModel, mesh) || '').trim();
          if (byMesh) return byMesh;
        }
      } catch (_) {
      }
    }
    const raw = [
      pick.modelName,
      pick.modelId,
      pick.modelKey,
      pick.key,
      pick.id,
      pick.name,
      pick.attachKey,
      pick?.detail?.modelName,
      pick?.detail?.modelId,
      pick?.detail?.modelKey,
      pick?.detail?.key,
      pick?.detail?.id,
      pick?.detail?.name,
      pick?.pickedMesh?.metadata?.modelKey,
      pick?.pickedMesh?.metadata?.modelName,
      pick?.pickedMesh?.metadata?.modelId,
      pick?.detail?.pickedMesh?.metadata?.modelKey,
      pick?.detail?.pickedMesh?.metadata?.modelName,
      pick?.detail?.pickedMesh?.metadata?.modelId,
    ]
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    for (let i = 0; i < raw.length; i += 1) {
      if (raw[i]) return raw[i];
    }
    const meshName = String(
      pick?.meshName
      || pick?.detail?.meshName
      || pick?.pickedMesh?.name
      || pick?.detail?.pickedMesh?.name
      || ''
    ).trim();
    if (meshName) {
      try {
        const appModel = kernel?.mainApp?.appModel;
        const findMeshByName = appModel?.findMeshByName;
        const findModelNameByMesh = appModel?.findModelNameByMesh;
        if (typeof findMeshByName === 'function' && typeof findModelNameByMesh === 'function') {
          const meshByName = findMeshByName.call(appModel, meshName);
          const byName = String(findModelNameByMesh.call(appModel, meshByName) || '').trim();
          if (byName) return byName;
        }
      } catch (_) {
      }
    }

    if (loadedModelKeys.size === 1) {
      const onlyKey = [...loadedModelKeys][0];
      if (onlyKey) return onlyKey;
    }
    return '';
  };

  const removeModelByPick = async (pick = null) => {
    let mesh = pick?.deleteMesh || pick?.detail?.pickedMesh || pick?.pickedMesh || null;
    if (!mesh) {
      try {
        mesh = window.__c3dLastPickedMesh || null;
      } catch (_) {
      }
    }
    const fallbackKey = String(
      pick?.registeredUnloadKey
      || pick?.attachKey
      || ''
    ).trim();
    return removeModelByVendorMesh(mesh, fallbackKey);
  };

  const runLoadModelImpl = async (modelUrl, productId, modelControlType, modelKey) => {
    if (!modelUrl) {
      showEmpty();
      return false;
    }

    const explicitKey = String(modelKey || '').trim();
    const attachKey = explicitKey || (productId ? `product-${productId}` : getModelSlotKey());
    const modelId = attachKey;

    detachModelActionBubbleDom();

    if (currentModelId === modelId && currentModelUrl === modelUrl) {
      if (shouldUseModelAddRemoveForSwap()) {
        const dicEarly = getLiveModelDic();
        if (dicEarly?.Has?.(attachKey)) {
          loadedModelKeys.add(attachKey);
          return true;
        }
      } else {
        return true;
      }
    }

    try {
      showLoading(0);
      resizeCanvas(ensureCanvas());

      if (!sdkInitialized && !await initSDK()) {
        showEmpty();
        return false;
      }

      if (attachKey === 'c3d-single-main' || /^product-\d+$/.test(attachKey)) {
        const keysToSweep = new Set(Array.from(loadedModelKeys));
        keysToSweep.add('c3d-single-main');
        if (productId) keysToSweep.add(`product-${productId}`);
        for (const k of keysToSweep) {
          const ks = String(k || '');
          if (ks !== 'c3d-single-main' && !/^product-\d+$/.test(ks)) continue;
          if (ks === attachKey) continue;
          if (!modelRuntimeHasKey(ks)) continue;
          await sdkUnloadByRegisteredName(ks);
        }
      }

      const modelHost = getModelAPIHost().value;
      let meshIdsBeforeAdd = null;
      let expectModelDicKey = false;
      const shouldForceReplace = forceReplaceNextLoad;
      forceReplaceNextLoad = false;
      const isTrackedModel = loadedModelKeys.has(attachKey);
      if (
        shouldForceReplace
        && (attachKey === 'c3d-single-main' || /^product-\d+$/.test(attachKey))
        && modelRuntimeHasKey(attachKey)
      ) {
        await sdkUnloadByRegisteredName(attachKey);
      }
      if ((isTrackedModel || shouldForceReplace) && useReplaceModelAPI()) {
        const ok = await callModelReplaceCompat(modelHost, attachKey, modelUrl, modelControlType);
        if (!ok) {
          throw new Error('[3D] model.replace failed in compatibility calls');
        }
      } else if (useAddRemoveModelAPI()) {
        expectModelDicKey = true;
        if (modelRuntimeHasKey(attachKey)) {
          await sdkUnloadByRegisteredName(attachKey);
        }
        meshIdsBeforeAdd = snapshotSceneMeshIds();
        const ok = await callModelAddCompat(modelHost, attachKey, modelUrl, modelControlType);
        if (!ok) {
          throw new Error('[3D] model.add failed in compatibility calls');
        }
      } else if (useReplaceModelAPI()) {
        const ok = await callModelReplaceCompat(modelHost, attachKey, modelUrl, modelControlType);
        if (!ok) {
          throw new Error('[3D] model.replace failed in compatibility calls');
        }
      } else if (!sdkInitialized) {
        if (!isLegacyBusinessMode()) {
          throw new Error('[3D] kernel.init swap is disabled in kernel_bl mode');
        }
        resetHotspotSubBindings();
        await awaitIfPromise(
          kernel.init(buildInitConfig(ensureCanvas(), [modelUrl]))
        );
        await reapplyEnvironmentAfterInit();
        sdkInitialized = true;
        afterKernelInitHooks();
      } else if (useRuntimeModelAPI()) {
        if (currentModelUrl) {
          await awaitIfPromise(
            modelHost.replace(attachKey, { url: modelUrl, draco: true })
          );
        } else {
          await awaitIfPromise(
            modelHost.load({ id: attachKey, url: modelUrl, draco: true })
          );
        }
      } else {
        if (window.CUSTOMIZATION_3D_LOG_MODEL_ADD === true) {
          console.info(
            '[3D] 换模路径: kernel.init(modelUrlList)（无 model.add/remove 且 model.load/replace 不可用）'
          );
        }
        if (!isLegacyBusinessMode()) {
          throw new Error('[3D] kernel.init swap is disabled in kernel_bl mode');
        }
        resizeCanvas(ensureCanvas());
        resetHotspotSubBindings();
        if (hasFn(modelHost, 'destroy')) {
          try {
            await awaitIfPromise(modelHost.destroy(RUNTIME_MODEL_ID));
          } catch (err) {
            console.warn('[3D] model.destroy before re-init failed (ignored):', err);
          }
        }
        await awaitIfPromise(
          kernel.init(buildInitConfig(ensureCanvas(), [modelUrl]))
        );
        await reapplyEnvironmentAfterInit();
        afterKernelInitHooks();
      }

      if (expectModelDicKey) {
        const dicEnd = getLiveModelDic();
        if (dicEnd && typeof dicEnd.Has === 'function' && !dicEnd.Has(attachKey)) {
          let keys = [];
          try {
            keys = dicEnd?.Keys?.() ? dicEnd.Keys().slice() : [];
          } catch (_) {
          }
          throw new Error(
            `[3D] modelDic 中未找到 modelId="${attachKey}"；当前 keys=${JSON.stringify(keys)}。请核对是否走了 init 直载、或供应商未 Set 字典。`
          );
        }
      }

      currentModelId  = modelId;
      currentModelUrl = modelUrl;
      currentModelAttachKey = attachKey;
      loadedModelKeys.add(attachKey);
      if (meshIdsBeforeAdd) {
        await registerModelMeshes(attachKey, meshIdsBeforeAdd);
      } else if (!modelMeshRegistry.has(attachKey) || (() => {
        const reg = modelMeshRegistry.get(attachKey);
        return !reg || (!reg.ids?.size && !reg.names?.size);
      })()) {
        const dicFill = getLiveModelDic();
        const roots = (dicFill && typeof dicFill.Get === 'function') ? (() => {
          try { return dicFill.Get(attachKey); } catch (_) { return null; }
        })() : null;
        if (Array.isArray(roots) && roots.length) {
          const ids = new Set();
          const names = new Set();
          roots.forEach((root) => {
            collectMeshesUnderImportRoot(root).forEach((mesh) => {
              const id = getMeshUniqueId(mesh);
              if (id) ids.add(id);
              const name = String(mesh?.name || '').trim();
              if (name) names.add(name);
              try {
                if (!mesh.metadata || typeof mesh.metadata !== 'object') mesh.metadata = {};
                mesh.metadata.modelId = attachKey;
                mesh.metadata.modelName = attachKey;
                mesh.metadata.modelKey = attachKey;
                const ctl = normalizeModelControlType(modelControlType);
                if (ctl) mesh.metadata.modelControlType = ctl;
              } catch (_) {}
            });
          });
          if (ids.size || names.size) {
            modelMeshRegistry.set(attachKey, { ids, names });
          }
        } else {
          const occupiedIds = new Set();
          for (const [k, reg] of modelMeshRegistry.entries()) {
            if (k !== attachKey) {
              reg.ids?.forEach((id) => occupiedIds.add(id));
            }
          }
          const ids = new Set();
          const names = new Set();
          getSceneMeshes().forEach((mesh) => {
            const id = getMeshUniqueId(mesh);
            if (id && !occupiedIds.has(id)) {
              ids.add(id);
              const name = String(mesh?.name || '').trim();
              if (name) names.add(name);
              try {
                if (!mesh.metadata || typeof mesh.metadata !== 'object') mesh.metadata = {};
                mesh.metadata.modelId = attachKey;
                mesh.metadata.modelName = attachKey;
                mesh.metadata.modelKey = attachKey;
                const ctl = normalizeModelControlType(modelControlType);
                if (ctl) mesh.metadata.modelControlType = ctl;
              } catch (_) {}
            }
          });
          if (ids.size || names.size) {
            modelMeshRegistry.set(attachKey, { ids, names });
          }
        }
      }
      return true;
    } catch (err) {
      console.error('[3D] loadModel failed:', err);
      try {
        loadedModelKeys.delete(attachKey);
        unregisterModelMeshes(attachKey);
      } catch (_) {
      }
      if (loadedModelKeys.size === 0) {
        showEmpty();
      } else {
        showModelReady();
      }
      return false;
    }
  };

  const loadModel = async (modelUrl, productId = null, modelControlType = 'color', modelKey = '') => {
    const p = loadModelMutex.then(() => runLoadModelImpl(modelUrl, productId, modelControlType, modelKey));
    loadModelMutex = p.catch(() => {});
    return p;
  };

  const clearScene = async () => {
    await unloadAllRegisteredModels();
    forceReplaceNextLoad = true;
  };

  const clearModel = () => unloadAllRegisteredModels();

  const removeSceneModelByAttachKey = () => removeRegisteredModelByKey();

  const getModelUrl = async () => null;

  const hotspot = {
    render: (items = []) => {
      if (!hasFn(kernel?.hotspot, 'render')) {
        console.warn('[3D] hotspot.render not available in current SDK version');
        return false;
      }
      kernel.hotspot.render(items);
      return true;
    },
    clear: () => {
      if (hasFn(kernel?.hotspot, 'render')) kernel.hotspot.render([]);
    },
  };

  const material = {
    apply: (targetOrOpts, preset) => {
      if (!hasFn(kernel?.material, 'apply')) {
        console.warn('[3D] material.apply not available in current SDK version');
        return false;
      }
      if (
        targetOrOpts
        && typeof targetOrOpts === 'object'
        && !preset
        && ('target' in targetOrOpts || 'albedoColor' in targetOrOpts || 'attribute' in targetOrOpts)
      ) {
        kernel.material.apply(targetOrOpts);
      } else if (preset && typeof preset === 'object') {
        kernel.material.apply({ target: targetOrOpts, ...preset });
      } else {
        kernel.material.apply({ target: targetOrOpts, material: preset });
      }
      return true;
    },
    batch: (entries = []) => {
      if (!hasFn(kernel?.material, 'batch')) {
        console.warn('[3D] material.batch not available in current SDK version');
        return false;
      }
      kernel.material.batch(entries);
      return true;
    },
    color: (materialName, hexColor, modelId) => {
      const scopedId = String(modelId || '').trim();
      if (scopedId && hasFn(kernel?.material, 'apply')) {
        kernel.material.apply({ target: materialName, modelId: scopedId, albedoColor: hexColor });
        return true;
      }
      if (hasFn(kernel?.material, 'color')) {
        kernel.material.color(materialName, hexColor);
        return true;
      }
      if (hasFn(kernel?.material, 'apply')) {
        kernel.material.apply({ target: materialName, albedoColor: hexColor });
        return true;
      }
      console.warn('[3D] material API not available in current SDK version');
      return false;
    },
    reset: (target) => {
      if (!hasFn(kernel?.material, 'reset')) {
        console.warn('[3D] material.reset not available in current SDK version');
        return false;
      }
      kernel.material.reset(target);
      return true;
    },
  };

  const camera = {
    set: (config) => {
      if (!hasFn(kernel?.camera, 'set')) {
        console.warn('[3D] camera.set not available in current SDK version');
        return false;
      }
      kernel.camera.set(config);
      return true;
    },
    animateTo: (config, options) => {
      if (!hasFn(kernel?.camera, 'animateTo')) {
        console.warn('[3D] camera.animateTo not available in current SDK version');
        return false;
      }
      kernel.camera.animateTo(config, options);
      return true;
    },
  };

  const lights = {
    update: (name, config) => {
      if (!hasFn(kernel?.lights, 'update')) {
        console.warn('[3D] lights.update not available in current SDK version');
        return false;
      }
      kernel.lights.update(name, config);
      return true;
    },
  };

  const door = {
    toggle: (config = {}) => {
      if (!hasFn(kernel?.door, 'toggle')) {
        console.warn('[3D] door.toggle not available in current SDK version');
        return false;
      }
      kernel.door.toggle(config);
      return true;
    },
  };

  const clipping = {
    setY: (height, keepBelow = true, meshNames = []) => {
      if (!hasFn(kernel?.clipping, 'setY')) {
        console.warn('[3D] clipping.setY not available in current SDK version');
        return false;
      }
      kernel.clipping.setY(height, keepBelow, meshNames);
      return true;
    },
  };

  const isLikelyBabylonMesh = (m) =>
    !!m
    && (typeof m.getScene === 'function'
      || typeof m.rotate === 'function'
      || typeof m.addRotation === 'function'
      || (m.rotation != null && typeof m.rotation === 'object'));

  const resolveMeshFromPickState = (pick) => {
    if (!pick) return null;
    const tryMesh = (m) => (isLikelyBabylonMesh(m) ? m : null);
    let m = tryMesh(pick.pickedMesh);
    if (m) return m;
    const d = pick.detail;
    m = tryMesh(d?.pickedMesh);
    if (m) return m;
    const name = String(pick.meshName || d?.meshName || '').trim();
    if (!name) return null;
    const scene = kernel?.scene
      || kernel?.mainApp?.appScene?.object
      || null;
    if (!scene) return null;
    try {
      if (typeof scene.getMeshByName === 'function') {
        m = scene.getMeshByName(name);
        if (isLikelyBabylonMesh(m)) return m;
      }
    } catch (_) {
      /* ignore */
    }
    const meshes = scene.meshes || [];
    m = meshes.find((x) => x && x.name === name);
    return isLikelyBabylonMesh(m) ? m : null;
  };

  const collectMeshesForAttachKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return [];
    const seen = new Set();
    const meshes = [];
    const push = (mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      const id = getMeshUniqueId(mesh);
      const token = id || String(mesh.name || '');
      if (token && seen.has(token)) return;
      if (token) seen.add(token);
      meshes.push(mesh);
    };
    getRegisteredMeshesForKey(k).forEach(push);
    collectModelDicMeshesForKey(k).forEach(push);
    return meshes;
  };

  const collectModelRootsForAttachKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return [];
    let roots = [];
    try {
      const dic = getLiveModelDic();
      const fromDic = dic && typeof dic.Get === 'function' ? dic.Get(k) : null;
      if (Array.isArray(fromDic)) roots = fromDic.filter(Boolean);
    } catch (_) {
    }
    if (!roots.length) roots = getRegisteredMeshesForKey(k);
    if (roots.length <= 1) return roots;
    return roots.filter((candidate) => !roots.some((other) => {
      if (candidate === other) return false;
      try {
        return typeof candidate.isDescendantOf === 'function'
          && candidate.isDescendantOf(other);
      } catch (_) {
        return false;
      }
    }));
  };

  const collectMeshesForModelKeys = (modelKeys) => {
    const keys = [...new Set(
      (Array.isArray(modelKeys) ? modelKeys : [modelKeys])
        .flatMap((key) => getModelGroupKeys(key))
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    )];
    const seen = new Set();
    const meshes = [];
    const push = (mesh) => {
      if (!mesh || mesh.isDisposed?.()) return;
      const id = getMeshUniqueId(mesh);
      const token = id || String(mesh.name || '');
      if (token && seen.has(token)) return;
      if (token) seen.add(token);
      meshes.push(mesh);
    };
    keys.forEach((key) => collectMeshesForAttachKey(key).forEach(push));
    return meshes;
  };

  const collectRootsForModelKeys = (modelKeys) => {
    const keys = [...new Set(
      (Array.isArray(modelKeys) ? modelKeys : [modelKeys])
        .flatMap((key) => getModelGroupKeys(key))
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    )];
    const seen = new Set();
    const roots = [];
    keys.forEach((key) => {
      collectModelRootsForAttachKey(key).forEach((root) => {
        const id = getMeshUniqueId(root);
        const token = id || String(root?.name || '');
        if (token && seen.has(token)) return;
        if (token) seen.add(token);
        roots.push(root);
      });
    });
    return roots;
  };

  const getModelWorldBounds = (modelKey) => {
    const meshes = collectMeshesForModelKeys([modelKey]);
    if (!meshes.length) return null;
    return getWorldBoundsForMeshes(meshes);
  };

  const getWorldBoundsForMeshes = (meshes) => {
    if (!meshes.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    meshes.forEach((mesh) => {
      try {
        mesh.computeWorldMatrix?.(true);
        const bb = mesh.getBoundingInfo?.()?.boundingBox;
        if (!bb) return;
        const mn = bb.minimumWorld;
        const mx = bb.maximumWorld;
        if (mn) {
          minX = Math.min(minX, mn.x);
          minY = Math.min(minY, mn.y);
          minZ = Math.min(minZ, mn.z);
        }
        if (mx) {
          maxX = Math.max(maxX, mx.x);
          maxY = Math.max(maxY, mx.y);
          maxZ = Math.max(maxZ, mx.z);
        }
      } catch (_) {
      }
    });
    if (!Number.isFinite(minX)) return null;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
    return {
      centerWorld: [centerX, centerY, centerZ],
      bottomWorld: [centerX, minY, centerZ],
      topWorld: [centerX, maxY, centerZ],
      meshes,
    };
  };

  const getModelWorldBoundsForKeys = (modelKeys) =>
    getWorldBoundsForMeshes(collectMeshesForModelKeys(modelKeys));

  const getRepresentativeMeshForKey = (modelKey) => {
    const meshes = collectMeshesForAttachKey(modelKey);
    if (!meshes.length) return null;
    let best = meshes[0];
    let bestSize = -1;
    meshes.forEach((mesh) => {
      try {
        mesh.computeWorldMatrix?.(true);
        const bb = mesh.getBoundingInfo?.()?.boundingBox;
        if (!bb) return;
        const dx = bb.maximumWorld.x - bb.minimumWorld.x;
        const dy = bb.maximumWorld.y - bb.minimumWorld.y;
        const dz = bb.maximumWorld.z - bb.minimumWorld.z;
        const size = dx * dy * dz;
        if (size > bestSize) {
          bestSize = size;
          best = mesh;
        }
      } catch (_) {
      }
    });
    return best;
  };

  const stripRuntimeModelKeySuffix = (key) =>
    String(key || '').trim().replace(/_\d{10,}$/, '');

  const countLoadedKeysForRuntimeBase = (baseKey) => {
    const base = String(baseKey || '').trim();
    if (!base) return 0;
    let count = 0;
    loadedModelKeys.forEach((key) => {
      const k = String(key || '').trim();
      if (!k || isMainModelRoleKey(k)) return;
      if (k === base || stripRuntimeModelKeySuffix(k) === base) count += 1;
    });
    return count;
  };

  const collectRotationIdCandidates = (attachKey, pickedMesh) => {
    const keys = new Set();
    const add = (value) => {
      const s = String(value || '').trim();
      if (s) keys.add(s);
    };
    add(attachKey);
    add(findSdkModelNameByMesh(pickedMesh));
    add(findTrackedModelKeyForMesh(pickedMesh));
    add(findRegisteredModelKeyForMesh(pickedMesh));
    add(resolveAttachKeyFromMesh(pickedMesh));
    const attach = String(attachKey || '').trim();
    const base = stripRuntimeModelKeySuffix(attach);
    if (base && base !== attach && countLoadedKeysForRuntimeBase(base) <= 1) {
      keys.add(base);
    }
    return [...keys];
  };

  const rotateMeshesAsGroupAroundWorldY = (meshes, radians) => {
    if (!Array.isArray(meshes) || !meshes.length || !Number.isFinite(radians)) return false;
    const topLevelMeshes = meshes.filter((mesh) => !meshes.some((other) => {
      if (mesh === other) return false;
      try {
        return typeof mesh.isDescendantOf === 'function' && mesh.isDescendantOf(other);
      } catch (_) {
        return false;
      }
    }));
    const targets = topLevelMeshes.length ? topLevelMeshes : meshes;
    const B = typeof window !== 'undefined' ? window.BABYLON : null;
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    meshes.forEach((mesh) => {
      try {
        mesh.computeWorldMatrix?.(true);
        const bb = mesh.getBoundingInfo?.()?.boundingBox;
        if (!bb) return;
        const mn = bb.minimumWorld;
        const mx = bb.maximumWorld;
        if (mn) {
          minX = Math.min(minX, mn.x);
          minY = Math.min(minY, mn.y);
          minZ = Math.min(minZ, mn.z);
        }
        if (mx) {
          maxX = Math.max(maxX, mx.x);
          maxY = Math.max(maxY, mx.y);
          maxZ = Math.max(maxZ, mx.z);
        }
      } catch (_) {
      }
    });
    if (!Number.isFinite(minX)) return false;
    const pivotX = (minX + maxX) / 2;
    const pivotY = (minY + maxY) / 2;
    const pivotZ = (minZ + maxZ) / 2;
    let didRotate = false;
    if (B?.Vector3 && B?.Matrix) {
      const pivot = new B.Vector3(pivotX, pivotY, pivotZ);
      const rotMatrix = B.Matrix.RotationY(radians);
      targets.forEach((mesh) => {
        try {
          mesh.computeWorldMatrix?.(true);
          const absPos = mesh.getAbsolutePosition?.();
          if (absPos) {
            const offset = absPos.subtract(pivot);
            const rotated = B.Vector3.TransformCoordinates(offset, rotMatrix);
            const next = rotated.add(pivot);
            if (typeof mesh.setAbsolutePosition === 'function') mesh.setAbsolutePosition(next);
            else if (mesh.position) {
              mesh.position.x = next.x;
              mesh.position.y = next.y;
              mesh.position.z = next.z;
            }
          }
          if (typeof mesh.rotate === 'function') {
            const space = B.Space && B.Space.WORLD !== undefined ? B.Space.WORLD : 1;
            mesh.rotate(B.Vector3.Up(), radians, space);
            didRotate = true;
          } else if (applyWorldYRotation(mesh, radians)) {
            didRotate = true;
          }
        } catch (_) {
        }
      });
      return didRotate;
    }
    targets.forEach((mesh) => {
      if (applyWorldYRotation(mesh, radians)) didRotate = true;
    });
    return didRotate;
  };

  const applyWorldYRotation = (mesh, radians) => {
    if (!mesh || !Number.isFinite(radians)) return false;
    try {
      if (typeof mesh.addRotation === 'function') {
        mesh.addRotation(0, radians, 0);
        return true;
      }
    } catch (_) {
      /* fall through */
    }
    try {
      if (mesh.rotation && typeof mesh.rotation === 'object') {
        const ry = Number(mesh.rotation.y);
        mesh.rotation.y = (Number.isFinite(ry) ? ry : 0) + radians;
        return true;
      }
    } catch (_) {
      /* fall through */
    }
    const B = typeof window !== 'undefined' ? window.BABYLON : null;
    if (B && B.Vector3 && typeof mesh.rotate === 'function') {
      try {
        const space = B.Space && B.Space.WORLD !== undefined ? B.Space.WORLD : 1;
        mesh.rotate(B.Vector3.Up(), radians, space);
        return true;
      } catch (err) {
        console.warn('[3D] meshRotate: BABYLON.rotate failed:', err);
      }
    }
    return false;
  };

  const rotateRootsAsGroupAroundWorldY = (roots, boundsMeshes, radians) => {
    if (!Array.isArray(roots) || !roots.length || !Number.isFinite(radians)) return false;
    const bounds = getWorldBoundsForMeshes(boundsMeshes);
    if (!bounds) return false;
    const [pivotX, pivotY, pivotZ] = bounds.centerWorld;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const B = typeof window !== 'undefined' ? window.BABYLON : null;
    const pivot = B?.Vector3 ? new B.Vector3(pivotX, pivotY, pivotZ) : null;
    let didRotate = false;

    roots.forEach((root) => {
      try {
        root.computeWorldMatrix?.(true);
        const absPos = root.getAbsolutePosition?.();
        if (absPos) {
          if (B?.Vector3 && B?.Matrix && pivot) {
            const offset = absPos.subtract(pivot);
            const rotated = B.Vector3.TransformCoordinates(offset, B.Matrix.RotationY(radians));
            const next = rotated.add(pivot);
            if (typeof root.setAbsolutePosition === 'function') root.setAbsolutePosition(next);
          } else {
            const dx = Number(absPos.x || 0) - pivotX;
            const dz = Number(absPos.z || 0) - pivotZ;
            const next = {
              x: pivotX + (dx * cos) - (dz * sin),
              y: Number(absPos.y || 0),
              z: pivotZ + (dx * sin) + (dz * cos),
            };
            if (typeof root.setAbsolutePosition === 'function') root.setAbsolutePosition(next);
            else if (root.position) {
              root.position.x = next.x;
              root.position.y = next.y;
              root.position.z = next.z;
            }
          }
        }
        if (typeof root.rotate === 'function' && B?.Vector3) {
          const space = B.Space && B.Space.WORLD !== undefined ? B.Space.WORLD : 1;
          root.rotate(B.Vector3.Up(), radians, space);
          didRotate = true;
        } else if (applyWorldYRotation(root, radians)) {
          didRotate = true;
        }
      } catch (_) {
      }
    });
    return didRotate;
  };

  const meshRotate = {
    aroundWorldY: (pickedMesh, radians = Math.PI / 2) => {
      const m = resolveMeshFromPickState({ pickedMesh, meshName: pickedMesh?.name, detail: null })
        || pickedMesh;
      return applyWorldYRotation(m, radians);
    },
    aroundWorldYFromPick: (pick, radians = Math.PI / 2) => {
      const mesh = resolveMeshFromPickState(pick);
      if (!mesh) {
        console.warn('[3D] meshRotate.aroundWorldYFromPick: could not resolve mesh', {
          meshName: pick?.meshName,
          hasDetail: !!pick?.detail,
        });
        return false;
      }
      const ok = applyWorldYRotation(mesh, radians);
      if (!ok) {
        console.warn('[3D] meshRotate.aroundWorldYFromPick: rotate failed on mesh', mesh.name);
      }
      return ok;
    },
  };

  const transform = {
    rotationByModelId: async (modelId, vector3 = { x: 0, y: 90, z: 0 }) => {
      const id = String(modelId || '').trim();
      if (!id || !hasFn(kernel?.transform, 'rotation')) return false;
      const delta = {
        x: Number(vector3.x) || 0,
        y: Number(vector3.y) || 0,
        z: Number(vector3.z) || 0,
      };
      try {
        const result = await awaitIfPromise(
          kernel.transform.rotation({ modelId: id, vector3: delta })
        );
        if (result === false || result?.success === false) return false;
        return true;
      } catch (err) {
        console.warn('[3D] transform.rotation failed:', err);
        return false;
      }
    },
  };

  const getMainModelMeshSet = () => {
    const set = new Set();
    const keys = [...loadedModelKeys].filter((key) => isMainModelRoleKey(key));
    if (currentModelAttachKey && isMainModelRoleKey(currentModelAttachKey) && !keys.includes(currentModelAttachKey)) {
      keys.push(currentModelAttachKey);
    }
    keys.forEach((key) => {
      collectMeshesForAttachKey(key).forEach((mesh) => set.add(mesh));
    });
    return set;
  };

  const isMeshPartOfMainModel = (mesh) => {
    if (!mesh || mesh.isDisposed?.()) return false;
    const key = findRegisteredModelKeyForMesh(mesh) || findTrackedModelKeyForMesh(mesh);
    if (key) {
      if (getModelRole(key) === 'furniture' || getModelRole(key) === 'accessory') return false;
      if (isMainModelRoleKey(key)) return true;
      return false;
    }
    const mainMeshes = getMainModelMeshSet();
    return mainMeshes.has(mesh);
  };

  const expandRotationModelKeys = (keys) => {
    const expanded = new Set(filterRotationModelKeys(keys));
    const seed = [...expanded];
    seed.forEach((key) => {
      getModelGroupKeys(key).forEach((groupKey) => expanded.add(groupKey));
    });
    return filterRotationModelKeys([...expanded]);
  };

  const collectNonMainMeshesForRotationKeys = (keys) => {
    const mainMeshes = getMainModelMeshSet();
    return collectMeshesForModelKeys(keys).filter((mesh) => (
      mesh && !mesh.isDisposed?.() && !mainMeshes.has(mesh) && !isMeshPartOfMainModel(mesh)
    ));
  };

  const collectNonMainRootsForRotationKeys = (keys) => {
    const mainMeshes = getMainModelMeshSet();
    return collectRootsForModelKeys(keys).filter((root) => (
      root && !root.isDisposed?.() && !mainMeshes.has(root) && !isMeshPartOfMainModel(root)
    ));
  };

  const clusterRotationRootsNearPick = (roots, pickedMesh) => {
    if (!pickedMesh || !Array.isArray(roots) || roots.length <= 1) return roots;

    const boundsForRoots = (rootList) => {
      let minX = Infinity;
      let minZ = Infinity;
      let maxX = -Infinity;
      let maxZ = -Infinity;
      rootList.forEach((root) => {
        collectMeshesUnderImportRoot(root).forEach((mesh) => {
          try {
            mesh.computeWorldMatrix?.(true);
            const bb = mesh.getBoundingInfo?.()?.boundingBox;
            if (!bb) return;
            const mn = bb.minimumWorld;
            const mx = bb.maximumWorld;
            if (mn) {
              minX = Math.min(minX, mn.x);
              minZ = Math.min(minZ, mn.z);
            }
            if (mx) {
              maxX = Math.max(maxX, mx.x);
              maxZ = Math.max(maxZ, mx.z);
            }
          } catch (_) {
          }
        });
      });
      if (!Number.isFinite(minX)) return null;
      return { minX, minZ, maxX, maxZ };
    };

    let cluster = roots.filter((root) => meshBelongsUnderAnyRootMesh(pickedMesh, [root]));
    if (!cluster.length) return roots;

    let changed = true;
    while (changed) {
      changed = false;
      const bounds = boundsForRoots(cluster);
      if (!bounds) break;
      const spanX = Math.max(bounds.maxX - bounds.minX, 0);
      const spanZ = Math.max(bounds.maxZ - bounds.minZ, 0);
      const padding = Math.max(spanX, spanZ, 1.5) * 0.25;
      const minX = bounds.minX - padding;
      const maxX = bounds.maxX + padding;
      const minZ = bounds.minZ - padding;
      const maxZ = bounds.maxZ + padding;
      roots.forEach((root) => {
        if (cluster.includes(root)) return;
        const rootBounds = boundsForRoots([root]);
        if (!rootBounds) return;
        const overlaps = (
          rootBounds.maxX >= minX
          && rootBounds.minX <= maxX
          && rootBounds.maxZ >= minZ
          && rootBounds.minZ <= maxZ
        );
        if (overlaps) {
          cluster.push(root);
          changed = true;
        }
      });
    }
    return cluster.length ? cluster : roots;
  };

  const getSceneGroundWorldY = () => {
    let groundY = 0;
    let found = false;
    getSceneMeshes().forEach((mesh) => {
      if (!isSceneEnvironmentMesh(mesh)) return;
      try {
        mesh.computeWorldMatrix?.(true);
        const bb = mesh.getBoundingInfo?.()?.boundingBox;
        if (!bb?.maximumWorld) return;
        const topY = Number(bb.maximumWorld.y);
        if (!Number.isFinite(topY)) return;
        if (!found) {
          groundY = topY;
          found = true;
        } else {
          groundY = Math.max(groundY, topY);
        }
      } catch (_) {
      }
    });
    return groundY;
  };

  const snapModelKeysToGround = (modelKeys, options = {}) => {
    const keys = [...new Set(
      (Array.isArray(modelKeys) ? modelKeys : [modelKeys])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    )];
    if (!keys.length) return false;
    const epsilon = Number(options.epsilon) || 0.02;
    const groundY = Number.isFinite(options.groundY) ? options.groundY : getSceneGroundWorldY();
    const meshes = collectMeshesForModelKeys(keys);
    const bounds = getWorldBoundsForMeshes(meshes);
    if (!bounds) return false;
    const minY = bounds.bottomWorld[1];
    if (!Number.isFinite(minY) || minY >= groundY - epsilon) return false;
    const deltaY = groundY - minY;
    const roots = collectRootsForModelKeys(keys);
    roots.forEach((root) => {
      try {
        if (typeof root.getAbsolutePosition === 'function' && typeof root.setAbsolutePosition === 'function') {
          const abs = root.getAbsolutePosition();
          root.setAbsolutePosition(abs.x, abs.y + deltaY, abs.z);
        } else if (root.position) {
          root.position.y += deltaY;
        }
      } catch (_) {
      }
    });
    return true;
  };

  const collectBoundsMeshesForRotationRoots = (roots) => {
    const meshes = [];
    const seen = new Set();
    const mainMeshes = getMainModelMeshSet();
    roots.forEach((root) => {
      collectMeshesUnderImportRoot(root).forEach((mesh) => {
        if (!mesh || mesh.isDisposed?.() || mainMeshes.has(mesh) || isMeshPartOfMainModel(mesh)) return;
        const id = getMeshUniqueId(mesh);
        const token = id || String(mesh.name || '');
        if (token && seen.has(token)) return;
        if (token) seen.add(token);
        meshes.push(mesh);
      });
    });
    return meshes;
  };

  const resolveRotationScopeFromPick = (pickedMesh, candidateKeys) => {
    const keys = filterRotationModelKeys(candidateKeys);
    const meshKey = filterRotationModelKeys([
      findRegisteredModelKeyForMesh(pickedMesh),
      findTrackedModelKeyForMesh(pickedMesh),
      findSdkModelNameByMesh(pickedMesh),
      resolveAttachKeyFromMesh(pickedMesh),
    ])[0] || keys[0] || '';
    const scopedKeys = meshKey ? expandRotationModelKeys([meshKey]) : keys;

    let roots = collectNonMainRootsForRotationKeys(scopedKeys);
    if (pickedMesh && roots.length > 1 && scopedKeys.length <= 1) {
      roots = clusterRotationRootsNearPick(roots, pickedMesh);
    }

    if (roots.length) {
      return {
        keys: scopedKeys,
        meshes: collectBoundsMeshesForRotationRoots(roots),
        roots,
      };
    }

    if (!pickedMesh) {
      return {
        keys: scopedKeys,
        meshes: collectNonMainMeshesForRotationKeys(scopedKeys),
        roots: [],
      };
    }

    const mainMeshes = getMainModelMeshSet();
    const pickMesh = resolveMeshFromPickState({ pickedMesh, meshName: pickedMesh?.name, detail: null })
      || pickedMesh;
    if (pickMesh && !mainMeshes.has(pickMesh) && !isMeshPartOfMainModel(pickMesh)) {
      return {
        keys: scopedKeys,
        meshes: collectBoundsMeshesForRotationRoots([pickMesh]),
        roots: [pickMesh],
      };
    }

    return {
      keys: scopedKeys,
      meshes: collectNonMainMeshesForRotationKeys(scopedKeys),
      roots: [],
    };
  };

  const filterRotationModelKeys = (keys) => [...new Set(
    (Array.isArray(keys) ? keys : [keys])
      .map((key) => String(key || '').trim())
      .filter((key) => key && !isMainModelRoleKey(key))
  )];

  const rotateModelByAttachKey = async (attachKey, pickedMesh, yDeg = 90, modelKeys = []) => {
    const rotationDegrees = Number(yDeg);
    if (!Number.isFinite(rotationDegrees)) return false;
    const resolvedFromMesh = pickedMesh
      ? filterRotationModelKeys([
        findSdkModelNameByMesh(pickedMesh),
        findRegisteredModelKeyForMesh(pickedMesh),
        findTrackedModelKeyForMesh(pickedMesh),
        resolveAttachKeyFromMesh(pickedMesh),
      ])
      : [];
    const attach = String(attachKey || '').trim();
    const scopedAttachKey = resolvedFromMesh[0] || attach;
    const suppliedKeys = filterRotationModelKeys(modelKeys);
    const groupedKeys = scopedAttachKey
      ? filterRotationModelKeys(getModelGroupKeys(scopedAttachKey))
      : [];

    let candidateKeys;
    if (resolvedFromMesh.length) {
      candidateKeys = expandRotationModelKeys(resolvedFromMesh);
    } else if (suppliedKeys.length) {
      candidateKeys = expandRotationModelKeys([...suppliedKeys, ...groupedKeys]);
    } else {
      candidateKeys = expandRotationModelKeys(
        groupedKeys.length
          ? groupedKeys
          : collectRotationIdCandidates(scopedAttachKey, pickedMesh)
      );
      if (!candidateKeys.length && scopedAttachKey) {
        candidateKeys = filterRotationModelKeys([scopedAttachKey]);
      }
    }

    const scope = resolveRotationScopeFromPick(pickedMesh, candidateKeys);
    const rotationModelIds = filterRotationModelKeys([
      findSdkModelNameByMesh(pickedMesh),
      scopedAttachKey,
      ...scope.keys,
    ]);
    if (!rotationModelIds.length || !hasFn(kernel?.transform, 'rotation')) {
      console.warn('[3D] kernel.transform.rotation is unavailable for model rotation.', {
        attachKey: scopedAttachKey,
        modelIds: rotationModelIds,
      });
      return false;
    }

    let sdkRotated = false;
    for (let i = 0; i < rotationModelIds.length; i += 1) {
      const ok = await transform.rotationByModelId(
        rotationModelIds[i],
        { x: 0, y: rotationDegrees, z: 0 }
      );
      if (ok) sdkRotated = true;
    }
    return sdkRotated;
  };

  const removeModelGroupByKeys = async (modelKeys) => {
    const requestedKeys = (Array.isArray(modelKeys) ? modelKeys : [modelKeys])
      .map((key) => String(key || '').trim())
      .filter(Boolean);
    const deletingAccessory = requestedKeys.some((key) => isAccessoryRuntimeKey(key));
    const candidateKeys = deletingAccessory
      ? requestedKeys.flatMap((key) => getModelGroupKeys(key))
      : requestedKeys;
    const targets = [...new Set(
      candidateKeys
        .map((key) => String(key || '').trim())
        .filter((key) => key && (!deletingAccessory || isAccessoryRuntimeKey(key)))
    )];
    let removed = false;
    for (let i = targets.length - 1; i >= 0; i -= 1) {
      const ok = await removeRegisteredModelByKey(targets[i]);
      removed = ok || removed;
    }
    return { ok: removed, modelNames: targets };
  };

  const dropZone = {
    setData: (data) => {
      if (!hasFn(kernel?.dropZone, 'setData')) return false;
      kernel.dropZone.setData(data);
      return true;
    },
    show: () => {
      if (!hasFn(kernel?.dropZone, 'show')) return false;
      kernel.dropZone.show();
      return true;
    },
    hide: () => {
      if (!hasFn(kernel?.dropZone, 'hide')) return false;
      kernel.dropZone.hide();
      return true;
    },
    showWall: (wallName) => {
      if (!hasFn(kernel?.dropZone, 'showWall')) return false;
      kernel.dropZone.showWall(wallName);
      return true;
    },
    hideAll: () => {
      if (hasFn(kernel?.dropZone, 'hideAll')) {
        kernel.dropZone.hideAll();
        return true;
      }
      return false;
    },
    clearZones: () => {
      if (!hasFn(kernel?.dropZone, 'clearZones')) return false;
      kernel.dropZone.clearZones();
      return true;
    },
    updateDivisions: (motion) => {
      if (!hasFn(kernel?.dropZone, 'updateDivisions')) return false;
      return kernel.dropZone.updateDivisions(motion);
    },
  };

  const domTo3D = {
    attach: (id, element, position3D, offset2D = { x: 0, y: 0 }) => {
      if (!hasFn(kernel?.domTo3D, 'attach')) {
        console.warn('[3D] domTo3D.attach not available in current SDK version');
        return false;
      }
      patchDomTo3DLayoutToCssPixels();
      patchDomTo3DHideAllBridge();
      kernel.domTo3D.attach(id, element, position3D, offset2D);
      return true;
    },
    detach: (id) => {
      if (!hasFn(kernel?.domTo3D, 'detach')) {
        console.warn('[3D] domTo3D.detach not available in current SDK version');
        return false;
      }
      kernel.domTo3D.detach(id);
      return true;
    },
  };

  return {
    init: initSDK,
    setupLayoutObserver: setupContainerResizeObserver,
    loadModel,
    clearModel,
    clearScene,
    removeSceneModelByAttachKey,
    unloadAllRegisteredModels,
    removeRegisteredModelByKey,
    removeNonPrimaryModels,
    getCurrentModelAttachKey,
    resolveAttachKeyFromPick,
    findSdkModelNameByMesh: (mesh) => findSdkModelNameByMesh(mesh),
    findTrackedModelKeyForMesh: (mesh) => findTrackedModelKeyForMesh(mesh),
    findRegisteredModelKeyForMesh: (mesh) => findRegisteredModelKeyForMesh(mesh),
    getModelWorldBounds: (modelKey) => getModelWorldBounds(modelKey),
    getModelWorldBoundsForKeys: (modelKeys) => getModelWorldBoundsForKeys(modelKeys),
    getRepresentativeMeshForKey: (modelKey) => getRepresentativeMeshForKey(modelKey),
    getModelGroupKeys: (modelKey) => getModelGroupKeys(modelKey),
    getModelRole: (modelKey) => getModelRole(modelKey),
    isRotationModelKey: (modelKey) => isRotationModelKey(modelKey),
    rotateModelByAttachKey: (attachKey, pickedMesh, yDeg, modelKeys) =>
      rotateModelByAttachKey(attachKey, pickedMesh, yDeg, modelKeys),
    removeModelGroupByKeys: (modelKeys) => removeModelGroupByKeys(modelKeys),
    clearModelOutlineSelection,
    clearModelOutlineForModelKey,
    isPickableProductMesh: (mesh) => isPickableProductMesh(mesh),
    isSceneEnvironmentMesh: (mesh) => isSceneEnvironmentMesh(mesh),
    isSceneEnvironmentMeshName: (name) => isSceneEnvironmentMeshName(name),
    listKernelModelDicKeys: () => {
      try {
        const dic = getLiveModelDic();
        const keys = dic?.Keys?.();
        return keys ? keys.slice() : [];
      } catch (_) {
        return [];
      }
    },
    listViewerTrackedModelKeys: () => [...loadedModelKeys],
    modelKeyExistsInKernel: (modelKey) => kernelModelExistsByCandidates(modelKey),
    modelKeyHasVisibleMeshes: (modelKey) => modelKeyHasVisibleMeshes(modelKey),
    pruneStaleTrackedModelKeys: () => {
      const removed = [];
      loadedModelKeys.forEach((key) => {
        const k = String(key || '').trim();
        if (!k) return;
        if (modelKeyHasVisibleMeshes(k)) return;
        removed.push(k);
        loadedModelKeys.delete(k);
        unregisterModelMeshes(k);
        modelRotationAccumY.delete(k);
        modelSkuRegistry.delete(k);
        unregisterModelGroupKey(k);
        vendorClearModelPlacement(k);
        clearBizSkuMapping(k);
      });
      return removed;
    },
    restoreModelVisibilityByKey,
    debugFindRegisteredKeyAfterPick: () => {
      try {
        const m = typeof window !== 'undefined' ? window.__c3dLastPickedMesh : null;
        const k = findRegisteredModelKeyForMesh(m);
        let dicKeys = [];
        try {
          const d = getLiveModelDic();
          dicKeys = d?.Keys ? d.Keys().slice() : [];
        } catch (_) {
        }
        const tracked = [...loadedModelKeys];
        return {
          meshName: m?.name || '',
          registeredKey: k,
          kernelModelDicKeys: dicKeys,
          viewerTrackedKeys: tracked,
        };
      } catch (_) {
        return { meshName: '', registeredKey: '', kernelModelDicKeys: [], viewerTrackedKeys: [] };
      }
    },
    getTrackedModelCount: () => loadedModelKeys.size,
    removeModelByPick,
    showEmpty,
    showLoading,
    getModelUrl,
    getSDKCapability,
    isInitialized: () => sdkInitialized,
    getCurrentModelId: () => currentModelId,

    whenReady: async () => {
      const ok = await initSDK();
      if (!ok) return false;
      try {
        await vendorApi.whenKernelReady();
        return true;
      } catch (err) {
        console.warn('[3D] whenReady failed:', err);
        return false;
      }
    },

    getKernel: () => kernel,
    getBusinessLayer,
    hotspot,
    dropZone,
    material,
    camera,
    lights,
    door,
    clipping,
    domTo3D,
    meshRotate,
    transform,
    vendor: vendorApi,
  };
})();

(function () {
  const bootLayoutOnly = () => {
    if (document.querySelector('.customization-3d-wrapper')) {
      window.Customization3DViewer.setupLayoutObserver?.();
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootLayoutOnly);
  } else {
    bootLayoutOnly();
  }
})();
